import { useState, useEffect, useCallback, useRef } from "react";

const API_BASE = "https://cbb-scout-ai-v1-production.up.railway.app";

// ── Auth-aware fetch ──────────────────────────────────────────────────────────
function makeApi(token, onUnauth) {
  return async function apiFetch(path, options = {}) {
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "Authorization": `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });
    if (res.status === 401) { onUnauth(); throw new Error("Unauthorized"); }
    if (!res.ok) throw new Error(`API ${res.status}`);
    return res.json();
  };
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --sidebar-w: 220px;
    --sidebar-bg: #0A1A18;
    --sidebar-border: rgba(255,255,255,0.06);
    --accent: #0D9488;
    --accent-light: #2DD4BF;
    --accent-dim: rgba(13,148,136,0.12);
    --accent-dim2: rgba(13,148,136,0.06);
    --surface: #FFFFFF;
    --surface2: #F7F8FA;
    --surface3: #EEF0F5;
    --text-primary: #0D0F14;
    --text-secondary: #5A6070;
    --text-muted: #9AA0B0;
    --border: #E4E7EF;
    --green: #16A86A;
    --green-bg: #EEFAF4;
    --amber: #D4860A;
    --amber-bg: #FEF5E4;
    --red: #D63B3B;
    --red-bg: #FEF0F0;
    --blue: #3B72D6;
    --blue-bg: #EEF3FE;
    --teal: #0D9488;
    --teal-bg: #F0FDFA;
    --radius: 10px;
    --radius-lg: 14px;
    --shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
    --shadow-lg: 0 8px 32px rgba(0,0,0,0.10);
    --font-display: 'Plus Jakarta Sans', sans-serif;
    --font-body: 'DM Sans', sans-serif;
    --transition: 0.18s ease;
  }

  body { font-family: var(--font-body); background: var(--surface2); color: var(--text-primary); min-height: 100vh; }

  .layout { display: flex; min-height: 100vh; }

  /* ── Login ── */
  .login-shell { display: flex; min-height: 100vh; }
  .login-left {
    width: 420px; flex-shrink: 0;
    background: var(--sidebar-bg);
    display: flex; flex-direction: column;
    padding: 48px 40px;
    position: relative; overflow: hidden;
  }
  .login-left::before {
    content: ''; position: absolute;
    width: 400px; height: 400px; border-radius: 50%;
    background: rgba(13,148,136,0.08);
    bottom: -120px; right: -120px;
    pointer-events: none;
  }
  .login-brand { display: flex; align-items: center; gap: 12px; margin-bottom: 56px; }
  .login-logo-icon { /* styled inline */ }
  .login-brand-name { font-family: var(--font-display); font-size: 22px; font-weight: 700; color: #fff; letter-spacing: -0.4px; }
  .login-brand-sub { font-size: 10px; color: rgba(255,255,255,0.35); margin-top: 1px; letter-spacing: 0.5px; text-transform: uppercase; }
  .login-headline { font-family: var(--font-display); font-size: 32px; font-weight: 700; color: #fff; line-height: 1.2; letter-spacing: -0.8px; margin-top: auto; margin-bottom: auto; }
  .login-tagline { font-size: 14px; color: rgba(255,255,255,0.45); line-height: 1.6; }
  .login-stats { margin-top: 40px; display: flex; flex-direction: column; gap: 12px; }
  .login-stat { display: flex; align-items: center; gap: 10px; }
  .login-stat-icon { width: 32px; height: 32px; border-radius: 8px; background: rgba(13,148,136,0.15); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .login-stat-text { font-size: 12px; color: rgba(255,255,255,0.5); }
  .login-stat-value { font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.85); }
  .login-right { flex: 1; display: flex; align-items: center; justify-content: center; background: var(--surface); padding: 48px; }
  .login-form-wrap { width: 100%; max-width: 360px; }
  .login-form-title { font-family: var(--font-display); font-size: 26px; font-weight: 700; letter-spacing: -0.5px; margin-bottom: 6px; }
  .login-form-sub { font-size: 14px; color: var(--text-secondary); margin-bottom: 32px; }
  .form-group { margin-bottom: 18px; }
  .form-label { font-size: 12px; font-weight: 500; color: var(--text-secondary); margin-bottom: 6px; display: block; letter-spacing: 0.2px; }
  .form-input {
    width: 100%; padding: 11px 14px;
    border: 1px solid var(--border); border-radius: var(--radius);
    font-size: 14px; font-family: var(--font-body); color: var(--text-primary);
    background: var(--surface2); outline: none;
    transition: border-color var(--transition), background var(--transition);
  }
  .form-input:focus { border-color: var(--accent); background: var(--surface); }
  .form-input::placeholder { color: var(--text-muted); }
  .login-btn {
    width: 100%; padding: 12px;
    background: var(--accent); color: #fff; border: none;
    border-radius: var(--radius); font-size: 14px; font-weight: 500;
    font-family: var(--font-body); cursor: pointer;
    transition: background var(--transition);
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .login-btn:hover { background: #0f766e; }
  .login-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .login-error { font-size: 13px; color: var(--red); background: var(--red-bg); border: 1px solid rgba(214,59,59,0.2); border-radius: var(--radius); padding: 10px 14px; margin-top: 12px; }
  .login-footer { margin-top: 40px; font-size: 13px; color: var(--text-secondary); text-align: center; font-weight: 500; letter-spacing: 0.1px; }

  /* ── Sidebar ── */
  .sidebar {
    width: var(--sidebar-w); flex-shrink: 0;
    background: var(--sidebar-bg);
    border-right: 1px solid var(--sidebar-border);
    display: flex; flex-direction: column;
    position: fixed; top: 0; left: 0; height: 100vh;
    z-index: 100; padding: 0 0 24px;
  }
  .sidebar-logo { padding: 24px 20px 20px; border-bottom: 1px solid var(--sidebar-border); margin-bottom: 8px; }
  .logo-mark { display: flex; align-items: center; gap: 10px; }
  .logo-icon { width: 32px; height: 32px; border-radius: 8px; background: var(--accent); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .logo-text { font-family: var(--font-display); font-size: 20px; font-weight: 700; color: #fff; letter-spacing: -0.4px; line-height: 1.2; }
  .logo-sub { font-size: 10px; color: rgba(255,255,255,0.35); margin-top: 1px; letter-spacing: 0.5px; text-transform: uppercase; }

  .sidebar-section { padding: 8px 12px 4px; font-size: 10px; color: rgba(255,255,255,0.25); text-transform: uppercase; letter-spacing: 1px; font-weight: 500; }

  .nav-item {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 20px; margin: 1px 8px;
    border-radius: 8px; cursor: pointer;
    color: rgba(255,255,255,0.5); font-size: 13.5px;
    transition: all var(--transition); font-weight: 400;
    border: none; background: none; width: calc(100% - 16px); text-align: left;
  }
  .nav-item:hover { color: rgba(255,255,255,0.85); background: rgba(255,255,255,0.05); }
  .nav-item.active { color: var(--accent-light); background: var(--accent-dim); font-weight: 500; }
  .nav-item svg { flex-shrink: 0; opacity: 0.7; }
  .nav-item.active svg { opacity: 1; }

  .sidebar-bottom { margin-top: auto; padding: 0 12px; display: flex; flex-direction: column; gap: 6px; }
  .status-pill {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 12px; border-radius: 8px;
    background: rgba(255,255,255,0.04); border: 1px solid var(--sidebar-border);
    font-size: 12px; color: rgba(255,255,255,0.5);
  }
  .status-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--green); flex-shrink: 0; }
  .status-dot.offline { background: var(--red); }
  .powered-by { font-size: 10px; color: rgba(255,255,255,0.2); text-align: center; padding: 4px 0; }
  .powered-tagline { font-size: 10px; color: rgba(255,255,255,0.18); text-align: center; font-style: italic; padding: 2px 0; }

  /* ── Main ── */
  .main { margin-left: var(--sidebar-w); flex: 1; display: flex; flex-direction: column; min-height: 100vh; }

  /* ── Topbar ── */
  .topbar {
    height: 60px; padding: 0 32px;
    background: var(--surface); border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    position: sticky; top: 0; z-index: 50;
  }
  .topbar-title { font-family: var(--font-display); font-size: 17px; font-weight: 600; letter-spacing: -0.3px; }
  .topbar-right { display: flex; align-items: center; gap: 10px; }
  .program-badge { padding: 5px 12px; border-radius: 20px; background: var(--accent-dim2); border: 1px solid rgba(13,148,136,0.25); font-size: 12px; color: var(--teal); font-weight: 500; }
  .budget-wrap { display: flex; align-items: center; gap: 6px; }
  .budget-label { font-size: 11px; color: var(--text-muted); }
  .budget-input-box { display: flex; align-items: center; gap: 4px; background: var(--surface2); border: 1px solid var(--border); border-radius: 8px; padding: 4px 10px; }
  .budget-prefix { font-size: 12px; font-weight: 600; color: var(--text-secondary); }
  .budget-input { width: 52px; border: none; background: none; font-size: 13px; font-family: var(--font-display); font-weight: 600; color: var(--text-primary); outline: none; text-align: right; }
  .budget-suffix { font-size: 11px; color: var(--text-muted); }
  .remaining-badge { padding: 5px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; }
  .remaining-ok { background: var(--green-bg); border: 1px solid rgba(22,168,106,0.2); color: var(--green); }
  .remaining-warn { background: var(--amber-bg); border: 1px solid rgba(212,134,10,0.25); color: var(--amber); }
  .remaining-over { background: var(--red-bg); border: 1px solid rgba(214,59,59,0.2); color: var(--red); }
  .logout-btn { padding: 5px 12px; border-radius: 8px; background: none; border: 1px solid var(--border); font-size: 12px; color: var(--text-muted); cursor: pointer; font-family: var(--font-body); transition: all var(--transition); }
  .logout-btn:hover { border-color: var(--red); color: var(--red); }

  /* ── Page content ── */
  .page { padding: 28px 32px; flex: 1; }
  .page-header { margin-bottom: 24px; }
  .page-title { font-family: var(--font-display); font-size: 24px; font-weight: 700; letter-spacing: -0.5px; }
  .page-sub { color: var(--text-secondary); font-size: 14px; margin-top: 4px; }

  /* ── KPI row ── */
  .kpi-row { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; margin-bottom: 24px; }
  .kpi-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 18px 20px; box-shadow: var(--shadow); }
  .kpi-card.clickable { cursor: pointer; transition: border-color 0.15s, box-shadow 0.15s; }
  .kpi-card.clickable:hover { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-dim); }
  .kpi-label { font-size: 11px; color: var(--text-primary); margin-bottom: 6px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; }
  .kpi-value { font-family: var(--font-display); font-size: 26px; font-weight: 700; letter-spacing: -0.5px; }
  .kpi-delta { font-size: 12px; margin-top: 3px; }
  .kpi-delta.up { color: var(--green); }
  .kpi-delta.warn { color: var(--amber); }
  .kpi-delta.teal { color: var(--teal); }

  /* ── Cards ── */
  .card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow); overflow: hidden; }
  .card-header { padding: 16px 20px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; overflow: visible; }
  .card-title { font-family: var(--font-display); font-size: 14px; font-weight: 600; }
  .card-sub { font-size: 12px; color: var(--text-muted); }
  .card-body { padding: 0; }

  /* ── Grid ── */
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }

  /* ── Player row ── */
  .player-row { display: flex; align-items: center; gap: 12px; padding: 12px 20px; border-bottom: 1px solid var(--border); cursor: pointer; transition: background var(--transition); }
  .player-row:last-child { border-bottom: none; }
  .player-row:hover { background: var(--surface2); }
  .player-row.selected { background: var(--accent-dim2); border-left: 3px solid var(--accent); padding-left: 17px; }
  .player-row.checked { background: var(--teal-bg); border-left: 3px solid var(--accent); padding-left: 17px; }
  .player-av { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; flex-shrink: 0; }
  .player-info { flex: 1; min-width: 0; }
  .player-name { font-size: 13.5px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .player-meta { font-size: 11.5px; color: var(--text-muted); margin-top: 1px; }

  /* ── Score pill ── */
  .score-pill { font-size: 12px; font-weight: 600; padding: 3px 9px; border-radius: 20px; flex-shrink: 0; }
  .score-pill.elite { background: var(--accent-dim2); color: var(--teal); border: 1px solid rgba(13,148,136,0.3); }
  .score-pill.premium { background: var(--blue-bg); color: var(--blue); border: 1px solid rgba(59,114,214,0.2); }
  .score-pill.mid { background: var(--surface2); color: var(--text-secondary); border: 1px solid var(--border); }
  .score-pill.utility { background: var(--surface2); color: var(--text-muted); border: 1px solid var(--border); }

  /* ── Value display ── */
  .value-usd { font-family: var(--font-display); font-size: 13px; font-weight: 600; color: var(--text-primary); min-width: 64px; text-align: right; }

  /* ── Trend badge ── */
  .trend { font-size: 11px; font-weight: 500; padding: 2px 8px; border-radius: 20px; flex-shrink: 0; }
  .trend.Improving { background: var(--green-bg); color: var(--green); }
  .trend.Declining { background: var(--red-bg); color: var(--red); }
  .trend.Stable { background: var(--surface2); color: var(--text-muted); }
  .trend.Unknown { background: var(--surface2); color: var(--text-muted); }

  /* ── Signed chip ── */
  .signed-chip { font-size: 10px; font-weight: 500; padding: 2px 7px; border-radius: 10px; background: var(--surface3); color: var(--text-muted); border: 1px solid var(--border); flex-shrink: 0; white-space: nowrap; }
  .signed-chip.signed { background: rgba(214,59,59,0.06); color: var(--red); border-color: rgba(214,59,59,0.2); }
  .watchlist-btn { cursor: pointer; transition: all var(--transition); }
  .watchlist-btn:hover { background: var(--accent-dim); color: var(--teal); border-color: rgba(13,148,136,0.35); }
  .watchlist-btn.watchlisted { background: var(--accent-dim); color: var(--teal); border-color: rgba(13,148,136,0.35); font-weight: 600; }

  /* ── Offer input ── */
  .offer-wrap { display: flex; align-items: center; gap: 4px; background: var(--teal-bg); border: 1px solid rgba(13,148,136,0.2); border-radius: 7px; padding: 3px 8px; }
  .offer-input { width: 58px; border: none; background: none; font-size: 12px; font-family: var(--font-display); font-weight: 600; color: var(--teal); outline: none; text-align: right; }
  .offer-prefix { font-size: 11px; color: var(--teal); font-weight: 500; }

  /* ── Search ── */
  .search-bar { display: flex; align-items: center; gap: 8px; padding: 9px 14px; background: var(--surface2); border: 1px solid var(--border); border-radius: var(--radius); margin: 14px 20px 0; transition: border-color var(--transition); }
  .search-bar:focus-within { border-color: var(--accent); }
  .search-bar input { flex: 1; border: none; background: none; font-size: 13.5px; outline: none; font-family: var(--font-body); color: var(--text-primary); }
  .search-bar input::placeholder { color: var(--text-muted); }

  /* ── Filter pills ── */
  .filter-row { display: flex; gap: 6px; padding: 12px 20px 0; flex-wrap: wrap; }
  .filter-pill { font-size: 12px; padding: 5px 12px; border-radius: 20px; border: 1px solid var(--border); background: var(--surface); cursor: pointer; transition: all var(--transition); color: var(--text-secondary); font-family: var(--font-body); }
  .filter-pill:hover { border-color: var(--accent); color: var(--teal); }
  .filter-pill.active { background: var(--accent); border-color: var(--accent); color: #fff; font-weight: 500; }

  /* ── Verdict box ── */
  .verdict-box { margin: 14px 20px; border-radius: var(--radius); padding: 12px 16px; }
  .verdict-box.green { background: var(--green-bg); border: 1px solid rgba(22,168,106,0.25); }
  .verdict-box.amber { background: var(--amber-bg); border: 1px solid rgba(212,134,10,0.25); }
  .verdict-box.red { background: var(--red-bg); border: 1px solid rgba(214,59,59,0.25); }
  .verdict-box.teal { background: var(--teal-bg); border: 1px solid rgba(13,148,136,0.25); }
  .verdict-title { font-size: 13px; font-weight: 600; margin-bottom: 3px; }
  .verdict-title.green { color: var(--green); }
  .verdict-title.amber { color: var(--amber); }
  .verdict-title.red { color: var(--red); }
  .verdict-title.teal { color: var(--teal); }
  .verdict-body { font-size: 12px; color: var(--text-secondary); line-height: 1.5; }

  /* ── Stats grid ── */
  .stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; }
  .stat-cell { padding: 12px 20px; border-bottom: 1px solid var(--border); }
  .stat-cell:nth-child(odd) { border-right: 1px solid var(--border); }
  .stat-cell:nth-last-child(-n+2) { border-bottom: none; }
  .stat-v { font-family: var(--font-display); font-size: 18px; font-weight: 700; }
  .stat-l { font-size: 11px; color: var(--text-muted); margin-top: 2px; }

  /* ── Buttons ── */
  .btn { font-family: var(--font-body); cursor: pointer; border-radius: 8px; font-size: 13px; font-weight: 500; padding: 8px 16px; transition: all var(--transition); border: none; }
  .btn-primary { background: var(--accent); color: #fff; }
  .btn-primary:hover { background: #0f766e; }
  .btn-secondary { background: var(--surface2); color: var(--text-primary); border: 1px solid var(--border); }
  .btn-secondary:hover { background: var(--surface3); }
  .btn-danger { background: var(--red-bg); color: var(--red); border: 1px solid rgba(214,59,59,0.2); }
  .btn-sm { padding: 5px 12px; font-size: 12px; }
  .btn-icon { padding: 6px 10px; }

  /* ── Loading ── */
  .loading { display: flex; align-items: center; justify-content: center; padding: 48px; color: var(--text-muted); font-size: 14px; gap: 10px; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .spinner { width: 18px; height: 18px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.7s linear infinite; }

  /* ── Bar chart ── */
  .bar-row { display: flex; align-items: center; gap: 10px; padding: 8px 20px; }
  .bar-label { font-size: 12px; color: var(--text-secondary); width: 80px; flex-shrink: 0; }
  .bar-track { flex: 1; height: 6px; background: var(--surface3); border-radius: 3px; overflow: hidden; }
  .bar-fill { height: 100%; border-radius: 3px; background: var(--accent); }
  .bar-val { font-size: 12px; font-weight: 500; width: 60px; text-align: right; flex-shrink: 0; }

  /* ── Empty state ── */
  .empty { padding: 40px 20px; text-align: center; color: var(--text-muted); font-size: 13.5px; }

  /* ── Scrollable list ── */
  .scroll-list { max-height: 384px; overflow-y: auto; }
  .scroll-list::-webkit-scrollbar { width: 4px; }
  .scroll-list::-webkit-scrollbar-track { background: transparent; }
  .scroll-list::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }

  /* ── Portal offer bar ── */
  .offer-bar { background: var(--teal-bg); border-top: 1px solid rgba(13,148,136,0.15); padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .offer-bar-stat { font-size: 12px; color: var(--text-secondary); }
  .offer-bar-value { font-family: var(--font-display); font-size: 16px; font-weight: 700; color: var(--teal); }

  /* ── Roster player row ── */
  .roster-row { display: flex; align-items: center; gap: 12px; padding: 10px 20px; border-bottom: 1px solid var(--border); }
  .roster-row:last-child { border-bottom: none; }

  /* ── Note ── */
  .note-item { padding: 10px 20px; border-bottom: 1px solid var(--border); }
  .note-text { font-size: 13px; color: var(--text-primary); line-height: 1.5; }
  .note-time { font-size: 11px; color: var(--text-muted); margin-top: 3px; }

  /* ── Animation ── */
  @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
  .animate-in { animation: fadeIn 0.25s ease forwards; }

  /* ── Checkbox ── */
  .cb { width: 16px; height: 16px; border: 1.5px solid var(--border); border-radius: 4px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; cursor: pointer; transition: all var(--transition); background: var(--surface); }
  .cb.on { background: var(--accent); border-color: var(--accent); }
  .cb svg { display: none; }
  .cb.on svg { display: block; }

  /* ── Elig badge ── */
  .elig-badge { font-size: 10px; font-weight: 600; padding: 2px 6px; border-radius: 8px; background: rgba(13,148,136,0.1); color: var(--teal); flex-shrink: 0; }
  /* ── Tooltip ── */
  .tt-wrap { position: relative; display: inline-flex; align-items: center; }
  .tt-icon { width: 14px; height: 14px; border-radius: 50%; background: var(--surface3);
             border: 1px solid var(--border); color: var(--text-muted); font-size: 9px;
             font-weight: 700; display: inline-flex; align-items: center; justify-content: center;
             cursor: default; margin-left: 5px; flex-shrink: 0; font-family: var(--font-display);
             transition: background 0.15s, border-color 0.15s; }
  .tt-wrap:hover .tt-icon { background: var(--accent-dim); border-color: var(--accent); color: var(--teal); }
  .tt-box {
    position: absolute; top: calc(100% + 8px); left: 50%; transform: translateX(-50%);
    background: #0D0F14; color: #E8EAF0; font-size: 11.5px; line-height: 1.55;
    padding: 9px 13px; border-radius: 9px; width: 220px; pointer-events: none;
    opacity: 0; transition: opacity 0.15s, transform 0.15s;
    transform: translateX(-50%) translateY(-4px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.22); z-index: 9999;
    font-family: var(--font-body); letter-spacing: 0.1px;
    max-width: calc(100vw - 32px);
  }
  .tt-box::after {
    content: ""; position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%);
    border: 5px solid transparent; border-bottom-color: #0D0F14;
  }
  .tt-wrap:hover .tt-box { opacity: 1; transform: translateX(-50%) translateY(0); }
  .tt-wrap.tt-left .tt-box { left: auto; right: 0; transform: translateX(0) translateY(-4px); }
  .tt-wrap.tt-left .tt-box::after { left: auto; right: 12px; transform: none; }
  .tt-wrap.tt-left:hover .tt-box { transform: translateX(0) translateY(0); }
  .tt-wrap.tt-right .tt-box { left: 0; right: auto; transform: translateX(0) translateY(-4px); }
  .tt-wrap.tt-right .tt-box::after { left: 12px; right: auto; transform: none; }
  .tt-wrap.tt-right:hover .tt-box { transform: translateX(0) translateY(0); }
  .tt-label { font-weight: 600; color: #fff; margin-bottom: 3px; font-size: 11px;
              text-transform: uppercase; letter-spacing: 0.4px; }
  .tt-body { color: rgba(232,234,240,0.75); }
  .tt-pill { display: inline-block; margin-top: 6px; padding: 2px 8px; border-radius: 99px;
             font-size: 10px; font-weight: 600; background: rgba(13,148,136,0.25); color: #2DD4BF; }

`;

// ── Icons ─────────────────────────────────────────────────────────────────────
const Icon = ({ name, size = 16 }) => {
  const icons = {
    search: <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><circle cx="6.5" cy="6.5" r="4" stroke="currentColor" strokeWidth="1.5"/><path d="M10 10l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
    grid:   <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><rect x="1.5" y="1.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="9.5" y="1.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="1.5" y="9.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="9.5" y="9.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/></svg>,
    chart:  <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M2 12l3.5-4 3 2.5L12 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 14h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
    portal: <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M8 2v12M3 5l5-3 5 3M3 11l5 3 5-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    user:   <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
    check:  <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    trash:  <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M3 4h10M6 4V3h4v1M5 4v9h6V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    logout: <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M6 3H3v10h3M10 5l3 3-3 3M13 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  };
  return icons[name] || null;
};

// ── Tooltip ──────────────────────────────────────────────────────────────────
function Tooltip({ label, body, pill, left }) {
  return (
    <span className={`tt-wrap${left ? " tt-left" : ""}`}>
      <span className="tt-icon">i</span>
      <span className="tt-box">
        {label && <div className="tt-label">{label}</div>}
        <div className="tt-body">{body}</div>
        {pill && <span className="tt-pill">{pill}</span>}
      </span>
    </span>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtUsd = v => {
  if (!v && v !== 0) return "—";
  const n = parseFloat(v);
  if (n >= 1000000) return `$${(n/1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n/1000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};
const fmtM = v => {
  if (!v && v !== 0) return "—";
  return `$${parseFloat(v).toFixed(2)}M`;
};
const initials = n => n.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();
const avColor = i => [
  ["rgba(13,148,136,0.12)","#0D9488"],
  ["rgba(59,114,214,0.12)","#3B72D6"],
  ["rgba(22,168,106,0.12)","#16A86A"],
  ["rgba(214,134,10,0.12)","#D4860A"],
  ["rgba(139,92,246,0.12)","#8B5CF6"],
][i % 5];

const CONF_TIER_LABELS = {
  1:"Power 4", 2:"P4-Adjacent", 3:"G5 / Strong Mid",
  4:"Rest D-I", 5:"D-II", 6:"D-III"
};
const confTierLabel = v => CONF_TIER_LABELS[parseInt(v)] || (v ? String(v) : "—");

const tierClass = t => ({ "Elite":"elite","Premium":"premium","First Team":"mid","Role Player":"utility","Emerging":"utility","Mid-tier":"mid","Utility":"utility" }[t] || "mid");
const verdictColor = v => {
  if (!v) return "amber";
  const vl = v.toLowerCase();
  if (vl.includes("strong")) return "green";
  if (vl.includes("offer")) return "teal";
  if (vl.includes("monitor")) return "amber";
  return "red";
};
const fmtDate = iso => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" });
};

// ── Login screen ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);

  // Pre-check ToS if this browser has a consent record for this username
  function checkLocalConsent(uname) {
    try {
      const stored = localStorage.getItem(`tos_consent_${uname.trim().toLowerCase()}`);
      if (stored) {
        const { tos_version } = JSON.parse(stored);
        if (tos_version === "1.0") setTosAccepted(true);
      }
    } catch {}
  }

  const TOS_VERSION = "1.0";

  async function handleSubmit(e) {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim().toLowerCase(), password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Invalid username or password."); return; }

      const authHeaders = { "Content-Type": "application/json", "Authorization": `Bearer ${data.token}` };

      // Check if user has already accepted current ToS version
      let alreadyConsented = false;
      try {
        const cr = await fetch(`${API_BASE}/user/consent`, { headers: authHeaders });
        const cd = await cr.json();
        alreadyConsented = (cd.consents || []).some(c => c.tos_version === TOS_VERSION);
      } catch {}

      // If not consented yet, require checkbox
      if (!alreadyConsented && !tosAccepted) {
        setError("Please read and accept the Terms of Service and Privacy Policy to continue.");
        return;
      }

      // Record consent (upsert — safe to call even if already exists)
      if (!alreadyConsented) {
        try {
          await fetch(`${API_BASE}/user/consent`, {
            method: "POST",
            headers: authHeaders,
            body: JSON.stringify({ tos_version: TOS_VERSION, ip_address: null, user_agent: navigator.userAgent }),
          });
          // Save to localStorage keyed by username — no cross-user leakage
          localStorage.setItem(
            `tos_consent_${username.trim().toLowerCase()}`,
            JSON.stringify({ tos_version: TOS_VERSION, accepted_at: new Date().toISOString() })
          );
        } catch {}
      } else {
        // Already consented — save to localStorage if not already there (e.g. new device)
        try {
          localStorage.setItem(
            `tos_consent_${username.trim().toLowerCase()}`,
            JSON.stringify({ tos_version: TOS_VERSION, accepted_at: new Date().toISOString() })
          );
        } catch {}
      }

      onLogin({ token: data.token, user: data.user, program: data.program, role: data.role });
    } catch {
      setError("Could not reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <style>{styles}</style>
      <div className="login-shell">
        <div className="login-left">
          <div className="login-brand">
            <div className="login-logo-icon" style={{width:36,height:36,borderRadius:9,background:"#141B24",border:"1px solid rgba(77,212,168,0.15)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <svg width="22" height="22" viewBox="0 0 72 72" fill="none">
                  <path d="M36 11 L13 36 L36 61 L36 11 Z" fill="#3B9FD4"/>
                  <path d="M36 11 L59 36 L36 61 L36 11 Z" fill="#4DD4A8"/>
                </svg>
              </div>
            <div>
              <div className="login-brand-name">Rah-AI: Vantage</div>
              <div className="login-brand-sub" style={{color:"rgba(255,255,255,0.7)",letterSpacing:"0.2px"}}>You make the call. We run the numbers.</div>
            </div>
          </div>
          <div className="login-headline">
            <span style={{fontWeight:400,fontStyle:"italic",fontSize:"13px",letterSpacing:"0px",opacity:0.5,display:"block",marginBottom:12}}>Your competitive edge, built for —</span>
            {[
              "Evaluating every profile",
              "Optimizing your roster budget",
              "Meeting compliance at every step",
              "Calling the shots, with data",
            ].map(item => (
              <div key={item} style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                <span style={{color:"#2DD4BF",flexShrink:0,fontSize:"19px",fontWeight:400,lineHeight:1}}>—</span>
                <span style={{fontWeight:600,fontStyle:"normal",fontSize:"19px",letterSpacing:"-0.3px",color:"rgba(255,255,255,0.9)",lineHeight:"1.25"}}>{item}</span>
              </div>
            ))}
          </div>


          <div className="login-stats">
            {[
              ["~4,000","Players indexed across D-I, D-II, D-III"],
              ["50","2026 portal entrants scored"],
              ["$1B+","Spent in transfer portal in 2025–26"],
            ].map(([v,l]) => (
              <div className="login-stat" key={v}>
                <div className="login-stat-icon">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2 10l3-3.5 3 2.5L12 4" stroke="#2DD4BF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <div className="login-stat-value">{v}</div>
                  <div className="login-stat-text">{l}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="login-right">
          <div className="login-form-wrap">
            <div style={{textAlign:"left",marginBottom:24,fontSize:18,fontFamily:"var(--font-sans)",whiteSpace:"nowrap"}}>
              <span style={{fontStyle:"italic",color:"var(--text-secondary)",fontWeight:400}}>"We evaluate </span>
              <span style={{fontStyle:"normal",fontWeight:700,color:"var(--text-primary)"}}>Profiles</span>
              <span style={{fontStyle:"italic",color:"var(--text-secondary)",fontWeight:400}}>, not players."</span>
            </div>
            <div className="login-form-title">Sign in</div>
            <div className="login-form-sub">Enter your credentials to access your program's dashboard.</div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Username</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="your_username"
                  value={username}
                  onChange={e => { setUsername(e.target.value); checkLocalConsent(e.target.value); }}
                  autoFocus
                  autoComplete="username"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <div style={{position:"relative"}}>
                  <input
                    className="form-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="current-password"
                    style={{paddingRight:44}}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",
                            background:"none",border:"none",cursor:"pointer",padding:4,
                            color:"var(--text-muted)",fontSize:13,lineHeight:1}}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >{showPassword ? "Hide" : "Show"}</button>
                </div>
              </div>
              <button className="login-btn" type="submit" disabled={loading || !username || !password || !tosAccepted}>
                {loading ? <><div className="spinner" style={{width:14,height:14,borderWidth:1.5,borderTopColor:"#fff"}}/>Signing in…</> : "Sign in →"}
              </button>
              <div style={{display:"flex",alignItems:"flex-start",gap:10,margin:"16px 0 4px"}}>
                <input
                  type="checkbox"
                  id="tos-checkbox"
                  checked={tosAccepted}
                  onChange={e => { if (!tosAccepted) setTosAccepted(e.target.checked); }}
                  disabled={tosAccepted}
                  style={{marginTop:3,accentColor:"var(--teal)",flexShrink:0,
                          cursor:tosAccepted?"not-allowed":"pointer",opacity:1}}
                />
                <label htmlFor="tos-checkbox"
                  style={{fontSize:12,color:"var(--text-muted)",lineHeight:1.5,
                          cursor:tosAccepted?"default":"pointer"}}>
                  {tosAccepted ? (
                    <span>
                      <span style={{color:"var(--teal)",fontWeight:600}}>&#10003; Accepted</span>
                      {" — "}
                      <a href="https://epicentrallc.com/legal/tos" target="_blank" rel="noopener noreferrer"
                        style={{color:"var(--teal)",textDecoration:"underline"}}>Terms of Service</a>
                      {" and "}
                      <a href="https://epicentrallc.com/legal/privacy" target="_blank" rel="noopener noreferrer"
                        style={{color:"var(--teal)",textDecoration:"underline"}}>Privacy Policy</a>
                      {" of Epicentra LLC."}
                    </span>
                  ) : (
                    <span>I have read and agree to the{" "}
                      <a href="https://epicentrallc.com/legal/tos" target="_blank" rel="noopener noreferrer"
                        style={{color:"var(--teal)",textDecoration:"underline"}}>Terms of Service</a>
                      {" "}and{" "}
                      <a href="https://epicentrallc.com/legal/privacy" target="_blank" rel="noopener noreferrer"
                        style={{color:"var(--teal)",textDecoration:"underline"}}>Privacy Policy</a>
                      {" "}of Epicentra LLC. I understand that platform outputs are <strong>not FMV certifications</strong> and are for <strong>internal planning use only</strong>.
                    </span>
                  )}
                </label>
              </div>
              {error && <div className="login-error">{error}</div>}
            </form>
            <div className="login-footer">Every dollar. Every decision.<br/>Powered by Epicentra LLC</div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Portal Board ──────────────────────────────────────────────────────────────
function PortalBoard({ api, program, roster, setRoster, budgetM, setBudgetM, programCtx, initialTopMatch, onImportProfile, onGoRoster, onGoNotes }) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasAutoFiltered = useRef(false);
  const [roleFilter, setRoleFilter] = useState("All");
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [offers, setOffers] = useState({});       // player_name -> offer $
  const [signedTo, setSignedTo] = useState({});   // player_name -> school string
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [watchlistKeys, setWatchlistKeys] = useState(new Set());
  const [editingSigned, setEditingSigned] = useState(null);
  const [signedInput, setSignedInput] = useState("");
  const [tierFilter, setTierFilter] = useState("All");
  const [eligFilter, setEligFilter] = useState("All");
  const [watchlistOnly, setWatchlistOnly] = useState(false);
  const [topMatchOnly, setTopMatchOnly] = useState(false);
  const [offerSlider, setOfferSlider] = useState(null);

  // Auto-set filters from program context — fires once only (first intake completion)
  useEffect(() => {
    if (hasAutoFiltered.current) return;
    if (!programCtx?.upgrade_positions?.length) return;
    hasAutoFiltered.current = true;
    const pos = programCtx.upgrade_positions;
    setRoleFilter(pos[0].toLowerCase());
  }, [JSON.stringify(programCtx?.upgrade_positions)]);

  // Auto-enable Top Profile Match when intake completes
  useEffect(() => {
    if (initialTopMatch) setTopMatchOnly(true);
  }, [initialTopMatch]);

  useEffect(() => {
    api("/portal_board?page_size=2000")
      .then(d => {
        const ps = d.players || [];
        setPlayers(ps);
        const initOffers = {};
        ps.forEach(p => { initOffers[p.player_name] = (p.predicted_nil_value / 1000000).toFixed(2); });
        setOffers(initOffers);
      })
      .finally(() => setLoading(false));
    api("/user/watchlist").then(d => {
      const keys = new Set((d.watchlist || []).map(w => w.player_key));
      setWatchlistKeys(keys);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    api(`/user/notes/${encodeURIComponent(selected.player_name)}`)
      .then(d => setNotes(d.notes || []))
      .catch(() => setNotes([]));
  }, [selected]);

  const totalOffers = players
    .filter(p => watchlistKeys.has(p.player_name))
    .reduce((sum, p) => sum + parseFloat(offers[p.player_name] || 0), 0);

  const roles = ["All", "guard", "forward", "center"];
  const filtered = (() => {
    if (topMatchOnly && programCtx?.upgrade_positions?.length > 0) {
      const needs = programCtx.burning_needs || [];
      // Burning needs → stat boosts (30% of total score)
      const needBoost = (p) => {
        let boost = 0;
        if (needs.includes("Scoring"))           boost += Math.min((p.pts_per_g||0)/30, 1) * 30;
        if (needs.includes("Playmaking"))        boost += Math.min((p.ast_per_g||0)/10, 1) * 30;
        if (needs.includes("Rebounding"))        boost += Math.min((p.reb_per_g||0)/12, 1) * 30;
        if (needs.includes("Interior Defense"))  boost += Math.min((p.blk_per_g||0)/3,  1) * 30;
        if (needs.includes("Perimeter Defense")) boost += Math.min((p.stl_per_g||0)/3,  1) * 30;
        if (needs.includes("Depth / Insurance")) boost += 15;
        return Math.min(boost, 30); // cap at 30
      };
      // Budget fit — remaining roster budget vs player's estimated value
      const totalM    = parseFloat(budgetM) || 0;
      const spentM    = (roster?.players||[]).reduce((s,pp) => s + parseFloat(pp.nil_value_m||0), 0);
      const remainingM = Math.max(totalM - spentM, 0);
      const scoreProfile = (p) => {
        const perfScore  = (p.perf_score||0);                   // 0-100 → weighted 40%
        const eligScore  = (p.elig_years_remaining||0) >= 3 ? 15
                         : (p.elig_years_remaining||0) === 2 ? 10
                         : (p.elig_years_remaining||0) === 1 ? 5 : 8;  // 15% weight
        const needScore  = needBoost(p);                        // 25% weight
        // Budget fit 20% — penalise players out of remaining budget range
        const nilValM    = (p.predicted_nil_value||0) / 1e6;
        const budgetScore = remainingM === 0 ? 10
                          : nilValM <= remainingM         ? 20
                          : nilValM <= remainingM * 1.25  ? 10
                          : 0;
        return (perfScore * 0.40) + eligScore + needScore + budgetScore;
      };
      const top = programCtx.upgrade_positions.flatMap(pos =>
        players
          .filter(p => p.role === pos.toLowerCase())
          .map(p => ({...p, _matchScore: scoreProfile(p)}))
          .sort((a,b) => b._matchScore - a._matchScore)
          .slice(0, 10)
      );
      return top;
    }
    return players
      .filter(p => roleFilter === "All" || p.role === roleFilter)
      .filter(p => !searchQ || p.player_name.toLowerCase().includes(searchQ.toLowerCase()) || p.school?.toLowerCase().includes(searchQ.toLowerCase()) || p.class_year?.toLowerCase().includes(searchQ.toLowerCase()))
      .filter(p => tierFilter === "All" || p.value_tier === tierFilter)
      .filter(p => eligFilter === "All" || (
        eligFilter === "1yr"  ? p.elig_years_remaining <= 1 :
        eligFilter === "2yr"  ? p.elig_years_remaining === 2 :
        eligFilter === "3yr+" ? p.elig_years_remaining >= 3 : true
      ))
      .filter(p => !watchlistOnly || watchlistKeys.has(p.player_name))
      .sort((a, b) => (b.predicted_nil_value || 0) - (a.predicted_nil_value || 0));
  })();

  async function toggleWatchlist(p) {
    const key = p.player_name;
    if (watchlistKeys.has(key)) {
      await api(`/user/watchlist/${encodeURIComponent(key)}`, { method: "DELETE" }).catch(() => {});
      setWatchlistKeys(s => { const n = new Set(s); n.delete(key); return n; });
    } else {
      const meta = JSON.stringify({
        role: p.role || "—", school: p.school || "—", nil: p.nil_value_m || "—",
        conf: p.conf_tier_label || p.conference || "Rest D-I",
        elig: p.elig_years_remaining || 2,
        pts: p.pts_per_g, reb: p.reb_per_g, ast: p.ast_per_g,
        fg: p.fg_pct, fg3: p.fg3_pct, ft: p.ft_pct,
        stl: p.stl_per_g, blk: p.blk_per_g, mp: p.mp_per_g,
        tov: p.tov_per_g, orb: p.orb_per_g, g: p.g,
      });
      await api("/user/watchlist", {
        method: "POST",
        body: JSON.stringify({ player_key: key, player_name: p.player_name, status: meta }),
      }).catch(() => {});
      setWatchlistKeys(s => new Set([...s, key]));
    }
  }

  async function saveNote() {
    if (!noteInput.trim() || !selected) return;
    setSavingNote(true);
    try {
      const n = await api(`/user/notes/${encodeURIComponent(selected.player_name)}`, {
        method: "POST",
        body: JSON.stringify({ note_text: noteInput.trim() }),
      });
      setNotes(prev => [n, ...prev]);
      setNoteInput("");
    } finally { setSavingNote(false); }
  }

  async function deleteNote(id) {
    await api(`/user/notes/${id}`, { method: "DELETE" }).catch(() => {});
    setNotes(prev => prev.filter(n => n.id !== id));
  }

  function commitSigned(playerName) {
    setSignedTo(prev => ({ ...prev, [playerName]: signedInput.trim() }));
    setEditingSigned(null);
    setSignedInput("");
  }

  const vc = selected ? verdictColor(selected.verdict_display || selected.verdict) : "teal";

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-title">Scout Board</div>
        <div className="page-sub">Evaluate and rank profiles against 3,574 D-I players — filter by tier, position, and eligibility.</div>
      </div>
      {programCtx?.upgrade_positions?.length > 0 && (
        <div style={{margin:"0 0 16px",padding:"10px 16px",borderRadius:8,background:"rgba(13,148,136,0.08)",border:"1px solid rgba(13,148,136,0.2)",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
          <div style={{fontSize:12,color:"var(--teal)"}}>
            <span style={{fontWeight:600}}>Program context active —</span> upgrading: {programCtx.upgrade_positions.join(", ")}
            {programCtx.burning_needs?.length > 0 && <span style={{color:"var(--text-muted)"}}> · needs: {programCtx.burning_needs.join(", ")}</span>}
          </div>
          <div style={{display:"flex",gap:8}}>
            {programCtx.upgrade_positions.map(pos => (
              <button key={pos} onClick={() => setRoleFilter(pos.toLowerCase())}
                style={{padding:"3px 10px",borderRadius:99,fontSize:11,fontWeight:600,cursor:"pointer",
                  border:"1px solid rgba(13,148,136,0.3)",background: roleFilter===pos.toLowerCase() ? "var(--accent)" : "var(--accent-dim)",
                  color: roleFilter===pos.toLowerCase() ? "#fff" : "var(--teal)"}}>
                {pos.charAt(0).toUpperCase()+pos.slice(1)}
              </button>
            ))}
            <button onClick={() => setRoleFilter("All")}
              style={{padding:"3px 10px",borderRadius:99,fontSize:11,fontWeight:500,cursor:"pointer",
                border:"1px solid var(--border)",background:"none",color:"var(--text-muted)"}}>
              All
            </button>
          </div>
        </div>
      )}
      {(() => {
        const currPlayers = (roster?.players||[]).filter(p => p.is_current);
        if (currPlayers.length >= 2) {
          const perfs = currPlayers.map(p => parseFloat(p._result?.perf_score || p._stats?.pts_per_g || 30));
          const topScore = Math.max(...perfs);
          const restAvg = perfs.filter(s => s !== topScore).reduce((a,b)=>a+b,0) / Math.max(perfs.length-1,1);
          const sdi = (topScore - restAvg) / (restAvg || 1);
          if (sdi > 0.6) return (
            <div style={{margin:"0 0 12px",padding:"10px 16px",borderRadius:8,
              background:"rgba(245,158,11,0.07)",border:"1px solid rgba(245,158,11,0.25)",
              display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:14}}>⭐</span>
              <div style={{flex:1}}>
                <span style={{fontSize:12,fontWeight:600,color:"var(--amber)"}}>Star dependency detected — </span>
                <span style={{fontSize:12,color:"var(--text-secondary)"}}>your roster relies heavily on 1–2 players. Prioritize a secondary scorer in your search.</span>
              </div>
            </div>
          );
        }
        return null;
      })()}
      <div className="kpi-row">
        {(() => {
          const spentM = (roster?.players||[]).reduce((s,p) => s + parseFloat(p.nil_value_m||p.predicted_nil_value/1000000||0), 0);
          const totalM = parseFloat(budgetM||0);
          return (
            <>
              <div className="kpi-card">
                <div className="kpi-label">Roster Budget</div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginTop:4}}>
                  <span style={{fontFamily:"var(--font-display)",fontSize:26,fontWeight:700,letterSpacing:"-0.5px"}}>${spentM.toFixed(1)}M</span>
                  <span style={{display:"flex",alignItems:"baseline",gap:1,color:"var(--text-muted)"}}>
                    <span style={{fontSize:21}}>of $</span>
                    <input
                      type="number"
                      step="0.5"
                      value={budgetM}
                      onChange={e => setBudgetM(e.target.value)}
                      style={{fontFamily:"var(--font-display)",fontSize:21,fontWeight:600,
                              border:"none",background:"none",outline:"none",width:42,
                              color:"var(--text-muted)",textAlign:"right"}}
                    />
                    <span style={{fontSize:21}}>M</span>
                  </span>
                </div>
                <div className="kpi-delta teal" style={{marginTop:4}}>spent · edit total ↗</div>
              </div>
              <div className="kpi-card clickable" onClick={onGoNotes}>
                <div className="kpi-label">Watchlisted</div>
                <div className="kpi-value">{watchlistKeys.size}</div>
                <div className="kpi-delta teal">players tracked ↗</div>
              </div>
              <div className="kpi-card clickable" onClick={onGoRoster}>
                <div className="kpi-label">Roster Size</div>
                <div className="kpi-value">{(roster?.players||[]).length}</div>
                <div className="kpi-delta up">players committed ↗</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Profiles Indexed</div>
                <div className="kpi-value">3,574</div>
                <div className="kpi-delta teal">2026 season</div>
              </div>
            </>
          );
        })()}
      </div>
      <div className="grid-2" style={{gap:16}}>
        <div className="card">
          <div className="card-header">
            <div className="card-title">Historical Profile Board<Tooltip body="3,574 D-I profiles ranked by predicted NIL value. Filter by position, tier, or watchlist." /></div>
            <div className="card-sub">compare predicted tier · as of April 2026</div>
          </div>
          <div className="search-bar">
            <Icon name="search" size={14}/>
            <input placeholder="Search by school, year or position…" value={searchQ} onChange={e => setSearchQ(e.target.value)}/>
          </div>
          <div style={{display:"flex",gap:8,padding:"10px 20px 0",alignItems:"center",flexWrap:"wrap"}}>
            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
              style={{padding:"5px 10px",borderRadius:7,border:"1px solid",
                borderColor:roleFilter!=="All"?"rgba(13,148,136,0.4)":"var(--border)",
                background:"var(--surface2)",color:roleFilter!=="All"?"var(--teal)":"var(--text-secondary)",
                fontSize:12,cursor:"pointer",outline:"none",fontFamily:"var(--font-body)",
                fontWeight:roleFilter!=="All"?600:400}}>
              <option value="All">Position: All</option>
              <option value="guard">Guard</option>
              <option value="forward">Forward</option>
              <option value="center">Center</option>
            </select>
            <select value={tierFilter} onChange={e => setTierFilter(e.target.value)}
              style={{padding:"5px 10px",borderRadius:7,border:"1px solid",
                borderColor:tierFilter!=="All"?"rgba(13,148,136,0.4)":"var(--border)",
                background:"var(--surface2)",color:tierFilter!=="All"?"var(--teal)":"var(--text-secondary)",
                fontSize:12,cursor:"pointer",outline:"none",fontFamily:"var(--font-body)",
                fontWeight:tierFilter!=="All"?600:400}}>
              <option value="All">Tier: All</option>
              <option value="Elite">Elite</option>
              <option value="Premium">Premium</option>
              <option value="First Team">First Team</option>
              <option value="Role Player">Role Player</option>
            </select>
            <select value={eligFilter} onChange={e => setEligFilter(e.target.value)}
              style={{padding:"5px 10px",borderRadius:7,border:"1px solid",
                borderColor:eligFilter!=="All"?"rgba(13,148,136,0.4)":"var(--border)",
                background:"var(--surface2)",color:eligFilter!=="All"?"var(--teal)":"var(--text-secondary)",
                fontSize:12,cursor:"pointer",outline:"none",fontFamily:"var(--font-body)",
                fontWeight:eligFilter!=="All"?600:400}}>
              <option value="All">Eligibility: All</option>
              <option value="1yr">1 yr left</option>
              <option value="2yr">2 yrs left</option>
              <option value="3yr+">3+ yrs left</option>
            </select>
            <div style={{width:"0.5px",height:20,background:"var(--border)",margin:"0 2px"}}/>
            <button
              className={`filter-pill ${watchlistOnly?"active":""}`}
              onClick={() => setWatchlistOnly(v => !v)}
              style={!watchlistOnly ? {borderColor:"rgba(13,148,136,0.3)",color:"var(--teal)"} : {}}>
              ★ Watchlisted{watchlistOnly && watchlistKeys.size > 0 ? ` (${watchlistKeys.size})` : ""}
            </button>
            {programCtx?.upgrade_positions?.length > 0 && (
              <button
                className={`filter-pill ${topMatchOnly?"active":""}`}
                onClick={() => setTopMatchOnly(v => !v)}
                style={!topMatchOnly ? {borderColor:"rgba(13,148,136,0.3)",color:"var(--teal)"} : {}}>
                🎯 Top Profile Match
              </button>
            )}
          </div>

          <div className="scroll-list" style={{marginTop:10}}>
            {loading ? <div className="loading"><div className="spinner"/>Loading profiles…</div>
            : filtered.length === 0 ? <div className="empty">No players found</div>
            : filtered.map((p, i) => {
              const [bg, fg] = avColor(i);
              const onList = watchlistKeys.has(p.player_name);
              const signed = signedTo[p.player_name];
              return (
                <div key={p.player_name}
                  className={`player-row ${selected?.player_name===p.player_name?"selected":""}${onList?" checked":""}`}
                  style={{opacity: signed ? 0.6 : 1}}
                  onClick={() => setSelected(p)}
                >
                  <span style={{
                    fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:10, flexShrink:0,
                    background: "rgba(13,148,136,0.08)",
                    color: "var(--teal)",
                    border: "1px solid rgba(13,148,136,0.2)",
                  }}>2026</span>
                  <div className="player-av" style={{background:bg, color:fg}}>{p.class_year || "?"}</div>
                  <div className="player-info">
                    <div className="player-name" style={{fontSize:12.5,fontWeight:600}}>{(p.role||"").charAt(0).toUpperCase()+(p.role||"").slice(1)} · {p.school}</div>
                    <div style={{fontSize:11,color:"var(--text-muted)",marginTop:1}}>{p.player_name}</div>
                    <div className="player-meta">{(p.role||"").charAt(0).toUpperCase()+(p.role||"").slice(1)} · {p.school} · {p.elig_years_remaining}yr elig</div>
                  </div>
                  {signed
                    ? <span className="signed-chip signed">→ {signed}</span>
                    : <span className={`signed-chip watchlist-btn${onList ? " watchlisted" : ""}`} onClick={e => { e.stopPropagation(); toggleWatchlist(p); }}>{onList ? "✓ Watchlisted" : "+ Watchlist"}</span>
                  }
                  <span className={`score-pill ${tierClass(p.value_tier)}`}>{p.value_tier || "—"}</span>
                </div>
              );
            })}
          </div>
          {watchlistKeys.size > 0 && (
            <div className="offer-bar">
              <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
                <div className="offer-bar-stat">{watchlistKeys.size} profile{watchlistKeys.size!==1?"s":""} watchlisted</div>
                {["guard","forward","center"].map(r => {
                  const cnt = [...watchlistKeys].filter(k => {
                    const p = players.find(x => x.player_name === k);
                    return p?.role === r;
                  }).length;
                  if (!cnt) return null;
                  return <span key={r} style={{fontSize:12,color:"var(--text-secondary)",textTransform:"capitalize"}}>{r.charAt(0).toUpperCase()+r.slice(1)}s: <strong style={{color:"var(--text-primary)"}}>{cnt}</strong></span>;
                })}
              </div>
            </div>
          )}
        </div>

        {!selected && (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",
                       minHeight:360,flexDirection:"column",gap:16,
                       background:"var(--surface)",border:"1px solid var(--border)",
                       borderRadius:"var(--radius-lg)"}}>
            <div style={{fontSize:40,opacity:0.5}}>🔍</div>
            <div style={{fontSize:16,fontWeight:600,color:"var(--text-primary)",textAlign:"center",
                         lineHeight:1.6,maxWidth:240,marginTop:4}}>
              Select a player profile
            </div>
            <div style={{fontSize:13,color:"var(--text-muted)",textAlign:"center",
                         lineHeight:1.6,maxWidth:220}}>
              ← Click any row on the board<br/>to view their stats and evaluate
            </div>
          </div>
        )}
        {selected && (
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">{selected.player_name}</div>
                  <div className="card-sub">{selected.role} · {selected.school} · {selected.conference} · {selected.elig_years_remaining}yr eligibility</div>
                </div>
                <span className={`score-pill ${tierClass(selected.value_tier)}`}>{selected.class_year || "—"}</span>
              </div>
              <div style={{padding:"16px 20px 0"}}>
                <div style={{display:"flex",gap:16,alignItems:"flex-start",marginBottom:16}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:4}}>Performance score</div>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{flex:1,height:8,background:"var(--surface3)",borderRadius:4,overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${selected.perf_score||0}%`,background:"var(--accent)",borderRadius:4,transition:"width .5s"}}/>
                      </div>
                      <span style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:18}}>{selected.perf_score?.toFixed(0) || "—"}</span>
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:6}}>Predicted tier</div>
                    <span className={`score-pill ${tierClass(selected.value_tier)}`} style={{fontSize:15,padding:"6px 14px"}}>{selected.value_tier || "—"}</span>
                    
                  </div>
                </div>
              </div>
              <div className="stats-grid">
                {[
                  ["PTS/G",  selected.pts_per_g?.toFixed(1)],
                  ["REB/G",  selected.reb_per_g?.toFixed(1)],
                  ["AST/G",  selected.ast_per_g?.toFixed(1)],
                  ["FG%",    selected.fg_pct  ? `${(selected.fg_pct*100).toFixed(1)}%`  : "—"],
                  ["3P%",    selected.fg3_pct ? `${(selected.fg3_pct*100).toFixed(1)}%` : "—"],
                  ["FT%",    selected.ft_pct  ? `${(selected.ft_pct*100).toFixed(1)}%`  : "—"],
                  ["STL/G",  selected.stl_per_g?.toFixed(1)],
                  ["BLK/G",  selected.blk_per_g?.toFixed(1)],
                ].map(([l,v]) => (
                  <div className="stat-cell" key={l}>
                    <div className="stat-v">{v || "—"}</div>
                    <div className="stat-l">{l}</div>
                  </div>
                ))}
              </div>
              <div style={{padding:"14px 20px",borderTop:"1px solid var(--border)"}}>
                <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:10}}>
                  Market tier based on statistical profile
                </div>
                <button
                  style={{width:"100%", padding:"12px 20px", border:"none", borderRadius:8, cursor:"pointer",
                          background:"linear-gradient(135deg, #7C3AED 0%, #A855F7 60%, #C084FC 100%)",
                          color:"#fff", fontSize:14, fontWeight:600, letterSpacing:"-0.2px",
                          boxShadow:"0 2px 12px rgba(124,58,237,0.35)", transition:"opacity 0.15s"}}
                  onMouseOver={e=>e.currentTarget.style.opacity="0.88"}
                  onMouseOut={e=>e.currentTarget.style.opacity="1"}
                  onClick={() => onImportProfile(selected)}>
                  ↗ Import to Profile Evaluator
                </button>
              </div>

            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title">Scouting notes</div>
                <div className="card-sub">{selected.player_name}</div>
              </div>
              <div style={{padding:"12px 20px 0"}}>
                <div style={{display:"flex",gap:8}}>
                  <input
                    className="form-input"
                    style={{flex:1,fontSize:13}}
                    placeholder="Add a note (offer amount, visit outcome, contact date…)"
                    value={noteInput}
                    onChange={e => setNoteInput(e.target.value)}
                    onKeyDown={e => e.key==="Enter" && saveNote()}
                  />
                  <button className="btn btn-primary btn-sm" onClick={saveNote} disabled={savingNote || !noteInput.trim()}>
                    {savingNote ? "…" : "Add"}
                  </button>
                </div>
              </div>
              <div className="scroll-list" style={{maxHeight:200,marginTop:10}}>
                {notes.length === 0
                  ? <div className="empty" style={{padding:"20px"}}>No notes yet</div>
                  : notes.map(n => (
                    <div className="note-item" key={n.id}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                        <div className="note-text">{n.note_text}</div>
                        <button className="btn btn-icon" style={{background:"none",border:"none",color:"var(--text-muted)",padding:"2px 4px"}} onClick={() => deleteNote(n.id)}>
                          <Icon name="trash" size={13}/>
                        </button>
                      </div>
                      <div className="note-time">{fmtDate(n.created_at)}</div>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{
        marginTop:32, padding:"10px 0",
        borderTop:"1px solid var(--border)",
        fontSize:11, color:"var(--text-muted)",
        lineHeight:1.6, textAlign:"center",
      }}>
        NIL valuations are AI-generated estimates based on statistical modeling.
        Actual market values may differ. Not financial or legal advice. · Rah-AI: Vantage by Epicentra LLC
      </div>
    </div>
  );
}

// ── Roster Builder ─────────────────────────────────────────────────────────────

// ── RunTheSet ─────────────────────────────────────────────────────────────────
function RunTheSet({ api, roster }) {
  const players = roster?.players || [];
  const [starters, setStarters] = useState([null,null,null,null,null]);
  const [objective, setObjective] = useState("Max scoring");
  const [customMode, setCustomMode] = useState(false);
  const [customAtk, setCustomAtk] = useState(60);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSlot, setPickerSlot] = useState(null);

  const OBJECTIVES = ["Max scoring","Perimeter attack","Interior attack","Lockdown defense","Perimeter defense","Transition/pace","Balanced two-way"];
  const OBJ_ICONS = {"Max scoring":"⬆","Perimeter attack":"○","Interior attack":"●","Lockdown defense":"🛡","Perimeter defense":"◑","Transition/pace":"⚡","Balanced two-way":"⇄"};

  const filled = starters.filter(Boolean);
  const allFilled = filled.length === 5;
  const usedIds = filled.map(s => s.player_name);
  const available = players.filter(p => !usedIds.includes(p.player_name));

  function computeAD(s) {
    if (!s.length) return 50;
    const w = [0.35,0.25,0.2,0.12,0.08];
    // Normalize to 0-1 scale using realistic D-I maximums
    const pts = s.map(p => Math.min(parseFloat(p._stats?.pts_per_g||8) / 25, 1));
    const def = s.map(p => Math.min((parseFloat(p._stats?.stl_per_g||0.8) + parseFloat(p._stats?.blk_per_g||0.5)) / 4, 1));
    const sortedPts = [...pts].sort((a,b)=>b-a);
    const sortedDef = [...def].sort((a,b)=>b-a);
    let off=0, dfn=0;
    s.forEach((_,i) => { off+=(sortedPts[i]||0.3)*(w[i]||0.1); dfn+=(sortedDef[i]||0.2)*(w[i]||0.1); });
    const base = off + dfn || 1;
    return Math.round((off/base)*100);
  }

  function getPosArch(s) {
    const g=s.filter(x=>x.role==="guard").length, f=s.filter(x=>x.role==="forward").length, c=s.filter(x=>x.role==="center").length;
    if(g>=3)return"Guard-heavy"; if(f>=3)return"Wing-heavy"; if(c>=2)return"Big-heavy"; return"Balanced";
  }

  function getTierMix(s) {
    const tiers = s.map(x => x._result?.tier||"Role Player");
    const e=tiers.filter(t=>t==="Elite").length, p=tiers.filter(t=>t==="Premium").length, r=tiers.filter(t=>t==="Role Player").length;
    if(e>=2)return"Elite-led"; if(p>=2)return"Premium-core"; if(r>=3)return"Role-Player-heavy"; return"Mixed";
  }

  function getObjFromSlider(atk) {
    if(atk>=80)return"Max scoring"; if(atk>=65)return"Perimeter attack"; if(atk>=55)return"Transition/pace";
    if(atk>=45)return"Balanced two-way"; if(atk>=35)return"Perimeter defense"; if(atk>=20)return"Lockdown defense"; return"Interior attack";
  }

  function getTierStyle(tier) {
    if(tier==="Elite")return{background:"rgba(20,184,166,0.12)",color:"#14b8a6",border:"1px solid rgba(20,184,166,0.3)"};
    if(tier==="Premium")return{background:"rgba(99,102,241,0.12)",color:"#818cf8",border:"1px solid rgba(99,102,241,0.3)"};
    if(tier==="First Team")return{background:"rgba(245,158,11,0.12)",color:"#f59e0b",border:"1px solid rgba(245,158,11,0.3)"};
    return{background:"var(--surface2)",color:"var(--text-muted)",border:"1px solid var(--border)"};
  }

  function getRoleColor(role) {
    if(role==="guard")return{bg:"rgba(20,184,166,0.15)",color:"#14b8a6"};
    if(role==="forward")return{bg:"rgba(99,102,241,0.15)",color:"#818cf8"};
    return{bg:"rgba(245,158,11,0.15)",color:"#f59e0b"};
  }

  function getMeterColor(s){return s>=65?"#14b8a6":s>=45?"#6366f1":"#ef4444";}
  function getMeterLabel(s){return s>=75?"Heavy attack":s>=60?"Attack-leaning":s>=45?"Balanced":s>=30?"Defense-leaning":"Heavy defense";}
  function getUrgColor(u){return u==="Critical"?{bg:"rgba(239,68,68,0.1)",color:"#ef4444"}:u==="Helpful"?{bg:"rgba(245,158,11,0.1)",color:"#f59e0b"}:{bg:"var(--surface2)",color:"var(--text-muted)"};}

  async function runTheSet() {
    if (!allFilled) return;
    setLoading(true); setResult(null);
    const adScore = computeAD(filled);
    const posArch = getPosArch(filled);
    const tierMix = getTierMix(filled);
    const effectiveObj = customMode ? getObjFromSlider(customAtk) : objective;
    try {
      const data = await api("/run_the_set", {
        method: "POST",
        body: JSON.stringify({ ad_score: adScore, position_archetype: posArch, tier_mix: tierMix, objective: effectiveObj })
      });
      setResult(data);
    } catch(e) {
      console.error("run_the_set error", e);
    } finally {
      setLoading(false);
    }
  }

  const adScore = computeAD(filled);
  const customDef = 100 - customAtk;
  const effectiveObj = customMode ? getObjFromSlider(customAtk) : objective;

  const rtsStyles = {
    card: { background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"var(--radius-lg)", overflow:"hidden" },
    header: { padding:"12px 16px", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"space-between" },
    sectionLabel: { fontSize:10, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:"var(--text-muted)", marginBottom:8 },
    slot: { border:"1px dashed var(--border)", borderRadius:8, padding:"8px 10px", cursor:"pointer", display:"flex", alignItems:"center", gap:8, minHeight:42, transition:"all 0.15s" },
    slotFilled: { border:"1px solid var(--border)", background:"var(--surface2)", borderRadius:8, padding:"8px 10px", display:"flex", alignItems:"center", gap:8, minHeight:42, position:"relative" },
  };

  return (
    <div style={rtsStyles.card}>
      <div style={rtsStyles.header}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span className="card-title">Running the Set<Tooltip body="Pick 5 starters and a set objective to simulate how your lineup performs." /></span>
          <span style={{fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:99,background:"rgba(20,184,166,0.12)",color:"#14b8a6",border:"1px solid rgba(20,184,166,0.3)",letterSpacing:"0.05em"}}>BETA</span>
        </div>
        <span style={{fontSize:11,color:"var(--text-muted)"}}>{filled.length}/5 starters</span>
      </div>

      {/* Two-column: lineup + objective */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",borderBottom:"1px solid var(--border)"}}>
        {/* Left: Starting lineup */}
        <div style={{padding:"14px 16px",borderRight:"1px solid var(--border)"}}>
          <div style={rtsStyles.sectionLabel}>Starting lineup</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {starters.map((s,i) => {
              if (!s) return (
                <div key={i} style={rtsStyles.slot} onClick={() => { setPickerSlot(i); setPickerOpen(true); }}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--surface2)"}
                  onMouseLeave={e=>e.currentTarget.style.background=""}>
                  <span style={{fontSize:10,color:"var(--text-muted)",minWidth:14}}>{i+1}</span>
                  <span style={{fontSize:11,color:"var(--text-muted)"}}>+ Add starter</span>
                </div>
              );
              const rc = getRoleColor(s.role);
              const ts = getTierStyle(s._result?.tier||"Role Player");
              return (
                <div key={i} style={rtsStyles.slotFilled}>
                  <span style={{fontSize:10,color:"var(--text-muted)",minWidth:14}}>{i+1}</span>
                  <div style={{width:22,height:22,borderRadius:"50%",background:rc.bg,color:rc.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:700,flexShrink:0,textTransform:"uppercase"}}>
                    {s.role?.slice(0,1)||"?"}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:11,fontWeight:600,color:"var(--text-primary)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.player_name||`Profile ${i+1}`}</div>
                    <div style={{display:"flex",alignItems:"center",gap:4,marginTop:1}}>
                      <span style={{...ts,fontSize:9,padding:"1px 5px",borderRadius:99,fontWeight:600}}>{s._result?.tier||"—"}</span>
                      <span style={{fontSize:9,color:"var(--text-muted)"}}>{s.elig_years||"—"}yr</span>
                    </div>
                  </div>
                  <button onClick={() => { const ns=[...starters]; ns[i]=null; setStarters(ns); setResult(null); }}
                    style={{width:14,height:14,borderRadius:"50%",background:"var(--surface)",border:"1px solid var(--border)",color:"var(--text-muted)",fontSize:9,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,lineHeight:1}}>×</button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Objective */}
        <div style={{padding:"14px 16px"}}>
          <div style={rtsStyles.sectionLabel}>Set objective</div>
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            {OBJECTIVES.map(o => {
              const active = !customMode && objective===o;
              return (
                <button key={o} onClick={() => { setObjective(o); setCustomMode(false); setResult(null); }}
                  style={{padding:"6px 10px",borderRadius:6,border:active?"1px solid rgba(20,184,166,0.5)":"1px solid var(--border)",background:active?"rgba(20,184,166,0.12)":"var(--surface)",color:active?"#14b8a6":"var(--text-secondary)",fontSize:11,cursor:"pointer",textAlign:"left",fontWeight:active?600:400,transition:"all 0.15s",display:"flex",alignItems:"center",gap:6,width:"100%"}}>
                  <span style={{fontSize:10}}>{OBJ_ICONS[o]||"•"}</span>{o}
                </button>
              );
            })}
            {/* Custom split */}
            <button onClick={() => { setCustomMode(!customMode); setResult(null); }}
              style={{padding:"6px 10px",borderRadius:6,border:customMode?"1px solid rgba(20,184,166,0.5)":"1px dashed var(--border)",background:customMode?"rgba(20,184,166,0.12)":"var(--surface)",color:customMode?"#14b8a6":"var(--text-muted)",fontSize:11,cursor:"pointer",textAlign:"left",fontWeight:customMode?600:400,transition:"all 0.15s",display:"flex",alignItems:"center",gap:6,width:"100%"}}>
              <span style={{fontSize:10}}>⟺</span>Custom split{customMode?` · ${customAtk}A/${customDef}D`:""}
            </button>
            {customMode && (
              <div style={{background:"var(--surface2)",border:"1px solid rgba(20,184,166,0.3)",borderRadius:8,padding:"10px 12px",marginTop:2}}>
                <input type="range" min="0" max="100" step="1" value={customAtk}
                  onChange={e => { setCustomAtk(parseInt(e.target.value)); setResult(null); }}
                  style={{width:"100%",accentColor:"#14b8a6",marginBottom:8}}/>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:16,fontWeight:700,color:"#14b8a6"}}>{customAtk}</div>
                    <div style={{fontSize:10,color:"var(--text-muted)"}}>Attack</div>
                  </div>
                  <div style={{width:1,height:32,background:"var(--border)"}}/>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:16,fontWeight:700,color:"#6366f1"}}>{customDef}</div>
                    <div style={{fontSize:10,color:"var(--text-muted)"}}>Defense</div>
                  </div>
                </div>
                <div style={{marginTop:8,fontSize:10,color:"var(--text-muted)",textAlign:"center"}}>Maps to: <span style={{color:"#14b8a6",fontWeight:600}}>{getObjFromSlider(customAtk)}</span></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AD Meter — only when all 5 filled */}
      {allFilled && (
        <div style={{padding:"12px 16px",borderBottom:"1px solid var(--border)"}}>
          <div style={rtsStyles.sectionLabel}>Attack / defense profile</div>
          <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:6}}>
            <span style={{fontSize:20,fontWeight:700,color:"var(--text-primary)"}}>{adScore}</span>
            <span style={{fontSize:11,color:"var(--text-muted)"}}>{getMeterLabel(adScore)}</span>
          </div>
          <div style={{height:6,borderRadius:99,background:"var(--surface2)",overflow:"hidden"}}>
            <div style={{height:"100%",borderRadius:99,background:getMeterColor(adScore),width:`${adScore}%`,transition:"width 0.4s ease"}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
            <span style={{fontSize:10,color:"var(--text-muted)"}}>← Defense</span>
            <span style={{fontSize:10,color:"var(--text-muted)"}}>Attack →</span>
          </div>
        </div>
      )}

      {/* Run button */}
      <div style={{padding:"14px 16px",borderBottom: (result||loading)?"1px solid var(--border)":"none"}}>
        <button onClick={runTheSet} disabled={!allFilled||loading}
          style={{width:"100%",padding:"12px",borderRadius:8,background:allFilled&&!loading?"linear-gradient(135deg,#7c3aed,#4f46e5)":"var(--surface2)",border:"none",color:allFilled&&!loading?"#fff":"var(--text-muted)",fontSize:13,fontWeight:600,cursor:allFilled&&!loading?"pointer":"not-allowed",display:"flex",alignItems:"center",justifyContent:"center",gap:8,transition:"all 0.15s",boxShadow:allFilled&&!loading?"0 2px 12px rgba(124,58,237,0.35)":"none"}}>
          {loading ? <><div className="spinner" style={{width:14,height:14,borderWidth:2}}/>Analyzing...</> : <>🏀 Run the Set</>}
        </button>
        {!allFilled && <div style={{fontSize:11,color:"var(--text-muted)",textAlign:"center",marginTop:8}}>Select all 5 starters to run this set</div>}
      </div>

      {/* Result */}
      {loading && (
        <div style={{padding:"20px 16px",textAlign:"center"}}>
          <div className="spinner" style={{margin:"0 auto 8px"}}/>
          <div style={{fontSize:12,color:"var(--text-muted)"}}>Analyzing set...</div>
        </div>
      )}
      {result && !loading && (
        <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:10}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",color:"var(--text-muted)",marginBottom:2}}>Set report</div>

          {/* Synopsis card */}
          {(result.excels_at || result.exposure) && (
            <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:8,padding:"12px 14px",display:"flex",flexDirection:"column",gap:8}}>
              {result.excels_at && (
                <div style={{display:"flex",alignItems:"flex-start",gap:8}}>
                  <span style={{fontSize:13,flexShrink:0}}>✅</span>
                  <div>
                    <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",color:"#14b8a6",marginRight:6}}>Excels at</span>
                    <span style={{fontSize:12,color:"var(--text-primary)",lineHeight:1.5}}>{result.excels_at}</span>
                  </div>
                </div>
              )}
              {result.exposure && (
                <div style={{display:"flex",alignItems:"flex-start",gap:8,paddingTop:result.excels_at?8:0,borderTop:result.excels_at?"1px solid var(--border)":"none"}}>
                  <span style={{fontSize:13,flexShrink:0}}>⚠️</span>
                  <div>
                    <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",color:"#f59e0b",marginRight:6}}>Exposure</span>
                    <span style={{fontSize:12,color:"var(--text-primary)",lineHeight:1.5}}>{result.exposure}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Summary */}
          <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:8,padding:"12px 14px"}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",color:"var(--text-muted)",marginBottom:6}}>Set analysis</div>
            <div style={{fontSize:12,color:"var(--text-primary)",lineHeight:1.65}}>{result.narrative?.summary}</div>
          </div>

          {/* Overperformer */}
          <div style={{background:"rgba(20,184,166,0.08)",border:"1px solid rgba(20,184,166,0.25)",borderRadius:8,padding:"10px 12px"}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",color:"#14b8a6",marginBottom:4}}>⬆ Key lever</div>
            <div style={{fontSize:12,color:"var(--text-secondary)",lineHeight:1.55}}>{result.narrative?.overperformer_flag}</div>
          </div>

          {/* Mismatch */}
          <div style={{background:"rgba(239,68,68,0.07)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:8,padding:"10px 12px"}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",color:"#ef4444",marginBottom:4}}>⚠ Downside</div>
            <div style={{fontSize:12,color:"var(--text-secondary)",lineHeight:1.55}}>{result.narrative?.mismatch_flag}</div>
          </div>

          {/* Bench schemes */}
          {result.bench_schemes?.length > 0 && (
            <div>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",color:"var(--text-muted)",marginBottom:6}}>Bench scheme suggestions</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {result.bench_schemes.map((b,i) => {
                  const uc = getUrgColor(b.urgency);
                  return (
                    <div key={i} style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:8,padding:"10px 12px"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:2}}>
                        <span style={{fontSize:12,fontWeight:600,color:"var(--text-primary)"}}>{b.role}</span>
                        <span style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:99,background:uc.bg,color:uc.color}}>{b.urgency}</span>
                      </div>
                      <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:4}}>{b.profile_type}</div>
                      <div style={{fontSize:11,color:"var(--text-secondary)",lineHeight:1.5,paddingTop:6,borderTop:"1px solid var(--border)"}}>{b.why}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{fontSize:10,color:"var(--text-muted)",lineHeight:1.5,borderTop:"1px solid var(--border)",paddingTop:10}}>
            {result.disclaimer}
          </div>
        </div>
      )}

      {/* Picker modal */}
      {pickerOpen && (
        <div style={{position:"fixed",inset:0,zIndex:200,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={() => setPickerOpen(false)}>
          <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-lg)",padding:16,width:300,maxHeight:380,display:"flex",flexDirection:"column",gap:10}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:13,fontWeight:700,color:"var(--text-primary)"}}>Select starter {pickerSlot+1}</div>
            <div style={{overflowY:"auto",display:"flex",flexDirection:"column",gap:5}}>
              {available.length === 0
                ? <div style={{fontSize:12,color:"var(--text-muted)",padding:"6px 0"}}>All roster players already selected</div>
                : available.map(p => {
                    const rc = getRoleColor(p.role);
                    return (
                      <div key={p.player_name} onClick={() => { const ns=[...starters]; ns[pickerSlot]=p; setStarters(ns); setPickerOpen(false); setResult(null); }}
                        style={{padding:"9px 11px",borderRadius:8,border:"1px solid var(--border)",cursor:"pointer",display:"flex",alignItems:"center",gap:9,transition:"background 0.1s"}}
                        onMouseEnter={e=>e.currentTarget.style.background="var(--surface2)"}
                        onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <div style={{width:22,height:22,borderRadius:"50%",background:rc.bg,color:rc.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:700,textTransform:"uppercase"}}>{p.role?.slice(0,1)||"?"}</div>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:"var(--text-primary)"}}>{p.player_name}</div>
                          <div style={{fontSize:10,color:"var(--text-muted)",textTransform:"capitalize"}}>{p.role} · {p.elig_years||"—"}yr</div>
                        </div>
                      </div>
                    );
                  })}
            </div>
            <div style={{fontSize:12,color:"var(--text-muted)",textAlign:"center",cursor:"pointer",padding:4}} onClick={() => setPickerOpen(false)}>Cancel</div>
          </div>
        </div>
      )}
    </div>
  );
}



function CompatResultView({ result, compatPosTab, setCompatPosTab, expandedCompatIdx, setExpandedCompatIdx }) {
  const VSTYLE = {
    "Strong Upgrade":  {bg:"rgba(124,58,237,0.10)", border:"rgba(124,58,237,0.30)", color:"#7c3aed"},
    "Solid Fit":       {bg:"rgba(99,102,241,0.10)", border:"rgba(99,102,241,0.30)", color:"#818cf8"},
    "Depth Add":       {bg:"rgba(59,130,246,0.10)", border:"rgba(59,130,246,0.30)", color:"#3b82f6"},
    "Rotation":        {bg:"rgba(13,148,136,0.10)", border:"rgba(13,148,136,0.30)", color:"var(--teal)"},
    "Scheme Fit":      {bg:"rgba(245,158,11,0.10)", border:"rgba(245,158,11,0.30)", color:"var(--amber)"},
    "Marginal":        {bg:"rgba(156,163,175,0.10)",border:"rgba(156,163,175,0.30)",color:"var(--text-muted)"},
    "Positional Fallback": {bg:"rgba(239,68,68,0.08)",  border:"rgba(239,68,68,0.25)",  color:"var(--red)"},
    "Roster Surplus":  {bg:"rgba(239,68,68,0.08)",  border:"rgba(239,68,68,0.25)",  color:"var(--red)"},
    "Positional Risk": {bg:"rgba(239,68,68,0.08)",  border:"rgba(239,68,68,0.25)",  color:"var(--red)"},
  };
  const posAv = r => r==="guard"?"G":r==="forward"?"F":"C";
  const posBg = r => r==="guard"?"rgba(59,130,246,0.15)":r==="forward"?"rgba(13,148,136,0.15)":"rgba(245,158,11,0.15)";
  const posFg = r => r==="guard"?"#3b82f6":r==="forward"?"var(--teal)":"var(--amber)";
  const allRoles = [...new Set((result.scored_players||[]).map(p=>p.role))];
  const filtered = (result.scored_players||[]).filter(p => compatPosTab==="all"||p.role===compatPosTab);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {allRoles.length > 1 && (
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {["all",...allRoles].map(pos => (
            <button key={pos} onClick={() => { setCompatPosTab(pos); setExpandedCompatIdx(0); }}
              style={{padding:"4px 12px",borderRadius:99,fontSize:11,fontWeight:500,cursor:"pointer",
                border:`1px solid ${compatPosTab===pos?"var(--teal)":"var(--border)"}`,
                background:compatPosTab===pos?"rgba(13,148,136,0.1)":"none",
                color:compatPosTab===pos?"var(--teal)":"var(--text-secondary)"}}>
              {pos==="all"?"All":(pos.charAt(0).toUpperCase()+pos.slice(1))+"s"}
              {" "}({pos==="all"?(result.scored_players||[]).length:(result.scored_players||[]).filter(p=>p.role===pos).length})
            </button>
          ))}
        </div>
      )}
      {filtered.length > 1 && (
        <div style={{background:"var(--surface2)",borderRadius:8,padding:"10px 14px",border:"1px solid var(--border)"}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",color:"var(--text-muted)",marginBottom:8}}>Best fit ranking</div>
          {filtered.map((np,i) => {
            const vs = VSTYLE[np.verdict]||{bg:"transparent",border:"transparent",color:"var(--text-muted)"};
            return (
              <div key={i} onClick={() => setExpandedCompatIdx(i)}
                style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",cursor:"pointer",
                  borderBottom:i<filtered.length-1?"1px solid var(--border)":"none"}}>
                <span style={{fontSize:11,color:"var(--text-muted)",width:16,flexShrink:0}}>{i+1}.</span>
                <div style={{width:24,height:24,borderRadius:"50%",background:posBg(np.role),
                  display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <span style={{fontSize:10,fontWeight:700,color:posFg(np.role)}}>{posAv(np.role)}</span>
                </div>
                <span style={{fontSize:12,fontWeight:500,color:"var(--text-primary)",flex:1,
                  whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{np.player_name}</span>
                <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:99,flexShrink:0,
                  background:vs.bg,border:`1px solid ${vs.border}`,color:vs.color}}>{np.verdict}</span>
                <div style={{display:"flex",gap:4,flexShrink:0,alignItems:"center"}}>
                  <span style={{fontSize:10,color:"var(--text-muted)",fontWeight:500}}>Pos</span>
                  <span style={{fontSize:11,fontWeight:700,color:"var(--text-primary)",width:22,textAlign:"right"}}>{np.position_score ?? np.fit_score}</span>
                  <span style={{fontSize:10,color:"var(--border)"}}>·</span>
                  <span style={{fontSize:10,color:"var(--text-muted)",fontWeight:500}}>Overall</span>
                  <span style={{fontSize:11,fontWeight:700,color:vs.color,width:22,textAlign:"right"}}>{np.roster_fit_score ?? np.fit_score}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {filtered.map((np, idx) => {
        const vs = VSTYLE[np.verdict]||{bg:"rgba(154,160,176,0.08)",border:"rgba(154,160,176,0.25)",color:"var(--text-muted)"};
        const isOpen = expandedCompatIdx===idx;
        return (
          <div key={idx} style={{border:`1px solid ${isOpen?vs.border:"var(--border)"}`,borderRadius:10,
            overflow:"hidden",background:isOpen?vs.bg:"var(--surface)"}}>
            <div onClick={() => setExpandedCompatIdx(isOpen?null:idx)}
              style={{padding:"10px 14px",display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
              <div style={{width:32,height:32,borderRadius:"50%",background:posBg(np.role),
                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <span style={{fontSize:13,fontWeight:700,color:posFg(np.role)}}>{posAv(np.role)}</span>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--text-primary)",
                  whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{np.player_name}</div>
                <div style={{fontSize:11,color:"var(--text-muted)",marginTop:1,textTransform:"capitalize"}}>
                  {np.role} · {np.role_projection}
                  <span style={{marginLeft:6,fontWeight:600,color:"var(--text-secondary)"}}>
                    Position <span style={{color:"var(--text-primary)"}}>{np.position_score ?? np.fit_score}</span>
                    {" · "}
                    Overall <span style={{color:vs.color}}>{np.roster_fit_score ?? np.fit_score}</span>
                  </span>
                </div>
              </div>
              <span style={{fontSize:10,fontWeight:600,padding:"3px 9px",borderRadius:99,flexShrink:0,
                background:vs.bg,border:`1px solid ${vs.border}`,color:vs.color}}>{np.verdict}</span>
              <span style={{fontSize:12,color:"var(--text-muted)",flexShrink:0}}>{isOpen?"▲":"▼"}</span>
            </div>
            {isOpen && (
              <div style={{padding:"12px 14px",borderTop:`1px solid ${vs.border}`,
                background:"var(--surface)",display:"flex",flexDirection:"column",gap:8}}>
                <div style={{fontSize:11,color:"var(--text-secondary)",lineHeight:1.6,paddingBottom:6,borderBottom:"1px solid var(--border)"}}>
                  {np.why_text}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <span style={{fontSize:11,padding:"3px 9px",borderRadius:99,background:"var(--surface2)",
                    border:"1px solid var(--border)",color:"var(--text-secondary)",fontWeight:600}}>
                    Projected: {np.role_projection}
                  </span>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <span style={{fontSize:11,color:"var(--text-muted)"}}>
                      Position <strong style={{color:"var(--text-primary)"}}>{np.position_score ?? np.fit_score}</strong>
                    </span>
                    <span style={{fontSize:11,color:"var(--text-muted)"}}>
                      Overall <strong style={{color:vs.color}}>{np.roster_fit_score ?? np.fit_score}</strong>
                    </span>
                  </div>
                </div>
                {(np.stat_deltas||[]).length > 0 && (
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {(np.stat_deltas||[]).map(d => (
                      <span key={d.label} style={{fontSize:11,padding:"2px 8px",borderRadius:6,
                        background:d.delta>0?"rgba(13,148,136,0.08)":"rgba(239,68,68,0.08)",
                        border:d.delta>0?"1px solid rgba(13,148,136,0.2)":"1px solid rgba(239,68,68,0.2)",
                        color:d.delta>0?"var(--teal)":"var(--red)",fontWeight:600}}>
                        {d.delta>0?"+":""}{d.delta.toFixed(1)} {d.label}
                      </span>
                    ))}
                  </div>
                )}
                {(np.need_matches||[]).length > 0 && (
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                    <span style={{fontSize:10,color:"var(--text-muted)"}}>Addresses:</span>
                    {(np.need_matches||[]).map(n => (
                      <span key={n} style={{fontSize:10,padding:"2px 7px",borderRadius:99,
                        background:"rgba(13,148,136,0.1)",border:"1px solid rgba(13,148,136,0.25)",
                        color:"var(--teal)",fontWeight:600}}>✓ {n}</span>
                    ))}
                  </div>
                )}
                <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:4,marginTop:2}}>
                  {[
                    {label:"Need",   val:(np.signals||{}).need||0,    max:25},
                    {label:"Upgrade",val:(np.signals||{}).upgrade||0, max:25},
                    {label:"Role",   val:(np.signals||{}).role||0,    max:20},
                    {label:"Comp",   val:(np.signals||{}).comp||0,    max:20},
                    {label:"Elig",   val:(np.signals||{}).elig||0,    max:10},
                  ].map(sig => (
                    <div key={sig.label} style={{textAlign:"center"}}>
                      <div style={{fontSize:9,color:"var(--text-muted)",marginBottom:2}}>{sig.label}</div>
                      <div style={{height:3,background:"var(--surface3)",borderRadius:2,overflow:"hidden",marginBottom:2}}>
                        <div style={{height:"100%",width:`${Math.min((sig.val/sig.max)*100,100)}%`,background:vs.color,borderRadius:2}}/>
                      </div>
                      <div style={{fontSize:9,fontWeight:600,color:vs.color}}>{Math.round(sig.val)}</div>
                    </div>
                  ))}
                </div>
                {((np.score_helps||[]).length>0||(np.score_hurts||[]).length>0||(np.tips||[]).length>0) && (
                  <details style={{marginTop:4}}>
                    <summary style={{fontSize:11,fontWeight:600,color:"var(--teal)",cursor:"pointer",
                      userSelect:"none",listStyle:"none",display:"flex",alignItems:"center",gap:5,
                      padding:"5px 8px",borderRadius:6,background:"rgba(13,148,136,0.06)",
                      border:"1px solid rgba(13,148,136,0.15)"}}>
                      <span style={{fontSize:10}}>▸</span>
                      <span>Score breakdown — why this score?</span>
                    </summary>
                    <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:8}}>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                        {(np.score_helps||[]).length>0 && (
                          <div style={{background:"rgba(13,148,136,0.06)",borderRadius:7,padding:"8px 10px",
                            border:"1px solid rgba(13,148,136,0.15)"}}>
                            <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",
                              letterSpacing:"0.05em",color:"var(--teal)",marginBottom:5}}>What helps</div>
                            {(np.score_helps||[]).map((h,i) => (
                              <div key={i} style={{fontSize:10,color:"var(--text-secondary)",marginBottom:3,
                                display:"flex",alignItems:"flex-start",gap:4}}>
                                <span style={{color:"var(--teal)",flexShrink:0}}>✓</span><span>{h}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {(np.score_hurts||[]).length>0 && (
                          <div style={{background:"rgba(239,68,68,0.05)",borderRadius:7,padding:"8px 10px",
                            border:"1px solid rgba(239,68,68,0.15)"}}>
                            <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",
                              letterSpacing:"0.05em",color:"var(--red)",marginBottom:5}}>What hurts</div>
                            {(np.score_hurts||[]).map((h,i) => (
                              <div key={i} style={{fontSize:10,color:"var(--text-secondary)",marginBottom:3,
                                display:"flex",alignItems:"flex-start",gap:4}}>
                                <span style={{color:"var(--red)",flexShrink:0}}>✕</span><span>{h}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {(np.tips||[]).length>0 && (
                        <div style={{background:"rgba(245,158,11,0.06)",borderRadius:7,padding:"8px 10px",
                          border:"1px solid rgba(245,158,11,0.2)"}}>
                          <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",
                            letterSpacing:"0.05em",color:"var(--amber)",marginBottom:5}}>
                            💡 How to improve this score</div>
                          {(np.tips||[]).map((t,i) => (
                            <div key={i} style={{fontSize:10,color:"var(--text-secondary)",
                              marginBottom:3,lineHeight:1.5}}>→ {t}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CompatibilityTest({ api, roster, programCtx, autoRun, onAutoRan, rerunKey }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedCompatIdx, setExpandedCompatIdx] = useState(0);
  const [compatPosTab, setCompatPosTab] = useState("all");

  const players = roster?.players || [];
  const currentPlayers = players.filter(p => p.is_current);
  const newPlayers = players.filter(p => !p.is_current);
  const hasEnough = currentPlayers.length >= 1 && newPlayers.length >= 1;

  useEffect(() => {
    if (autoRun && hasEnough && !loading && !result) {
      runTest();
      if (onAutoRan) onAutoRan();
    }
  }, [autoRun, hasEnough]);

  // Rerun when Save Stats fires (rerunKey increments)
  useEffect(() => {
    if (rerunKey > 0 && hasEnough && !loading) {
      runTest();
    }
  }, [rerunKey]);
  const needs = programCtx?.burning_needs || [];

  // ── Multi-signal compatibility scorer ──────────────────────────────
  function scoreNewPlayer(np, currentPlayers, needs, posDepth) {
    const role = np.role || "guard";
    const samePos = currentPlayers.filter(p => p.role === role);
    const npStats = np._stats || {};
    const npTier  = np._result?.tier || np.value_tier || np.tier || "Role Player";
    const npPerf  = np._result?.perf_score || np.perf_score || 30;
    const npElig  = np.elig_years_remaining || np.elig_years || 1;

    const TIER_RANK = {"Elite":4,"Premium":3,"First Team":2,"Role Player":1};
    const npTierRank = TIER_RANK[npTier] || 1;

    // 1. Need match (30%) — does this player address burning needs?
    let needScore = 0;
    const maxNeeds = Math.max(needs.length, 1);
    needs.forEach(need => {
      if (need === "Scoring")           needScore += Math.min((npStats.pts_per_g||0)/25, 1);
      if (need === "Playmaking")        needScore += Math.min((npStats.ast_per_g||0)/10, 1);
      if (need === "Rebounding")        needScore += Math.min((npStats.reb_per_g||0)/12, 1);
      if (need === "Interior Defense")  needScore += Math.min((npStats.blk_per_g||0)/3,  1);
      if (need === "Perimeter Defense") needScore += Math.min((npStats.stl_per_g||0)/3,  1);
      if (need === "Depth / Insurance") needScore += 0.7;
    });
    const needFinal = (needScore / maxNeeds) * 30;

    // 2. Position upgrade (25%) — how much better vs current at that slot?
    let upgScore = 0;
    if (samePos.length === 0) {
      upgScore = 25; // fills empty position — always good
    } else {
      // Compare vs weakest current at position (replacement model)
      const weakestCurrent = samePos.reduce((worst, p) => {
        const r = TIER_RANK[p._result?.tier || p.tier || "Role Player"] || 1;
        return r < worst ? r : worst;
      }, 5);
      const tierDelta = npTierRank - weakestCurrent;
      upgScore = tierDelta >= 2 ? 25 : tierDelta === 1 ? 20 : tierDelta === 0 ? 13 : 5;
    }

    // 3. Role projection (20%) — starter, rotation, or bench?
    let roleScore = 0;
    const allSamePos = [...samePos].sort((a,b) =>
      (TIER_RANK[b._result?.tier||b.tier||"Role Player"]||1) - (TIER_RANK[a._result?.tier||a.tier||"Role Player"]||1));
    const projectedSlot = allSamePos.findIndex(p =>
      (TIER_RANK[p._result?.tier||p.tier||"Role Player"]||1) < npTierRank);
    const roleLabel = projectedSlot === -1
      ? (samePos.length === 0 ? "Starter" : "Depth")
      : projectedSlot === 0 ? "Starter" : "Rotation";
    roleScore = roleLabel === "Starter" ? 20 : roleLabel === "Rotation" ? 13 : 5;

    // 4. Stat complementarity (15%) — fills gaps in current lineup
    let compScore = 0;
    if (currentPlayers.length > 0) {
      const safeStat = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
      const avgPts = currentPlayers.reduce((s,p)=>s+(safeStat(p._stats?.pts_per_g)||8),0)/currentPlayers.length;
      const avgReb = currentPlayers.reduce((s,p)=>s+(safeStat(p._stats?.reb_per_g)||4),0)/currentPlayers.length;
      const avgAst = currentPlayers.reduce((s,p)=>s+(safeStat(p._stats?.ast_per_g)||2),0)/currentPlayers.length;
      const avgDef = currentPlayers.reduce((s,p)=>s+(safeStat(p._stats?.stl_per_g)||0.8)+(safeStat(p._stats?.blk_per_g)||0.4),0)/currentPlayers.length;
      // Reward filling weakest dimension
      const gaps = [
        { gap: Math.max(0, 14-avgPts),  fill: (npStats.pts_per_g||0)/25 },
        { gap: Math.max(0, 6-avgReb),   fill: (npStats.reb_per_g||0)/12 },
        { gap: Math.max(0, 3-avgAst),   fill: (npStats.ast_per_g||0)/10 },
        { gap: Math.max(0, 2-avgDef),   fill: ((npStats.stl_per_g||0)+(npStats.blk_per_g||0))/4 },
      ];
      const totalGap = gaps.reduce((s,g)=>s+g.gap,0) || 1;
      compScore = gaps.reduce((s,g)=>s+(g.gap/totalGap)*g.fill,0) * 15;
    } else {
      compScore = 10;
    }

    // 5. Eligibility value (10%) — years remaining × tier
    const eligVal = Math.min(parseInt(npElig)||1, 4);
    const eligScore = (eligVal >= 3 ? 1.0 : eligVal === 2 ? 0.7 : 0.4) * (npTierRank/4) * 10;

    // Stat deltas vs current at same position
    // Replacement model: find weakest at position, compare deltas against them
    const safeStat2 = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
    const weakestPlayer = samePos.length > 0
      ? samePos.reduce((worst, p) => {
          const ws = (safeStat2(worst._stats?.pts_per_g)||8) + (safeStat2(worst._stats?.reb_per_g)||4)*0.5 + (safeStat2(worst._stats?.ast_per_g)||2)*0.5;
          const ps = (safeStat2(p._stats?.pts_per_g)||8) + (safeStat2(p._stats?.reb_per_g)||4)*0.5 + (safeStat2(p._stats?.ast_per_g)||2)*0.5;
          return ps < ws ? p : worst;
        }, samePos[0])
      : null;
    const replacementBase = weakestPlayer?._stats || null;

    const avgCurrStats = samePos.length > 0 ? {
      pts: replacementBase ? (safeStat2(replacementBase.pts_per_g)||8) : samePos.reduce((s,p)=>s+(safeStat2(p._stats?.pts_per_g)||8),0)/samePos.length,
      reb: replacementBase ? (safeStat2(replacementBase.reb_per_g)||4) : samePos.reduce((s,p)=>s+(safeStat2(p._stats?.reb_per_g)||4),0)/samePos.length,
      ast: replacementBase ? (safeStat2(replacementBase.ast_per_g)||2) : samePos.reduce((s,p)=>s+(safeStat2(p._stats?.ast_per_g)||2),0)/samePos.length,
      stl: replacementBase ? (safeStat2(replacementBase.stl_per_g)||0.8) : samePos.reduce((s,p)=>s+(safeStat2(p._stats?.stl_per_g)||0.8),0)/samePos.length,
      blk: replacementBase ? (safeStat2(replacementBase.blk_per_g)||0.4) : samePos.reduce((s,p)=>s+(safeStat2(p._stats?.blk_per_g)||0.4),0)/samePos.length,
    } : null;

    const statDeltas = avgCurrStats ? [
      { label:"PTS/G", delta: (npStats.pts_per_g||0) - avgCurrStats.pts },
      { label:"REB/G", delta: (npStats.reb_per_g||0) - avgCurrStats.reb },
      { label:"AST/G", delta: (npStats.ast_per_g||0) - avgCurrStats.ast },
      { label:"STL/G", delta: (npStats.stl_per_g||0) - avgCurrStats.stl },
      { label:"BLK/G", delta: (npStats.blk_per_g||0) - avgCurrStats.blk },
    ].filter(d => Math.abs(d.delta) >= 0.3)
     .sort((a,b) => Math.abs(b.delta)-Math.abs(a.delta))
     .slice(0,3) : [];

    const needMatches = needs.filter(need => {
      if (need === "Scoring")           return (npStats.pts_per_g||0) >= 12;
      if (need === "Playmaking")        return (npStats.ast_per_g||0) >= 4;
      if (need === "Rebounding")        return (npStats.reb_per_g||0) >= 6;
      if (need === "Interior Defense")  return (npStats.blk_per_g||0) >= 1.2;
      if (need === "Perimeter Defense") return (npStats.stl_per_g||0) >= 1.2;
      if (need === "Depth / Insurance") return true;
      return false;
    });

    const totalScore = needFinal + upgScore + roleScore + compScore + eligScore;

    // posCount must be declared before verdict/isOvercrowded
    const posCount = (posDepth && posDepth[role]) || currentPlayers.filter(p => p.role === role).length;

    const isOvercrowded = posCount >= 3 && npTierRank <= 2;
    const isThin = (posDepth?.[role] || currentPlayers.filter(p=>p.role===role).length) < 2;
    const verdict = (isOvercrowded && totalScore < 30) ? "Roster Surplus"
                  : (totalScore >= 75) ? "Strong Upgrade"
                  : (totalScore >= 60) ? "Solid Fit"
                  : (totalScore >= 50 && totalScore < 75) ? "Scheme Fit"
                  : (totalScore >= 40) ? "Depth Add"
                  : (totalScore >= 30) ? "Rotation"
                  : (totalScore >= 15 && (isThin || roleLabel === "Depth")) ? "Positional Fallback"
                  : "Marginal";

    // Plain English explanation
    const whyText = (() => {
      if (verdict === "Strong Upgrade") {
        const topStat = statDeltas.find(d => d.delta > 0);
        const needStr = needMatches.length > 0 ? `, addresses ${needMatches[0]}` : "";
        return `Projects as ${roleLabel.toLowerCase()} — ${topStat ? `adds ${topStat.delta>0?"+":""}${topStat.delta.toFixed(1)} ${topStat.label}` : "strong stat profile"}${needStr}. ${parseInt(npElig)||1}yr eligibility window is a long-term asset.`;
      }
      if (verdict === "Solid Fit") {
        const topStat = statDeltas.find(d => d.delta > 0);
        return `Fits well as ${roleLabel.toLowerCase()} — ${topStat ? `improves ${topStat.label} by ${topStat.delta.toFixed(1)}` : "comparable stat profile"}. ${needMatches.length > 0 ? `Addresses ${needMatches.join(", ")}.` : "Does not directly target burning needs."}`;
      }
      if (verdict === "Scheme Fit") {
        return `Profile fits your roster construction well — projects as ${roleLabel.toLowerCase()}. Limited upside as a position-group upgrade but addresses a specific system need.`;
      }
      if (verdict === "Depth Add") {
        return `Solid depth addition — ${roleLabel.toLowerCase()} role that adds roster flexibility. ${(parseInt(npElig)||1) >= 2 ? `${npElig}yr eligibility provides coverage.` : "Short eligibility window — evaluate if the roster spot is worth it."}`;
      }
      if (verdict === "Rotation") {
        const topStat = statDeltas.find(d => d.delta > 0);
        return `Rotation-caliber ${role} — ${topStat ? `improves ${topStat.label} by ${topStat.delta.toFixed(1)}` : `comparable to current ${role}s`}. Fits as a reliable contributor without starter upside.`;
      }
      if (verdict === "Positional Fallback") {
        return `Roster insurance at ${role} — limited starter upside but provides positional coverage. Useful if depth is thin or injuries occur.`;
      }
      if (verdict === "Marginal") {
        return `Limited statistical impact — similar caliber to current ${role}s. ${needMatches.length === 0 ? "Does not address burning needs." : `Partially addresses ${needMatches[0]}.`}`;
      }
      // Roster Surplus
      if (posCount >= 3) return `You already have ${posCount} ${role}s on the roster — a further addition limits budget flexibility for thinner positions.`;
      const negStats = statDeltas.filter(d => d.delta < 0);
      return `Statistically weaker than current ${role}s${negStats.length > 0 ? ` (${negStats[0].label} ${negStats[0].delta.toFixed(1)})` : ""}. Consider targeting a different position.`;
    })();

    const verdictStyle = ({
      "Strong Upgrade":  {bg:"rgba(124,58,237,0.10)", border:"rgba(124,58,237,0.3)",  color:"#7c3aed"},
      "Solid Fit":       {bg:"rgba(99,102,241,0.10)", border:"rgba(99,102,241,0.3)",  color:"#818cf8"},
      "Depth Add":       {bg:"rgba(59,130,246,0.10)", border:"rgba(59,130,246,0.3)",  color:"#3b82f6"},
      "Rotation":        {bg:"rgba(13,148,136,0.10)", border:"rgba(13,148,136,0.3)",  color:"var(--teal)"},
      "Scheme Fit":      {bg:"rgba(245,158,11,0.10)", border:"rgba(245,158,11,0.3)",  color:"var(--amber)"},
      "Marginal":        {bg:"rgba(156,163,175,0.10)",border:"rgba(156,163,175,0.3)", color:"var(--text-muted)"},
      "Positional Fallback": {bg:"rgba(239,68,68,0.10)",  border:"rgba(239,68,68,0.3)",   color:"var(--red)"},
      "Roster Surplus":  {bg:"rgba(239,68,68,0.10)",  border:"rgba(239,68,68,0.3)",   color:"var(--red)"},
      "Positional Risk": {bg:"rgba(239,68,68,0.10)",  border:"rgba(239,68,68,0.3)",   color:"var(--red)"},
    })[verdict] || {bg:"rgba(154,160,176,0.10)",border:"rgba(154,160,176,0.3)",color:"var(--text-muted)"};

    return { totalScore, verdict, verdictStyle, roleLabel, statDeltas, needMatches,
             needFinal, upgScore, roleScore, compScore, eligScore, whyText };
  }
  // ──────────────────────────────────────────────────────────────────

  function getPosArch(s) {
    const g=s.filter(x=>x.role==="guard").length, f=s.filter(x=>x.role==="forward").length, c=s.filter(x=>x.role==="center").length;
    if(g>=3)return"Guard-heavy"; if(f>=3)return"Wing-heavy"; if(c>=2)return"Big-heavy"; return"Balanced";
  }

  function getTierMix(s) {
    const tiers = s.map(x => x._result?.tier || x.tier || "Role Player");
    const e=tiers.filter(t=>t==="Elite").length, p=tiers.filter(t=>t==="Premium").length, r=tiers.filter(t=>t==="Role Player").length;
    if(e>=2)return"Elite-led"; if(p>=2)return"Premium-core"; if(r>=3)return"Role-Player-heavy"; return"Mixed";
  }

  function buildStarters(pool, n=5) {
    return [...pool].sort((a,b) => {
      const aScore = (a._result?.perf_score || a._stats?.pts_per_g || 0);
      const bScore = (b._result?.perf_score || b._stats?.pts_per_g || 0);
      return bScore - aScore;
    }).slice(0, n);
  }

  function computeADLocal(starters) {
    if (!starters.length) return 50;
    const w = [0.35,0.25,0.2,0.12,0.08];
    const pts = starters.map(p => Math.min(parseFloat(p._stats?.pts_per_g || p.pts_per_g || 8) / 25, 1));
    const def = starters.map(p => Math.min((parseFloat(p._stats?.stl_per_g || p.stl_per_g || 0.8) + parseFloat(p._stats?.blk_per_g || p.blk_per_g || 0.5)) / 4, 1));
    const sortedPts = [...pts].sort((a,b)=>b-a);
    const sortedDef = [...def].sort((a,b)=>b-a);
    let off=0, dfn=0;
    starters.forEach((_,i) => { off+=(sortedPts[i]||0.3)*(w[i]||0.1); dfn+=(sortedDef[i]||0.2)*(w[i]||0.1); });
    const base = off + dfn || 1;
    return Math.round((off/base)*100);
  }

  async function runTest() {
    setLoading(true); setResult(null); setError(null);
    try {
      const res = await api("/user/compatibility_score", {
        method: "POST",
        body: JSON.stringify({
          new_players:     newPlayers,
          current_players: currentPlayers,
          program_ctx:     programCtx || {}
        })
      });
      setResult(res);
      setExpandedCompatIdx(0);
      setCompatPosTab("all");
    } catch(e) {
      setError("API warming up — please retry in a moment.");
    } finally {
      setLoading(false);
    }
  }

  if (!hasEnough) return (
    <div className="card" style={{overflow:"hidden"}}>
      <div style={{padding:"12px 16px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span className="card-title">Roster Compatibility</span>
          <span style={{fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:99,background:"rgba(99,102,241,0.12)",color:"#818cf8",border:"1px solid rgba(99,102,241,0.3)",letterSpacing:"0.05em"}}>DELTA</span>
        </div>
      </div>
      <div style={{padding:"20px 16px",display:"flex",flexDirection:"column",alignItems:"center",gap:10,textAlign:"center"}}>
        <div style={{fontSize:28,opacity:0.4}}>⚡</div>
        <div style={{fontSize:13,fontWeight:600,color:"var(--text-primary)"}}>No new profiles yet</div>
        <div style={{fontSize:12,color:"var(--text-muted)",maxWidth:220,lineHeight:1.6}}>
          Add profiles from the Scout Board to run a compatibility delta against your current roster.
        </div>
      </div>
    </div>
  );

  return (
    <div className="card" style={{overflow:"hidden"}}>
      <div style={{padding:"12px 16px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span className="card-title">Roster Compatibility</span>
          <span style={{fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:99,background:"rgba(99,102,241,0.12)",color:"#818cf8",border:"1px solid rgba(99,102,241,0.3)",letterSpacing:"0.05em"}}>DELTA</span>
        </div>
        <span style={{fontSize:11,color:"var(--text-muted)"}}>{newPlayers.length} new · {currentPlayers.length} current</span>
      </div>

      <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:12}}>
        {!result && !loading && (
          <>
            <div style={{fontSize:12,color:"var(--text-secondary)",lineHeight:1.6}}>
              Scores each new profile against your current roster across <strong>5 signals</strong> — need match, position upgrade, role fit, stat complementarity, and eligibility value.
            </div>
            <button onClick={runTest}
              style={{padding:"9px 16px",borderRadius:8,border:"1px solid var(--teal)",background:"rgba(13,148,136,0.08)",
                color:"var(--teal)",fontSize:12,fontWeight:600,cursor:"pointer",transition:"all 0.15s",
                display:"flex",alignItems:"center",justifyContent:"center",gap:8}}
              onMouseOver={e=>{e.currentTarget.style.background="rgba(13,148,136,0.15)";}}
              onMouseOut={e=>{e.currentTarget.style.background="rgba(13,148,136,0.08)";}}>
              ⚡ Run Compatibility Test
            </button>
          </>
        )}

        {loading && (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:"16px 0",color:"var(--text-muted)",fontSize:12}}>
            <div className="spinner" style={{width:16,height:16}}/>
            Scoring profiles…
          </div>
        )}

        {error && <div style={{fontSize:12,color:"var(--red)",padding:"8px 12px",background:"rgba(239,68,68,0.07)",borderRadius:8,border:"1px solid rgba(239,68,68,0.2)"}}>{error}</div>}

        {result && (
          <CompatResultView
            result={result}
            compatPosTab={compatPosTab}
            setCompatPosTab={setCompatPosTab}
            expandedCompatIdx={expandedCompatIdx}
            setExpandedCompatIdx={setExpandedCompatIdx}
          />
        )}

        {result && (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {result.upgraded_narrative?.excels_at && (
              <div style={{background:"var(--surface2)",borderRadius:8,padding:"10px 14px",border:"1px solid var(--border)"}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",color:"var(--text-muted)",marginBottom:6}}>Upgraded lineup narrative</div>
                <div style={{fontSize:11,color:"var(--text-secondary)",marginBottom:result.upgraded_narrative.summary?6:0}}>✅ {result.upgraded_narrative.excels_at}</div>
                {result.upgraded_narrative.summary && <div style={{fontSize:11,color:"var(--text-secondary)",lineHeight:1.6,marginTop:4}}>{result.upgraded_narrative.summary}</div>}
              </div>
            )}
            {result.upgraded_narrative?.exposure && (
              <div style={{background:"rgba(245,158,11,0.06)",borderRadius:8,padding:"10px 12px",border:"1px solid rgba(245,158,11,0.2)"}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",color:"var(--amber)",marginBottom:4}}>⚠ Watch out for</div>
                <div style={{fontSize:11,color:"var(--text-secondary)"}}>{result.upgraded_narrative.exposure}</div>
              </div>
            )}
            {result.star_dependency?.detected && (
              <div style={{background:"rgba(245,158,11,0.06)",borderRadius:8,padding:"10px 12px",border:"1px solid rgba(245,158,11,0.3)"}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",color:"var(--amber)",marginBottom:4}}>⭐ Star Dependency Detected</div>
                <div style={{fontSize:11,color:"var(--text-secondary)"}}>{result.star_dependency.message}</div>
              </div>
            )}
            <button onClick={() => setResult(null)}
              style={{padding:"6px 12px",borderRadius:6,border:"1px solid var(--border)",background:"none",
                color:"var(--text-muted)",fontSize:11,cursor:"pointer",alignSelf:"flex-start"}}>
              ↺ Rerun test
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function RosterBuilder({ api, budgetM, onBudgetChange, roster, setRoster, onImportProfile, programCtx, autoRunCompat, onCompatRan }) {
  const [pendingStatEdits, setPendingStatEdits] = useState({});
  const [statSavedFor, setStatSavedFor] = useState(null);
  const [compatRerunKey, setCompatRerunKey] = useState(0);
  const [watchlist, setWatchlist] = useState([]);
  const [watchNotes, setWatchNotes] = useState({});
  const [watchNotesOpen, setWatchNotesOpen] = useState({});
  const [watchNoteInput, setWatchNoteInput] = useState({});

  useEffect(() => {
    api("/user/watchlist").then(d => setWatchlist(d.watchlist || [])).catch(() => {});
  }, []);

  async function removeFromWatchlist(key) {
    await api(`/user/watchlist/${encodeURIComponent(key)}`, { method: "DELETE" }).catch(() => {});
    setWatchlist(prev => prev.filter(w => w.player_key !== key));
  }

  async function toggleWatchNotes(key) {
    const open = !watchNotesOpen[key];
    setWatchNotesOpen(prev => ({...prev, [key]: open}));
    if (open && !watchNotes[key]) {
      api(`/user/notes/${encodeURIComponent(key)}`)
        .then(d => setWatchNotes(prev => ({...prev, [key]: d.notes || []})))
        .catch(() => {});
    }
  }

  async function addWatchNote(key) {
    const txt = (watchNoteInput[key] || "").trim();
    if (!txt) return;
    api(`/user/notes/${encodeURIComponent(key)}`, {
      method: "POST", body: JSON.stringify({ note_text: txt })
    }).then(n => {
      if (n) {
        setWatchNotes(prev => ({...prev, [key]: [n, ...(prev[key]||[])]}));
        setWatchNoteInput(prev => ({...prev, [key]: ""}));
      }
    }).catch(() => {});
  }
  const [squadName] = useState("2026-27 Roster");
  const [loading, setLoading] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [roleFilter, setRoleFilter] = useState("All");
  const [expandedIdx, setExpandedIdx] = useState(null);

  const players = roster?.players || [];
  const totalSpend = players.reduce((s, p) => s + parseFloat(p.nil_value_m || 0), 0);
  const remaining = (parseFloat(budgetM) || 0) - totalSpend;

  // Roster state is shared from App — no local load needed

  async function saveSquad(newSquad) {
    setRoster(newSquad);
    await api("/user/squads", {
      method: "POST",
      body: JSON.stringify({ squad_name: squadName, squad_data: newSquad, is_default: true }),
    }).catch(() => {});
  }

  function addPlayer(p) {
    const updated = { ...roster, players: [...players, { player_name: p.player_name, role: p.role, school: p.school, nil_value_m: (p.predicted_nil_value/1000000).toFixed(2), elig_years: p.elig_years_remaining }] };
    saveSquad(updated);
    setSearchQ(""); setSearchResults([]);
  }

  function removePlayer(idx) {
    const updated = { ...roster, players: players.filter((_,i) => i !== idx) };
    saveSquad(updated);
  }

  function updateNil(idx, val) {
    const updated = { ...roster, players: players.map((p,i) => i===idx ? {...p, nil_value_m: val} : p) };
    setRoster(updated);
  }

  async function saveNilEdit(idx) {
    await api("/user/squads", {
      method: "POST",
      body: JSON.stringify({ squad_name: squadName, squad_data: roster, is_default: true }),
    }).catch(() => {});
  }

  useEffect(() => {
    if (!searchQ.trim() || searchQ.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      setSearching(true);
      api(`/search?q=${encodeURIComponent(searchQ)}&limit=12`)
        .then(d => setSearchResults(d.players || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  const roles = ["All","guard","forward","center"];
  const filtered = roleFilter === "All" ? players : players.filter(p => p.role === roleFilter);

  const remainClass = remaining < 0 ? "remaining-over" : remaining < 2 ? "remaining-warn" : "remaining-ok";

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-title">Roster Builder</div>
        <div className="page-sub">Carefully build your roster, plan the deals — own your budget.</div>
      </div>
      <div className="grid-2" style={{gap:16}}>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <div className="card">
          <div className="card-header">
            <div className="card-title">Current roster<Tooltip body="Adjust the slider to set your offer per player. Donut updates in real time." /></div>
            <div className="card-sub">{players.length} players · ${totalSpend.toFixed(2)}M spent</div>
          </div>
          <div className="filter-row">
            {roles.map(r => (
              <button key={r} className={`filter-pill ${roleFilter===r?"active":""}`} onClick={() => setRoleFilter(r)}>
                {r.charAt(0).toUpperCase()+r.slice(1)}
              </button>
            ))}
          </div>

          <div className="scroll-list" style={{marginTop:8}}>
            {loading ? <div className="loading"><div className="spinner"/></div>
            : filtered.length === 0 ? <div className="empty">No players yet — add players from search</div>
            : filtered.map((p, i) => {
              const realIdx = players.indexOf(p);
              const [bg, fg] = avColor(i);
              const isCurrent = !!p.is_current;
              return (
                <div key={i}>
                <div className="roster-row" style={{flexWrap:"wrap",
                  borderLeft: isCurrent ? "3px solid var(--border)" : "3px solid var(--teal)",
                  paddingLeft:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,cursor:"pointer",flex:"0 0 auto"}} onClick={() => setExpandedIdx(expandedIdx===realIdx?null:realIdx)}>
                    <div className="player-av" style={{
                      background: isCurrent ? "var(--surface3)" : bg,
                      color: isCurrent ? "var(--text-muted)" : fg,
                      opacity: isCurrent ? 0.7 : 1}}>
                      {initials(p.player_name)}
                    </div>
                    <div className="player-info">
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <div className="player-name" style={{color: isCurrent ? "var(--text-secondary)" : "var(--text-primary)"}}>
                          {p.player_name}
                        </div>
                        {isCurrent ? (
                          <span style={{background:"var(--surface3)",border:"1px solid var(--border)",
                            borderRadius:99,padding:"1px 7px",fontSize:9,fontWeight:700,
                            color:"var(--text-muted)",letterSpacing:"0.3px"}}>CURRENT</span>
                        ) : (
                          <span style={{background:"rgba(13,148,136,0.08)",border:"1px solid rgba(13,148,136,0.25)",
                            borderRadius:99,padding:"1px 7px",fontSize:9,fontWeight:700,
                            color:"var(--teal)",letterSpacing:"0.3px"}}>NEW</span>
                        )}
                        {p._result?.tier && (() => { const tc=getTierConfig(p._result.tier); return (
                          <span style={{background:tc.bg,border:`1px solid ${tc.border}`,borderRadius:99,padding:"1px 7px",fontSize:9,fontWeight:700,color:tc.color}}>{tc.label}</span>
                        ); })()}
                        <span style={{fontSize:10,color:"var(--text-muted)",fontWeight:400}}>{expandedIdx===realIdx?"▲":"▼"}</span>
                      </div>
                      <div className="player-meta">{p.role} · {p.school} · {p.elig_years}yr elig</div>
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2,minWidth:140,marginLeft:"auto"}}>
                    <div className="offer-wrap">
                      <span className="offer-prefix">$</span>
                      <input
                        className="offer-input"
                        type="number" step="0.01"
                        value={p.nil_value_m}
                        onChange={e => updateNil(realIdx, e.target.value)}
                        onBlur={() => saveNilEdit(realIdx)}
                      />
                      <span style={{fontSize:10,color:"var(--teal)"}}>M</span>
                    </div>
                    {(() => {
                      const base = parseFloat(p.nil_value_m) || 0;
                      const step = base < 0.5 ? 0.01 : base < 1 ? 0.025 : 0.05;
                      const min = Math.max(0, base * 0.5);
                      const max = Math.min(4, base * 1.5);
                      const pct = max > min ? ((base - min) / (max - min)) * 100 : 50;
                      const color = "var(--accent)";
                      return (
                        <div style={{width:"100%"}}>
                          <div style={{display:"flex",alignItems:"center",gap:4}}>
                            <button style={{width:18,height:18,borderRadius:4,border:"1px solid var(--border)",background:"var(--surface2)",color:"var(--text-primary)",fontSize:13,lineHeight:1,cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}
                              onClick={() => { const v = Math.max(min, base - step); updateNil(realIdx, v.toFixed(2)); setTimeout(()=>saveNilEdit(realIdx),100); }}>−</button>
                            <input type="range"
                              min={min.toFixed(2)} max={max.toFixed(2)} step={step}
                              value={base}
                              onChange={e => updateNil(realIdx, parseFloat(e.target.value).toFixed(2))}
                              onMouseUp={() => saveNilEdit(realIdx)}
                              style={{flex:1,accentColor:color,height:12}}
                            />
                            <button style={{width:18,height:18,borderRadius:4,border:"1px solid var(--border)",background:"var(--surface2)",color:"var(--text-primary)",fontSize:13,lineHeight:1,cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}
                              onClick={() => { const v = Math.min(max, base + step); updateNil(realIdx, v.toFixed(2)); setTimeout(()=>saveNilEdit(realIdx),100); }}>+</button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <button onClick={() => removePlayer(realIdx)} style={{background:"none",border:"none",color:"var(--red)",fontSize:18,cursor:"pointer",padding:"2px 6px",lineHeight:1,flexShrink:0}} title="Remove">×</button>
                </div>
                {expandedIdx===realIdx && p._stats && (
                  <div style={{width:"100%",padding:"10px 16px 14px",background:"var(--surface2)",borderTop:"1px solid var(--border)"}}>
                    {p._result && (
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                        {(() => { const tc=getTierConfig(p._result.tier); return (
                          <span style={{background:tc.bg,border:`1px solid ${tc.border}`,borderRadius:99,padding:"3px 10px",fontSize:11,fontWeight:700,color:tc.color}}>{tc.icon} {tc.label}</span>
                        ); })()}
                        <span style={{fontFamily:"var(--font-display)",fontSize:15,fontWeight:700,color:"var(--teal)"}}>{p._result.range_fmt}</span>
                        <span style={{fontSize:11,color:"var(--text-muted)"}}>Perf: {p._result.perf_score?.toFixed(1)} · Adj: {p._result.adjusted_score?.toFixed(1)}</span>
                      </div>
                    )}
                    {p.is_current && (
                      <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
                        <span style={{color:"var(--teal)",fontWeight:600}}>✎ Edit stats</span>
                        <span>— updates Compatibility Test scoring</span>
                      </div>
                    )}
                    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"6px 12px"}}>
                      {[
                        ["PTS/G","pts_per_g",p._stats.pts_per_g],
                        ["AST/G","ast_per_g",p._stats.ast_per_g],
                        ["REB/G","reb_per_g",p._stats.reb_per_g],
                        ["STL/G","stl_per_g",p._stats.stl_per_g],
                        ["BLK/G","blk_per_g",p._stats.blk_per_g],
                        ["FG%","fg_pct",p._stats.fg_pct?(parseFloat(p._stats.fg_pct)*100).toFixed(1):null],
                        ["3P%","fg3_pct",p._stats.fg3_pct?(parseFloat(p._stats.fg3_pct)*100).toFixed(1):null],
                        ["FT%","ft_pct",p._stats.ft_pct?(parseFloat(p._stats.ft_pct)*100).toFixed(1):null],
                        ["MP/G","mp_per_g",p._stats.mp_per_g],
                      ].map(([label, key, val]) => val!=null&&val!==""&&String(val)!=="0" ? (
                        <div key={label} style={{background:"var(--surface)",borderRadius:6,padding:"5px 8px"}}>
                          <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:1}}>{label}</div>
                          {p.is_current ? (
                            <input
                              type="number"
                              step="0.1"
                              defaultValue={parseFloat(val)||0}
                              onBlur={e => {
                                const newVal = parseFloat(e.target.value)||0;
                                const pct = ["fg_pct","fg3_pct","ft_pct"].includes(key);
                                const stored = pct ? newVal/100 : newVal;
                                setPendingStatEdits(prev => ({
                                  ...prev,
                                  [p.player_name]: {...(prev[p.player_name]||{}), [key]: stored}
                                }));
                              }}
                              style={{fontSize:12,fontWeight:600,width:"100%",background:"none",border:"none",
                                borderBottom:"1px dashed var(--teal)",outline:"none",color:"var(--text-primary)",
                                padding:"1px 0",fontFamily:"var(--font-body)"}}
                            />
                          ) : (
                            <div style={{fontSize:12,fontWeight:600}}>{["fg_pct","fg3_pct","ft_pct"].includes(key)?val+"%":val}</div>
                          )}
                        </div>
                      ) : null)}
                      {/* Save Stats button — appears when pending edits exist for this player */}
                      {p.is_current && pendingStatEdits[p.player_name] && Object.keys(pendingStatEdits[p.player_name]).length > 0 && (
                        <div style={{gridColumn:"1/-1",marginTop:4}}>
                          <button
                            onClick={() => {
                              const edits = pendingStatEdits[p.player_name] || {};
                              setRoster(r => {
                                const updated = (r.players || []).map(pp => pp.player_name===p.player_name
                                  ? {...pp, _stats:{...pp._stats, ...edits}}
                                  : pp
                                );
                                const nr = {...r, players: updated};
                                api("/user/squads",{method:"POST",body:JSON.stringify({squad_name:"2026-27 Roster",squad_data:nr,is_default:true})})
                                  .catch(()=>{});
                                return nr;
                              });
                              setPendingStatEdits(prev => {
                                const next = {...prev};
                                delete next[p.player_name];
                                return next;
                              });
                              setStatSavedFor(p.player_name);
                              setCompatRerunKey(k => k + 1);
                              setTimeout(() => setStatSavedFor(null), 2500);
                            }}
                            style={{fontSize:11,fontWeight:700,padding:"5px 14px",borderRadius:6,cursor:"pointer",
                              background:"var(--teal)",border:"none",color:"#fff",letterSpacing:"0.02em"}}>
                            Save Stats
                          </button>
                        </div>
                      )}
                      {p.is_current && statSavedFor===p.player_name && (
                        <div style={{gridColumn:"1/-1",marginTop:2}}>
                          <span style={{fontSize:11,color:"var(--teal)",fontWeight:600}}>✓ Stats saved — updating Compatibility scores...</span>
                        </div>
                      )}
                      {/* Non-editable: Conf, Elig, Trend */}
                      {[["Conf",p._stats.conf_tier],["Elig",p._stats.elig_years+"yr"],["Trend",p._stats.trend]].map(([l,v])=>v&&v!=="undefinedyr"?(
                        <div key={l} style={{background:"var(--surface)",borderRadius:6,padding:"5px 8px"}}>
                          <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:1}}>{l}</div>
                          <div style={{fontSize:12,fontWeight:600}}>{v}</div>
                        </div>
                      ):null)}
                    </div>
                    <div style={{marginTop:12,borderTop:"1px solid var(--border)",paddingTop:12}}>
                      <div style={{fontSize:11,fontWeight:600,color:"var(--text-muted)",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.4px"}}>Scouting notes</div>
                      <RosterProfileNotes api={api} playerKey={p.player_name}/>
                    </div>
                  </div>
                )}
                </div>
              );
            })}
          </div>
          {players.length > 0 && (
            <div style={{padding:"10px 20px",borderTop:"1px solid var(--border)",background:"var(--surface2)",display:"flex",gap:16,flexWrap:"wrap",borderBottomLeftRadius:"var(--radius-lg)",borderBottomRightRadius:"var(--radius-lg)"}}>
              {["guard","forward","center"].map(r => {
                const cnt = players.filter(p=>p.role===r).length;
                if (!cnt) return null;
                return <span key={r} style={{fontSize:12,color:"var(--text-secondary)",textTransform:"capitalize"}}>{r}s: <strong style={{color:"var(--text-primary)"}}>{cnt}</strong></span>;
              })}
              <span style={{fontSize:12,color:"var(--text-secondary)",marginLeft:"auto"}}>Avg: <strong style={{color:"var(--text-primary)"}}>{players.length ? fmtM(totalSpend/players.length) : "—"}</strong></span>
            </div>
          )}
        </div>

        <div className="card">
            <div className="card-header"><div className="card-title">Roster Budget breakdown<Tooltip body="Committed spend by position. Red ring = over budget." /></div></div>
            <div style={{padding:"20px"}}>
              {(() => {
                const budget = parseFloat(budgetM||0);
                const roleColors = { guard:"#14b8a6", forward:"#6366f1", center:"#f59e0b" };
                const roleSpend = ["guard","forward","center"].map(r => ({
                  role: r, spend: players.filter(p=>p.role===r).reduce((s,p)=>s+parseFloat(p.nil_value_m||0),0)
                })).filter(r=>r.spend>0);
                const rem = Math.max(0, budget - totalSpend);
                const segments = [...roleSpend.map(r=>({label:r.role,val:r.spend,color:roleColors[r.role]})),
                                  ...(rem>0?[{label:"remaining",val:rem,color:"#e2e8f0"}]:[])];
                const total = segments.reduce((s,x)=>s+x.val,0)||1;
                const cx=130,cy=130,r=110,inner=68;
                let angle=-Math.PI/2;
                const slices = segments.map(s=>{
                  const sweep=(s.val/total)*2*Math.PI;
                  const x1=cx+r*Math.cos(angle),y1=cy+r*Math.sin(angle);
                  angle+=sweep;
                  const x2=cx+r*Math.cos(angle),y2=cy+r*Math.sin(angle);
                  const xi1=cx+inner*Math.cos(angle-sweep),yi1=cy+inner*Math.sin(angle-sweep);
                  const xi2=cx+inner*Math.cos(angle),yi2=cy+inner*Math.sin(angle);
                  const large=sweep>Math.PI?1:0;
                  return {d:`M${x1},${y1} A${r},${r},0,${large},1,${x2},${y2} L${xi2},${yi2} A${inner},${inner},0,${large},0,${xi1},${yi1} Z`,color:s.color,label:s.label,val:s.val};
                });
                const pctUsed=budget>0?Math.round((totalSpend/budget)*100):0;
                const overBudget=remaining<0;
                return (
                  <div style={{display:"flex",alignItems:"center",gap:20,flexWrap:"wrap"}}>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10}}>
                      <svg width={260} height={260} viewBox="0 0 260 260" style={{flexShrink:0}}>
                        {slices.map((s,i)=><path key={i} d={s.d} fill={s.color}/>)}
                        <text x={cx} y={cy-10} textAnchor="middle" style={{fontFamily:"var(--font-display)",fontSize:19,fontWeight:700,fill:"var(--text-primary)"}}>${totalSpend.toFixed(1)}M</text>
                        <text x={cx} y={cy+8} textAnchor="middle" style={{fontSize:10,fill:"var(--text-muted)"}}>of ${budget.toFixed(1)}M</text>
                        <text x={cx} y={cy+24} textAnchor="middle" style={{fontSize:11,fontWeight:600,fill:overBudget?"#ef4444":pctUsed>85?"#f59e0b":"#14b8a6"}}>{overBudget?"Over budget":`${pctUsed}% used`}</text>
                      </svg>

                    </div>
                    <div style={{flex:1,minWidth:160,display:"flex",gap:20}}>
                      <div style={{flex:1}}>
                        {slices.map((s,i)=>(
                          <div key={i} style={{display:"flex",alignItems:"center",gap:7,marginBottom:8}}>
                            <div style={{width:9,height:9,borderRadius:2,background:s.color,flexShrink:0}}/>
                            <div style={{flex:1}}>
                              <div style={{fontSize:14,fontWeight:600,textTransform:"capitalize",color:"var(--text-primary)"}}>{s.label}</div>
                              <div style={{fontSize:13,color:"var(--text-muted)"}}>{fmtM(s.val)} · {Math.round((s.val/total)*100)}%</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",gap:10,minWidth:90}}>
                        {[
                          ["Committed", fmtM(totalSpend), "var(--text-primary)"],
                          ["Remaining", (overBudget?"Over ":"")+fmtM(Math.abs(remaining)), overBudget?"#ef4444":remaining<2?"#f59e0b":"#14b8a6"],
                          ["Total", `$${parseFloat(budgetM||0).toFixed(1)}M`, "var(--text-primary)"],
                        ].map(([label, val, color]) => (
                          <div key={label}>
                            <div style={{fontSize:11,fontWeight:600,color:"#6b7280",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:2}}>{label}</div>
                            <div style={{fontFamily:"var(--font-display)",fontSize:15,fontWeight:700,color}}>{val}</div>
                          </div>
                        ))}
                        <div style={{paddingTop:8,borderTop:"1px solid var(--border)",display:"flex",alignItems:"baseline",gap:3}}>
                          <span style={{fontSize:10,color:"var(--text-muted)"}}>Budget</span>
                          <input type="number" step="0.5" value={budgetM} onChange={e=>onBudgetChange(e.target.value)}
                            style={{fontFamily:"var(--font-display)",fontSize:13,fontWeight:700,border:"none",background:"none",outline:"none",width:42,color:"var(--text-primary)"}}/>
                          <span style={{fontSize:10,color:"var(--text-muted)"}}>M</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
        </div>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <CompatibilityTest api={api} roster={roster} programCtx={programCtx} autoRun={autoRunCompat} onAutoRan={onCompatRan} rerunKey={compatRerunKey} />
          <RunTheSet api={api} roster={roster} />
          <div className="card" style={{display:"none"}}>
            <div className="card-header">
              <div className="card-title">Watchlisted players</div>
              <div className="card-sub">{watchlist.length} players tracked</div>
            </div>
            <div style={{maxHeight:384,overflowY:"auto"}}>
              {watchlist.length === 0
                ? <div className="empty" style={{padding:"20px"}}>No players watchlisted — add from Scout Board</div>
                : watchlist.map(w => {
                    const _m = (() => { try { return JSON.parse(w.status||"{}"); } catch(e) { return {}; } })();
                    return (
                      <div key={w.player_key} style={{borderBottom:"1px solid var(--border)"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,padding:"9px 16px"}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.player_key}</div>
                            <div style={{fontSize:11,color:"var(--text-muted)",marginTop:1,textTransform:"capitalize"}}>{_m.role||"—"} · {_m.school||"—"}</div>
                          </div>
                          <button title="Add note" onClick={() => setWatchNotesOpen(p => ({...p, [w.player_key]: !p[w.player_key]}))}
                            style={{background:watchNotesOpen[w.player_key]?"var(--accent-dim)":"none",border:"1px solid var(--border)",borderRadius:6,color:watchNotesOpen[w.player_key]?"var(--teal)":"var(--text-muted)",fontSize:11,cursor:"pointer",padding:"3px 7px",flexShrink:0,lineHeight:1}}>📝</button>
                          <button className="btn btn-primary btn-sm" style={{background:"linear-gradient(135deg,#7c3aed,#4f46e5)",border:"none",fontSize:10,padding:"3px 8px",flexShrink:0}} onClick={() => {
                            if (onImportProfile) onImportProfile({ player_name: w.player_key, role: _m.role||"guard", school: _m.school||"—", predicted_nil_value: parseFloat(_m.nil||"1.00")*1000000, elig_years_remaining: _m.elig||2, conf_tier_label: _m.conf||"Rest D-I", pts_per_g: _m.pts, reb_per_g: _m.reb, ast_per_g: _m.ast, fg_pct: _m.fg, fg3_pct: _m.fg3, ft_pct: _m.ft, stl_per_g: _m.stl, blk_per_g: _m.blk, mp_per_g: _m.mp, tov_per_g: _m.tov, orb_per_g: _m.orb, g: _m.g, _ts: Date.now() });
                          }}>↗ Evaluate</button>
                          <button onClick={() => removeFromWatchlist(w.player_key)} style={{background:"none",border:"none",color:"var(--red)",fontSize:18,cursor:"pointer",padding:"2px 6px",lineHeight:1,flexShrink:0}} title="Remove">×</button>
                        </div>
                        {watchNotesOpen[w.player_key] && (
                          <div style={{padding:"10px 16px 14px",background:"var(--surface2)",borderTop:"1px solid var(--border)"}}>
                            <RosterProfileNotes api={api} playerKey={w.player_key}/>
                          </div>
                        )}
                      </div>
                    );
                  })
              }
            </div>
            {watchlist.length > 0 && (
              <div style={{padding:"10px 20px",borderTop:"1px solid var(--border)",background:"var(--surface2)",display:"flex",gap:16,flexWrap:"wrap",borderBottomLeftRadius:"var(--radius-lg)",borderBottomRightRadius:"var(--radius-lg)"}}>
                {["guard","forward","center"].map(r => {
                  const cnt = watchlist.filter(w => { try { return JSON.parse(w.status||"{}").role===r; } catch(e) { return false; } }).length;
                  if (!cnt) return null;
                  return <span key={r} style={{fontSize:12,color:"var(--text-secondary)",textTransform:"capitalize"}}>{r}s: <strong style={{color:"var(--text-primary)"}}>{cnt}</strong></span>;
                })}
                <span style={{fontSize:12,color:"var(--text-secondary)",marginLeft:"auto"}}>{watchlist.length} tracked</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{
        marginTop:32, padding:"10px 0",
        borderTop:"1px solid var(--border)",
        fontSize:11, color:"var(--text-muted)",
        lineHeight:1.6, textAlign:"center",
      }}>
        NIL valuations are AI-generated estimates based on statistical modeling.
        Actual market values may differ. Not financial or legal advice. · Rah-AI: Vantage by Epicentra LLC
      </div>
    </div>
  );
}

// ── Scouting ──────────────────────────────────────────────────────────────────
function ScoutingScreen({ api }) {
  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("All");
  const [division, setDivision] = useState("All");
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState("");

  useEffect(() => {
    const params = new URLSearchParams({ limit:40, page_size:40 });
    if (role !== "All") params.set("role", role);
    if (division !== "All") params.set("division", division);
    api(`/players?${params}`).then(d => {
      const ps = d.players || [];
      setPlayers(ps);
    }).finally(() => setLoading(false));
  }, [role, division]);

  useEffect(() => {
    if (!query.trim()) return;
    const t = setTimeout(() => {
      api(`/search?q=${encodeURIComponent(query)}&limit=40`)
        .then(d => { const ps = d.players||[]; setPlayers(ps); });
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!selected) return;
    api(`/user/notes/${encodeURIComponent(selected.player_name_key || selected.player_name)}`)
      .then(d => setNotes(d.notes || [])).catch(() => setNotes([]));
  }, [selected]);

  async function addNote() {
    if (!noteInput.trim() || !selected) return;
    const key = selected.player_name_key || selected.player_name;
    const n = await api(`/user/notes/${encodeURIComponent(key)}`, {
      method:"POST", body: JSON.stringify({ note_text: noteInput.trim() })
    }).catch(() => null);
    if (n) { setNotes(prev => [n, ...prev]); setNoteInput(""); }
  }

  const vc = selected ? verdictColor(selected.verdict || "") : "teal";

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-title">Player scouting</div>
        <div className="page-sub">Search across 13,115 players · D-I, D-II, D-III · 2025 season</div>
      </div>
      <div className="kpi-row">
        {[
          ["Players indexed","13,115","D-I · D-II · D-III","teal"],
          ["D-I elite","247","conf tier 4–5","up"],
          ["Strong offer","1,120","portal verdicts","up"],
          ["Confirmed NIL","178","verified labels","teal"],
        ].map(([l,v,d,cls]) => (
          <div className="kpi-card" key={l}>
            <div className="kpi-label">{l}</div>
            <div className="kpi-value">{v}</div>
            <div className={`kpi-delta ${cls}`}>{d}</div>
          </div>
        ))}
      </div>
      <div className="grid-2" style={{gap:16}}>
        <div className="card">
          <div className="card-header">
            <div className="card-title">Player index</div>
            <div className="card-sub">{players.length} results</div>
          </div>
          <div className="search-bar">
            <Icon name="search" size={14}/>
            <input placeholder="Search by name…" value={query} onChange={e => setQuery(e.target.value)}/>
          </div>
          <div className="filter-row">
            {["All","guard","forward","center"].map(r => (
              <button key={r} className={`filter-pill ${role===r?"active":""}`} onClick={() => setRole(r)}>
                {r.charAt(0).toUpperCase()+r.slice(1)}
              </button>
            ))}
          </div>
          <div className="filter-row" style={{paddingTop:6}}>
            {["All","D-I","D-II","D-III"].map(d => (
              <button key={d} className={`filter-pill ${division===d?"active":""}`} onClick={() => setDivision(d)}>
                {d}
              </button>
            ))}
          </div>
          <div className="scroll-list" style={{marginTop:10}}>
            {loading ? <div className="loading"><div className="spinner"/>Loading…</div>
            : players.length === 0 ? <div className="empty">No players found</div>
            : players.map((p,i) => {
              const [bg,fg] = avColor(i);
              return (
                <div key={p.player_name_key||p.player_name} className={`player-row ${selected?.player_name===p.player_name?"selected":""}`} onClick={() => setSelected(p)}>
                  <div className="player-av" style={{background:bg,color:fg}}>{initials(p.player_name)}</div>
                  <div className="player-info">
                    <div className="player-name">{p.player_name}</div>
                    <div className="player-meta">{p.role} · {p.school} · {p.division}</div>
                  </div>
                  <span className={`trend ${p.trend||"Unknown"}`}>{p.trend==="Improving"?"↑":p.trend==="Declining"?"↓":"→"}</span>
                  <div className="value-usd">{fmtUsd(p.predicted_nil_value)}</div>
                  <span className={`score-pill ${tierClass(p.value_tier)}`}>{p.value_tier}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {selected && (
            <>
              <div className="card">
                <div className="card-header">
                  <div>
                    <div className="card-title">{selected.player_name}</div>
                    <div className="card-sub">{selected.role} · {selected.school} · {selected.conference}</div>
                  </div>
                  <span className={`score-pill ${tierClass(selected.value_tier)}`}>{selected.class_year || "—"}</span>
                </div>
                <div style={{padding:"14px 20px 0"}}>
                  <div style={{display:"flex",gap:16,alignItems:"flex-start",marginBottom:14}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:4}}>Performance score</div>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{flex:1,height:8,background:"var(--surface3)",borderRadius:4,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${selected.perf_score||selected.adjusted_score||0}%`,background:"var(--accent)",borderRadius:4,transition:"width .5s"}}/>
                        </div>
                        <span style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:18}}>{(selected.perf_score||selected.adjusted_score||0).toFixed(0)}</span>
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:2}}>Predicted NIL</div>
                      <div style={{fontFamily:"var(--font-display)",fontSize:22,fontWeight:700,color:"var(--teal)"}}>{fmtUsd(selected.predicted_nil_value)}</div>
                    </div>
                  </div>
                </div>
                <div className="stats-grid">
                  {[
                    ["PTS/G",(selected.pts_per_g||0).toFixed(1)],
                    ["REB/G",(selected.reb_per_g||0).toFixed(1)],
                    ["AST/G",(selected.ast_per_g||0).toFixed(1)],
                    ["Elig yrs",selected.elig_years_remaining||"—"],
                    ["Division",selected.division||"—"],
                    ["Trend",selected.trend||"—"],
                  ].map(([l,v]) => (
                    <div className="stat-cell" key={l}>
                      <div className="stat-v">{v}</div>
                      <div className="stat-l">{l}</div>
                    </div>
                  ))}
                </div>
                <div className={`verdict-box ${vc}`}>
                  <div className={`verdict-title ${vc}`}>{selected.verdict || "No verdict"}</div>
                  <div className="verdict-body">{selected.conf_tier ? `Conf tier ${selected.conf_tier} · ` : ""}{selected.label_type === "confirmed" ? "Confirmed NIL label" : "Model-predicted value"}</div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="card-title">Scouting notes</div>
                  <div className="card-sub">{selected.player_name}</div>
                </div>
                <div style={{padding:"12px 20px 0"}}>
                  <div style={{display:"flex",gap:8}}>
                    <input className="form-input" style={{flex:1,fontSize:13}} placeholder="Add a note…" value={noteInput} onChange={e => setNoteInput(e.target.value)} onKeyDown={e => e.key==="Enter" && addNote()}/>
                    <button className="btn btn-primary btn-sm" onClick={addNote} disabled={!noteInput.trim()}>Add</button>
                  </div>
                </div>
                <div className="scroll-list" style={{maxHeight:180,marginTop:10}}>
                  {notes.length === 0 ? <div className="empty" style={{padding:20}}>No notes yet</div>
                  : notes.map(n => (
                    <div className="note-item" key={n.id} style={{display:"flex",alignItems:"flex-start",gap:8}}>
                      <div style={{flex:1}}>
                        <div className="note-text">{n.note_text}</div>
                        <div className="note-time">{fmtDate(n.created_at)}</div>
                      </div>
                      <button onClick={() => { api(`/user/notes/${n.id}`,{method:"DELETE"}).catch(()=>{}); setNotes(prev=>prev.filter(x=>x.id!==n.id)); }}
                        style={{background:"none",border:"none",color:"var(--text-muted)",cursor:"pointer",fontSize:14,padding:"2px 4px",flexShrink:0,lineHeight:1}}
                        title="Delete note">×</button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{
        marginTop:32, padding:"10px 0",
        borderTop:"1px solid var(--border)",
        fontSize:11, color:"var(--text-muted)",
        lineHeight:1.6, textAlign:"center",
      }}>
        NIL valuations are AI-generated estimates based on statistical modeling.
        Actual market values may differ. Not financial or legal advice. · Rah-AI: Vantage by Epicentra LLC
      </div>
    </div>
  );
}

// ── Analytics ─────────────────────────────────────────────────────────────────
function AnalyticsScreen({ api, roster, budgetM }) {
  const [stats, setStats] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const players = roster?.players || [];
  const totalSpend = players.reduce((s, p) => s + parseFloat(p.nil_value_m || 0), 0);
  const remaining = (parseFloat(budgetM) || 0) - totalSpend;
  const byRole = ["guard","forward","center"].map(r => ({
    role: r,
    count: players.filter(p => p.role === r).length,
    spend: players.filter(p => p.role === r).reduce((s, p) => s + parseFloat(p.nil_value_m || 0), 0),
  })).filter(r => r.count > 0);

  useEffect(() => {
    Promise.all([
      api("/stats"),
      api("/user/watchlist"),
    ]).then(([s, w]) => {
      setStats(s);
      // Fetch recent notes for watchlisted players
      const watchlist = w.watchlist || [];
      if (watchlist.length > 0) {
        Promise.all(
          watchlist.slice(0,5).map(p =>
            api(`/user/notes/${encodeURIComponent(p.player_key)}`)
              .then(d => (d.notes || []).map(n => ({...n, player_name: p.player_name})))
              .catch(() => [])
          )
        ).then(allNotes => {
          const flat = allNotes.flat().sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
          setNotes(flat.slice(0, 8));
        });
      }
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><div className="spinner"/>Loading analytics…</div>;

  const positionColors = { guard:"var(--accent)", forward:"var(--blue)", center:"var(--amber)" };

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-title">Analytics</div>
        <div className="page-sub">Program overview · roster spend · portal landscape</div>
      </div>

      <div className="kpi-row">
        {[
          ["Roster size", players.length, "players committed", "teal"],
          ["NIL committed", `${fmtM(totalSpend)}`, "of budget", "teal"],
          ["Remaining", `${fmtM(remaining)}`, remaining < 0 ? "over budget" : "available", remaining < 0 ? "warn" : "up"],
          ["Portal targets", stats?.total_players?.toLocaleString() || "—", "players indexed", ""],
        ].map(([l,v,d,cls]) => (
          <div className="kpi-card" key={l}>
            <div className="kpi-label">{l}</div>
            <div className="kpi-value">{v}</div>
            <div className={`kpi-delta ${cls}`}>{d}</div>
          </div>
        ))}
      </div>

      <div className="grid-2" style={{marginBottom:16}}>
        <div className="card">
          <div className="card-header">
            <div className="card-title">Roster NIL breakdown</div>
            <div className="card-sub">${totalSpend.toFixed(2)}M of ${parseFloat(budgetM||0).toFixed(1)}M budget</div>
          </div>
          {players.length === 0
            ? <div className="empty">No players in roster yet — add from Scout Board or Roster Builder</div>
            : <>
              <div style={{padding:"16px 20px 8px"}}>
                <div style={{height:10,background:"var(--surface3)",borderRadius:5,overflow:"hidden",marginBottom:12}}>
                  <div style={{height:"100%",width:`${Math.min(100,(totalSpend/(parseFloat(budgetM)||1))*100)}%`,background:remaining<0?"var(--red)":remaining<2?"var(--amber)":"var(--accent)",borderRadius:5,transition:"width .4s"}}/>
                </div>
              </div>
              {players.map((p,i) => {
                const [bg,fg] = avColor(i);
                const pct = totalSpend > 0 ? (parseFloat(p.nil_value_m||0)/totalSpend)*100 : 0;
                return (
                  <div key={p.player_name} className="bar-row" style={{padding:"8px 20px"}}>
                    <div style={{width:28,height:28,borderRadius:"50%",background:bg,color:fg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:600,flexShrink:0}}>{initials(p.player_name)}</div>
                    <div style={{flex:1,minWidth:0,marginLeft:8}}>
                      <div style={{fontSize:12,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.player_name}</div>
                      <div style={{height:4,background:"var(--surface3)",borderRadius:2,overflow:"hidden",marginTop:3}}>
                        <div style={{height:"100%",width:`${pct.toFixed(0)}%`,background:positionColors[p.role]||"var(--accent)",borderRadius:2}}/>
                      </div>
                    </div>
                    <div style={{fontSize:12,fontWeight:600,color:"var(--teal)",minWidth:52,textAlign:"right",flexShrink:0}}>{fmtM(p.nil_value_m)}</div>
                  </div>
                );
              })}
              {byRole.length > 0 && (
                <div style={{padding:"12px 20px",borderTop:"1px solid var(--border)"}}>
                  <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.4px"}}>By position</div>
                  {byRole.map(r => (
                    <div className="bar-row" key={r.role} style={{padding:"4px 0"}}>
                      <div className="bar-label" style={{width:60,fontSize:12,textTransform:"capitalize"}}>{r.role}</div>
                      <div className="bar-track"><div className="bar-fill" style={{width:`${Math.min(100,(r.spend/(parseFloat(budgetM)||1))*100)}%`,background:positionColors[r.role]||"var(--accent)"}}/></div>
                      <div className="bar-val">{fmtM(r.spend)}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          }
        </div>

        {watchlist.length > 0 && (
          <div className="card" style={{marginTop:0}}>
            <div className="card-header">
              <div className="card-title">Watchlisted players</div>
              <div className="card-sub">{watchlist.length} players tracked · click to add to roster</div>
            </div>
            <div>
              {watchlist.map(w => {
                const alreadyOn = (roster?.players||[]).some(p => p.player_name === w.player_key);
                return (
                  <div key={w.player_key} style={{borderBottom:"1px solid var(--border)"}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 20px"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:600}}>{w.player_key}</div>
                        {(() => { try { const m = JSON.parse(w.status||"{}"); return <div style={{fontSize:11,color:"var(--text-muted)",marginTop:1}}>{m.role||"—"} · {m.school||"—"}</div>; } catch(e) { return <div style={{fontSize:11,color:"var(--text-muted)",marginTop:1}}>—</div>; } })()}
                      </div>
                      <button className="btn btn-secondary btn-sm"
                        onClick={() => toggleWatchNotes(w.player_key)}
                        style={{fontSize:11,marginRight:6}}>
                        {watchNotesOpen[w.player_key] ? "▲ Notes" : "📋 Notes"}
                      </button>
                      <button className="btn btn-danger btn-sm" style={{fontSize:11}} onClick={() => removeFromWatchlist(w.player_key)}>Remove</button>
                      {alreadyOn
                        ? <span style={{fontSize:11,color:"var(--teal)",fontWeight:500}}>✓ In roster</span>
                        : <button className="btn btn-primary btn-sm" style={{background:"linear-gradient(135deg,#7c3aed,#4f46e5)",border:"none",fontSize:11}} onClick={() => {
                            const _m = (() => { try { return JSON.parse(w.status||"{}"); } catch(e) { return {}; } })();
                            if (onImportProfile) onImportProfile({
                              player_name: w.player_key,
                              role: _m.role||"guard",
                              school: _m.school||"—",
                              predicted_nil_value: parseFloat(_m.nil||"1.00")*1000000,
                              elig_years_remaining: _m.elig||2,
                              conf_tier_label: _m.conf||"Rest D-I",
                              pts_per_g: _m.pts, reb_per_g: _m.reb, ast_per_g: _m.ast,
                              fg_pct: _m.fg, fg3_pct: _m.fg3, ft_pct: _m.ft,
                              stl_per_g: _m.stl, blk_per_g: _m.blk, mp_per_g: _m.mp,
                              tov_per_g: _m.tov, orb_per_g: _m.orb, g: _m.g,
                              _ts: Date.now(),
                            });
                          }}>↗ Evaluate Profile</button>
                      }
                    </div>
                    {watchNotesOpen[w.player_key] && (
                    <div style={{padding:"10px 20px 14px",borderTop:"1px solid var(--border)",background:"var(--surface2)"}}>
                      <div style={{display:"flex",gap:8,marginBottom:8}}>
                        <input
                          style={{flex:1,padding:"6px 10px",border:"1px solid var(--border)",borderRadius:7,fontSize:12,background:"var(--surface)",fontFamily:"var(--font-body)",outline:"none",color:"var(--text-primary)"}}
                          placeholder="Add a note (offer amount, visit outcome…)"
                          value={watchNoteInput[w.player_key] || ""}
                          onChange={e => setWatchNoteInput(prev => ({...prev, [w.player_key]: e.target.value}))}
                          onKeyDown={e => e.key === "Enter" && addWatchNote(w.player_key)}
                        />
                        <button className="btn btn-primary btn-sm" onClick={() => addWatchNote(w.player_key)}>Add</button>
                      </div>
                      {(watchNotes[w.player_key] || []).length === 0
                        ? <div style={{fontSize:12,color:"var(--text-muted)"}}>No notes yet</div>
                        : (watchNotes[w.player_key] || []).map(n => (
                            <div key={n.id} style={{fontSize:12,padding:"5px 0",borderBottom:"1px solid var(--border)",color:"var(--text-primary)",display:"flex",alignItems:"flex-start",gap:8}}>
                              <span style={{flex:1}}>{n.note_text}</span>
                              <button onClick={() => deleteNote(w.player_key, n.id)}
                                style={{background:"none",border:"none",color:"var(--text-muted)",cursor:"pointer",fontSize:14,padding:"0 2px",flexShrink:0,lineHeight:1}}
                                title="Delete note">×</button>
                            </div>
                          ))
                      }
                    </div>
                  )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div className="card">
            <div className="card-header">
              <div className="card-title">Portal landscape</div>
              <div className="card-sub">2025 season · D-I players</div>
            </div>
            <div style={{padding:"8px 0"}}>
              {[
                ["Strong offer", stats?.verdict_distribution?.["Strong offer"]||0, "var(--green)"],
                ["Offer", stats?.verdict_distribution?.["Offer"]||0, "var(--teal)"],
                ["Monitor", stats?.verdict_distribution?.["Monitor"]||0, "var(--amber)"],
                ["Pass", stats?.verdict_distribution?.["Pass"]||0, "var(--text-muted)"],
              ].map(([label, count, color]) => (
                <div className="bar-row" key={label}>
                  <div className="bar-label">{label}</div>
                  <div className="bar-track">
                    <div className="bar-fill" style={{width:`${((count/(stats?.total_players||1))*100).toFixed(0)}%`,background:color}}/>
                  </div>
                  <div className="bar-val">{count?.toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">NIL by position</div>
              <div className="card-sub">median predicted value · D-I 2025</div>
            </div>
            <div style={{padding:"14px 20px"}}>
              {[
                ["Guard", 487000, "var(--accent)"],
                ["Forward", 412000, "var(--blue)"],
                ["Center", 389000, "var(--amber)"],
              ].map(([pos, med, color]) => (
                <div key={pos} style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:12,color:"var(--text-secondary)"}}>{pos}</span>
                    <span style={{fontSize:12,fontWeight:600,color:"var(--text-primary)"}}>{fmtUsd(med)}</span>
                  </div>
                  <div style={{height:6,background:"var(--surface3)",borderRadius:3,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${(med/500000*100).toFixed(0)}%`,background:color,borderRadius:3}}/>
                  </div>
                </div>
              ))}
              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:8}}>Median across all D-I players with Strong offer / Offer verdict</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Recent scouting notes</div>
              <div className="card-sub">across watchlisted players</div>
            </div>
            {notes.length === 0
              ? <div className="empty" style={{padding:20}}>No notes yet — add notes from Scout Board</div>
              : notes.map(n => (
                <div className="note-item" key={n.id} style={{display:"flex",alignItems:"flex-start",gap:8}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:500,color:"var(--teal)",marginBottom:2}}>{n.player_name}</div>
                    <div className="note-text">{n.note_text}</div>
                    <div className="note-time">{fmtDate(n.created_at)}</div>
                  </div>
                  <button onClick={() => { api(`/user/notes/${n.id}`,{method:"DELETE"}).catch(()=>{}); setNotes(prev=>prev.filter(x=>x.id!==n.id)); }}
                    style={{background:"none",border:"none",color:"var(--text-muted)",cursor:"pointer",fontSize:14,padding:"2px 4px",flexShrink:0,lineHeight:1}}
                    title="Delete note">×</button>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      <div style={{marginTop:32,padding:"10px 0",borderTop:"1px solid var(--border)",fontSize:11,color:"var(--text-muted)",lineHeight:1.6,textAlign:"center"}}>
        NIL valuations are AI-generated estimates based on statistical modeling. Actual market values may differ. Not financial or legal advice. · Rah-AI: Vantage by Epicentra LLC
      </div>
    </div>
  );
}


const TIER_CONFIG = {
  "Elite":   { color:"var(--teal)",   bg:"rgba(13,148,136,0.10)",  border:"rgba(13,148,136,0.25)",  icon:"⭐", label:"Elite",
    tagline:"Top-market profile. This player commands genuine competition for their services.",
    bullets: ["Use the range midpoint as your opening offer",
              "Expect competing programs to move fast — set a decision timeline",
              "High elig years + elite conf = premium multiplier is working",
              "Cross-reference with Scout Board to see who else you are competing against"],
    use:["📋 Confirm stats with official athletics page before any commitment",
         "🎯 Conference tier is the biggest lever — recheck if they transfer",
         "⚡ Engage early — Elite profiles rarely stay available long",
         "💼 Budget this against your remaining NIL cap before extending"]},
  "Premium": { color:"var(--blue)",   bg:"rgba(59,114,214,0.08)",  border:"rgba(59,114,214,0.22)",  icon:"🔷", label:"Premium",
    tagline:"Strong market value. A meaningful NIL investment with clear statistical justification.",
    bullets: ["Range reflects real market demand — do not anchor too low",
              "Production is legitimate; gap to Elite is typically conf tier or eligibility",
              "Good fit for programs looking for proven contributors",
              "Watchlist and revisit after conference tournament performance"],
    use:["📋 Cross-check conf tier — upgrading by one tier can shift estimate significantly",
         "🎯 Use lower end of range if eligibility years are 1 or fewer",
         "📊 Compare multiple Premium profiles to find best fit-to-value",
         "💼 Strong candidate for roster builder commitment"]},
  "First Team": { color:"var(--amber)", bg:"rgba(212,134,10,0.08)",  border:"rgba(212,134,10,0.22)", icon:"🔶", label:"First Team",
    tagline:"Developmental value. Productive role player — NIL range reflects position and production floor.",
    bullets: ["Value is real but comp-driven — market sets the floor here",
              "Eligibility years are a key multiplier; 3-4 yrs remaining lifts the range",
              "Suitable for depth and role-fit acquisitions",
              "Monitor trend — an Improving signal can push toward Premium"],
    use:["📋 Stats are borderline — verify games played (low sample distorts model)",
         "🎯 Trend field matters most here — set to Improving if recent trajectory supports it",
         "📊 Use range low as an anchor; upside depends on role fit",
         "💼 Good depth target; budget accordingly against Elite and Premium commitments"]},
  "Role Player":  { color:"var(--text-secondary)", bg:"var(--surface2)", border:"var(--border)", icon:"◾", label:"Role Player",
    tagline:"Floor-value profile. Model sees limited statistical case for significant NIL investment.",
    bullets: ["Range reflects statistical floor — not a ceiling assessment",
              "Walk-on or minimum NIL territory based on this profile alone",
              "Non-statistical factors (coachability, leadership, fit) are not modeled",
              "Revisit with updated stats if production improves"],
    use:["📋 Double-check inputs — a missing required stat will deflate results",
         "🎯 Consider re-running with Improving trend if recent form warrants",
         "📊 Non-stat value (practice player, depth insurance) not captured here",
         "💼 If targeting this profile, NIL range should not drive the decision"]},
};
const getTierConfig = tier => TIER_CONFIG[tier] || TIER_CONFIG["Mid-tier"];

// ── PDF Export ────────────────────────────────────────────────────────────────
function exportToPDF(result, role, confTier, elig, trend, stats) {
  const tier       = result.tier || result.value_tier || "—";
  const tc         = getTierConfig(tier);
  const date       = new Date().toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"});
  const nilRange   = result.range_fmt || result.point_estimate_fmt || "—";
  const perfScore  = result.perf_score != null ? result.perf_score.toFixed(1) : "—";
  const adjScore   = result.adjusted_score != null ? result.adjusted_score.toFixed(1) : "—";
  const percentile = "Top " + Math.max(1, Math.round(100-(result.perf_score||0))) + "%";

  const statRows = [
    ["Position",       role ? role.charAt(0).toUpperCase()+role.slice(1) : null],
    ["Conference",     confTier||null],
    ["Eligibility",    elig ? elig+" yr"+(elig!==1?"s":"") : null],
    ["Trend",          trend||null],
    ["PTS/G",          stats.pts_per_g||null],
    ["AST/G",          stats.ast_per_g||null],
    ["REB/G",          stats.reb_per_g||null],
    ["FG%",            stats.fg_pct ? (parseFloat(stats.fg_pct)*100).toFixed(1)+"%" : null],
    ["3P%",            stats.fg3_pct ? (parseFloat(stats.fg3_pct)*100).toFixed(1)+"%" : null],
    ["FT%",            stats.ft_pct ? (parseFloat(stats.ft_pct)*100).toFixed(1)+"%" : null],
    ["STL/G",          stats.stl_per_g||null],
    ["BLK/G",          stats.blk_per_g||null],
    ["MP/G",           stats.mp_per_g||null],
  ].filter(([,v]) => v && v !== "0" && v !== "");

  const statCells = statRows.map(([l,v]) =>
    '<div class="stat-cell"><div class="stat-val">'+v+'</div><div class="stat-lbl">'+l+'</div></div>'
  ).join("");

  const html = [
    '<!DOCTYPE html><html><head><meta charset="utf-8"/>',
    '<title>Rah-AI: Vantage — Profile Evaluation</title>',
    '<style>',
    '@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap");',
    '*{box-sizing:border-box;margin:0;padding:0;}',
    'body{font-family:"Plus Jakarta Sans",sans-serif;color:#0D0F14;background:#fff;padding:48px;max-width:720px;margin:0 auto;}',
    '.watermark{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-35deg);font-size:72px;font-weight:800;color:rgba(0,0,0,0.04);white-space:nowrap;pointer-events:none;z-index:0;}',
    '.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px;padding-bottom:20px;border-bottom:2px solid #0D9488;}',
    '.brand{font-size:22px;font-weight:800;color:#0D9488;}',
    '.brand-sub{font-size:11px;color:#9AA0B0;margin-top:2px;}',
    '.tier-pill{display:inline-block;padding:6px 16px;border-radius:99px;font-size:13px;font-weight:700;background:#F0FDFA;color:#0D9488;border:1px solid rgba(13,148,136,0.3);margin-bottom:8px;}',
    '.nil-label{font-size:11px;color:#9AA0B0;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;}',
    '.nil-range{font-size:48px;font-weight:800;color:#0D9488;letter-spacing:-2px;line-height:1;margin-bottom:20px;}',
    '.scores{display:flex;gap:16px;margin-bottom:28px;}',
    '.score-card{flex:1;background:#F7F8FA;border-radius:10px;padding:12px 16px;text-align:center;}',
    '.score-label{font-size:10px;color:#9AA0B0;text-transform:uppercase;letter-spacing:0.3px;margin-bottom:4px;}',
    '.score-val{font-size:24px;font-weight:700;}',
    '.section-title{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#9AA0B0;margin-bottom:10px;margin-top:24px;}',
    '.stats-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:28px;}',
    '.stat-cell{background:#F7F8FA;border-radius:8px;padding:10px 12px;}',
    '.stat-val{font-size:18px;font-weight:700;}',
    '.stat-lbl{font-size:10px;color:#9AA0B0;margin-top:1px;}',
    '.disclaimer{background:#FEF2F2;border:1px solid #FECACA;border-radius:10px;padding:16px 18px;margin-top:28px;}',
    '.disc-title{font-size:11px;font-weight:800;color:#991B1B;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:8px;}',
    '.disc-body{font-size:12px;color:#7F1D1D;line-height:1.7;}',
    '.disc-fine{font-size:10px;color:#9AA0B0;margin-top:8px;line-height:1.6;}',
    '.footer{margin-top:32px;padding-top:16px;border-top:1px solid #E4E7EF;font-size:10px;color:#9AA0B0;display:flex;justify-content:space-between;}',
    '@media print{body{padding:32px;}}',
    '</style></head><body>',
    '<div class="watermark">FOR INTERNAL USE ONLY</div>',
    '<div class="header">',
      '<div><div class="brand">Rah-AI: Vantage</div>',
      '<div class="brand-sub">We evaluate profiles, not players. &middot; Epicentra LLC</div></div>',
      '<div style="text-align:right"><div style="font-weight:600;font-size:13px">Profile Evaluation Report</div>',
      '<div style="margin-top:3px;font-size:11px;color:#9AA0B0">'+date+'</div></div>',
    '</div>',
    '<div class="tier-pill">'+tc.icon+' '+tier+' Profile</div>',
    '<div class="nil-label">Estimated Roster Budget</div>',
    '<div class="nil-range">'+nilRange+'</div>',
    '<div class="scores">',
      '<div class="score-card"><div class="score-label">Stat Rating</div><div class="score-val">'+perfScore+'</div></div>',
      '<div class="score-card"><div class="score-label">Market Rating</div><div class="score-val">'+adjScore+'</div></div>',
      '<div class="score-card"><div class="score-label">D-I Rank</div><div class="score-val">'+percentile+'</div></div>',
    '</div>',
    '<div class="section-title">Profile Inputs</div>',
    '<div class="stats-grid">'+statCells+'</div>',
    '<div class="disclaimer">',
      '<div class="disc-title">&#9888; For Internal Planning Use Only — Not an FMV Certification</div>',
      '<div class="disc-body">This document is produced by Rah-AI: Vantage, a licensed statistical profile evaluation tool developed by Epicentra LLC. It is intended exclusively for internal roster planning and Roster Budget strategy by authorized program staff. This output must not be submitted to NIL Go, Deloitte, the Collegiate Sports Collective (CSC), or any third-party FMV certification or compliance body. It is not for presentation to student-athletes, their agents, family members, or legal representatives. It does not constitute legal, financial, or compliance advice.</div>',
      '<div class="disc-fine" style="margin-top:12px;padding-top:10px;border-top:1px solid #FECACA;">',
        '<strong style="color:#7F1D1D;font-size:10px;">How this estimate was generated:</strong><br/>',
        'The Roster Budget range is produced by a GradientBoosting regression model trained on 3,574 Division I player profiles from the 2025 season. The model evaluates 12 statistical inputs (points, rebounds, assists, shooting efficiency, steals, blocks, minutes, and usage) against historical NIL market data. A Stat Rating reflects raw on-court production relative to all D-I players. A Market Rating adjusts for conference strength and eligibility — the variables that drive real market demand beyond production alone. The final estimate represents the statistical production floor: what the on-court profile justifies in the current NIL market. Brand value, social following, and name recognition are not modeled and will produce upside above this floor for high-profile players.',
      '</div>',
      '<div class="disc-fine" style="margin-top:10px;">',
        '<strong style="color:#9AA0B0;">Model accuracy:</strong> 84% tier classification accuracy validated against publicly reported NIL market values (On3 NIL database, 2024–25). Elite-tier profiles: 89% exact classification accuracy. Estimates carry an approximate ±20% range reflecting genuine market variability.',
      '</div>',
      '<div class="disc-fine" style="margin-top:10px;">',
        'Rah-AI: Vantage is a subscription-based platform licensed to authorized athletic programs by Epicentra LLC. Outputs are confidential, program-specific, and covered under your program&#39;s active license agreement. Redistribution, resale, or use outside the licensed program is prohibited. &copy; Epicentra LLC. All rights reserved.',
      '</div>',
    '</div>',
    '<div class="footer">',
      '<span>Rah-AI: Vantage &middot; Epicentra LLC &middot; Confidential Internal Use Only</span>',
      '<span>Generated '+date+'</span>',
    '</div>',
    '<script>window.onload=function(){window.print();};<\/script>',
    '</body></html>'
  ].join("");

  const blob = new Blob([html], {type:"text/html"});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.target = "_blank"; a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  setTimeout(()=>{ document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
}


// ── Custom Predict Screen ─────────────────────────────────────────────────────
function CustomPredictScreen({ api, importedProfile, onImportConsumed, roster, setRoster, onAddedToRoster }) {
  const [role, setRole] = useState("guard");
  const [confTier, setConfTier] = useState("Rest D-I");
  const [elig, setElig] = useState(2);
  const [trend, setTrend] = useState("Stable");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [lastAdded, setLastAdded] = useState(null);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({
    pts_per_g:"",reb_per_g:"",ast_per_g:"",fg_pct:"",fg3_pct:"",ft_pct:"",
    stl_per_g:"",blk_per_g:"",tov_per_g:"",orb_per_g:"",mp_per_g:"",g:"",
    fg3a_per_g:"",fta_per_g:"",
  });

  const statFields = {
    guard: [
      {key:"pts_per_g",label:"Points/game",placeholder:"19.4",required:true},
      {key:"ast_per_g",label:"Assists/game",placeholder:"4.0",required:true},
      {key:"reb_per_g",label:"Rebounds/game",placeholder:"4.6",required:true},
      {key:"fg_pct",label:"FG% (0-1)",placeholder:"0.484",required:true},
      {key:"fg3_pct",label:"3P% (0-1)",placeholder:"0.333",required:false},
      {key:"ft_pct",label:"FT% (0-1)",placeholder:"0.780",required:false},
      {key:"stl_per_g",label:"Steals/game",placeholder:"1.2",required:false},
      {key:"tov_per_g",label:"Turnovers/game",placeholder:"2.8",required:false},
      {key:"mp_per_g",label:"Minutes/game",placeholder:"32.7",required:false},
      {key:"g",label:"Games played",placeholder:"30",required:false},
      {key:"fg3a_per_g",label:"3PA/game",placeholder:"4.0",required:false},
      {key:"fta_per_g",label:"FTA/game",placeholder:"3.5",required:false},
    ],
    forward: [
      {key:"pts_per_g",label:"Points/game",placeholder:"14.2",required:true},
      {key:"reb_per_g",label:"Rebounds/game",placeholder:"7.8",required:true},
      {key:"ast_per_g",label:"Assists/game",placeholder:"2.1",required:true},
      {key:"fg_pct",label:"FG% (0-1)",placeholder:"0.520",required:true},
      {key:"ft_pct",label:"FT% (0-1)",placeholder:"0.720",required:false},
      {key:"stl_per_g",label:"Steals/game",placeholder:"0.8",required:false},
      {key:"blk_per_g",label:"Blocks/game",placeholder:"1.2",required:false},
      {key:"orb_per_g",label:"Off Reb/game",placeholder:"1.5",required:false},
      {key:"tov_per_g",label:"Turnovers/game",placeholder:"2.0",required:false},
      {key:"mp_per_g",label:"Minutes/game",placeholder:"30.0",required:false},
      {key:"g",label:"Games played",placeholder:"30",required:false},
      {key:"fta_per_g",label:"FTA/game",placeholder:"4.0",required:false},
    ],
    center: [
      {key:"reb_per_g",label:"Rebounds/game",placeholder:"9.5",required:true},
      {key:"pts_per_g",label:"Points/game",placeholder:"12.0",required:true},
      {key:"fg_pct",label:"FG% (0-1)",placeholder:"0.580",required:true},
      {key:"blk_per_g",label:"Blocks/game",placeholder:"2.1",required:true},
      {key:"ast_per_g",label:"Assists/game",placeholder:"1.5",required:false},
      {key:"ft_pct",label:"FT% (0-1)",placeholder:"0.680",required:false},
      {key:"orb_per_g",label:"Off Reb/game",placeholder:"2.8",required:false},
      {key:"stl_per_g",label:"Steals/game",placeholder:"0.6",required:false},
      {key:"tov_per_g",label:"Turnovers/game",placeholder:"1.8",required:false},
      {key:"mp_per_g",label:"Minutes/game",placeholder:"28.0",required:false},
      {key:"g",label:"Games played",placeholder:"30",required:false},
    ],
  };

  const vColor = {"Strong offer":"green","Offer":"teal","Monitor":"amber","Pass":"red"};

  useEffect(() => {
    if (!importedProfile) return;
    const p = importedProfile;
    const r = (p.role||"guard").toLowerCase();
    setRole(["guard","forward","center"].includes(r) ? r : "guard");
    setConfTier(p.conf_tier_label || p.conference || "Rest D-I");
    setElig(p.elig_years_remaining || 2);
    setTrend(p.trend || "Stable");
    setStats({
      pts_per_g: p.pts_per_g  != null ? String(p.pts_per_g)  : "",
      reb_per_g: p.reb_per_g  != null ? String(p.reb_per_g)  : "",
      ast_per_g: p.ast_per_g  != null ? String(p.ast_per_g)  : "",
      fg_pct:    p.fg_pct     != null ? String(p.fg_pct)     : "",
      fg3_pct:   p.fg3_pct    != null ? String(p.fg3_pct)    : "",
      ft_pct:    p.ft_pct     != null ? String(p.ft_pct)     : "",
      stl_per_g: p.stl_per_g  != null ? String(p.stl_per_g)  : "",
      blk_per_g: p.blk_per_g  != null ? String(p.blk_per_g)  : "",
      mp_per_g:  p.mp_per_g   != null ? String(p.mp_per_g)   : "",
      tov_per_g: p.tov_per_g  != null ? String(p.tov_per_g)  : "",
      orb_per_g: p.orb_per_g  != null ? String(p.orb_per_g)  : "",
      g:         p.g          != null ? String(p.g)          : "",
      fg3a_per_g: p.fg3a_per_g != null ? String(p.fg3a_per_g) : "",
      fta_per_g:  p.fta_per_g  != null ? String(p.fta_per_g)  : "",
    });
    console.log("[import] stats set from profile:", p.player_name, p.pts_per_g, p.fg_pct);
    setResult(null); setError("");
    if (onImportConsumed) onImportConsumed();
  }, [importedProfile]);

  async function handlePredict() {
    setError(""); setResult(null); setLoading(true); setLastAdded(null);
    const required = statFields[role].filter(f=>f.required);
    const missing  = required.filter(f=>!stats[f.key]);
    if (missing.length>0) {
      setError("Please fill in: "+missing.map(f=>f.label).join(", "));
      setLoading(false); return;
    }
    const payload = {role, conf_tier_label:confTier,
                     elig_years_remaining:parseInt(elig), trend,
                     ...Object.fromEntries(
                       Object.entries(stats).map(([k,v])=>[k,parseFloat(v)||0]))};
    try {
      const res = await api("/predict",{method:"POST",body:JSON.stringify(payload)});
      setResult(res);
    } catch(e) {
      setError("Prediction failed — check inputs and try again.");
    } finally { setLoading(false); }
  }

  function resetRole(r) {
    setRole(r); setResult(null); setError(""); setLastAdded(null);
    setStats({pts_per_g:"",reb_per_g:"",ast_per_g:"",fg_pct:"",fg3_pct:"",ft_pct:"",
              stl_per_g:"",blk_per_g:"",tov_per_g:"",orb_per_g:"",mp_per_g:"",g:"",
              fg3a_per_g:"",fta_per_g:""});
  }

  const vc = result ? (vColor[result.verdict]||"teal") : "teal";

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-title">Profile Evaluator</div>
        <div className="page-sub">Fill in the stats, get estimates — set strategies ahead of time.</div>
      </div>
      <div className="kpi-row">
        {[
          ["Position Models",  "3",       "Guard · Forward · Center", "teal"],
          ["Instant Result",   "< 1s",    "model inference",           "up"],
          ["Budget Range",     "±20%",    "model uncertainty",         "warn"],
          ["Model Accuracy",   "84%",     "vs. public valuations",     "teal"],
        ].map(([l,v,d,cls])=>(
          <div className="kpi-card" key={l}>
            <div className="kpi-label">{l}</div>
            <div className="kpi-value">{v}</div>
            <div className={`kpi-delta ${cls}`}>{d}</div>
          </div>
        ))}
      </div>
      <div className="grid-2" style={{gap:16}}>
        <div className="card">
          <div className="card-header">
            <div className="card-title">Player profile<Tooltip body="Enter stats from any public source. Position determines which model runs. No player name needed." /></div>
            <div className="card-sub">enter stats from any public source</div>
          </div>
          <div style={{padding:"16px 20px 0"}}>
            <div style={{marginBottom:16}}>
              <div className="form-label">Position</div>
              <div className="filter-row" style={{padding:0}}>
                {["guard","forward","center"].map(r=>(
                  <button key={r} className={`filter-pill ${role===r?"active":""}`}
                    onClick={()=>resetRole(r)} style={{textTransform:"capitalize"}}>{r}</button>
                ))}
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 16px",marginBottom:16}}>
              {statFields[role].map(f=>(
                <div key={f.key}>
                  <label className="form-label">
                    {f.label}{f.required&&<span style={{color:"var(--red)",marginLeft:3}}>*</span>}
                  </label>
                  <input className="form-input" type="number" step="0.01"
                    placeholder={f.placeholder} value={stats[f.key]}
                    onChange={e=>setStats(p=>({...p,[f.key]:e.target.value}))}
                    style={{padding:"8px 12px",fontSize:13}}/>
                </div>
              ))}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"10px 16px",marginBottom:20}}>
              <div>
                <div className="form-label">Conference tier<Tooltip body="Adjusts Market Rating — same stats produce different value across conference levels." /></div>
                <select className="form-input" value={confTier}
                  onChange={e=>setConfTier(e.target.value)}
                  style={{padding:"8px 12px",fontSize:13}}>
                  {["Power 4","P4-Adjacent","G5 / Strong Mid","Rest D-I","D-II","D-III"].map(t=>(
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="form-label">Eligibility (yrs)<Tooltip body="Years remaining affects demand. 1-year players command a premium for immediate impact." /></div>
                <select className="form-input" value={elig}
                  onChange={e=>setElig(e.target.value)}
                  style={{padding:"8px 12px",fontSize:13}}>
                  {[0,1,2,3,4].map(n=><option key={n} value={n}>{n} yr{n!==1?"s":""}</option>)}
                </select>
              </div>
              <div>
                <div className="form-label">Trend<Tooltip body="'Improving' adds a market premium. 'Declining' applies a discount to the base estimate." /></div>
                <select className="form-input" value={trend}
                  onChange={e=>setTrend(e.target.value)}
                  style={{padding:"8px 12px",fontSize:13}}>
                  {["Improving","Stable","Declining","Unknown"].map(t=><option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
            {error&&<div className="login-error" style={{marginBottom:12}}>{error}</div>}
            <button
              style={{width:"100%",padding:"12px 20px",fontSize:14,fontWeight:600,
                      marginBottom:20,border:"none",borderRadius:8,
                      cursor:loading?"not-allowed":"pointer",
                      background:"linear-gradient(135deg, #7C3AED 0%, #A855F7 60%, #C084FC 100%)",
                      color:"#fff",letterSpacing:"-0.2px",
                      boxShadow:"0 2px 12px rgba(124,58,237,0.35)",
                      opacity:loading?0.7:1,transition:"opacity 0.15s"}}
              onMouseOver={e=>!loading&&(e.currentTarget.style.opacity="0.88")}
              onMouseOut={e=>e.currentTarget.style.opacity=loading?"0.7":"1"}
              onClick={handlePredict} disabled={loading}>
              {loading?<><div className="spinner" style={{width:14,height:14,borderWidth:1.5,borderTopColor:"#fff"}}/>Predicting…</>:"⚡ Evaluate Profile"}
            </button>
          </div>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {result?(
            <>
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Profile evaluation result</div>
                  <span style={{display:"flex",alignItems:"center",gap:8}}>
                    <span className={`score-pill ${tierClass(result.tier||result.value_tier)}`}>
                      {result.tier||result.value_tier}
                    </span>
                    <button
                      onClick={() => exportToPDF(result, role, confTier, elig, trend, stats)}
                      title="Export evaluation to PDF"
                      style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",
                              border:"1px solid rgba(220,38,38,0.35)",borderRadius:6,
                              background:"rgba(220,38,38,0.06)",
                              color:"#DC2626",fontSize:11,fontWeight:700,cursor:"pointer",
                              letterSpacing:"0.2px",transition:"all 0.15s",flexShrink:0}}
                      onMouseOver={e=>{e.currentTarget.style.background="rgba(220,38,38,0.12)";e.currentTarget.style.borderColor="rgba(220,38,38,0.6)";}}
                      onMouseOut={e=>{e.currentTarget.style.background="rgba(220,38,38,0.06)";e.currentTarget.style.borderColor="rgba(220,38,38,0.35)";}}>
                      <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1h6.5L11 4.5V13H1V1z" stroke="#DC2626" strokeWidth="1.2" fill="none"/>
                        <path d="M7 1v4h4" stroke="#DC2626" strokeWidth="1.2"/>
                        <path d="M3.5 7.5h5M3.5 9.5h5M3.5 5.5h2" stroke="#DC2626" strokeWidth="1" strokeLinecap="round"/>
                      </svg>
                      PDF
                    </button>
                  </span>
                </div>
                <div style={{padding:"20px 20px 24px",textAlign:"center",borderBottom:"1px solid var(--border)"}}>
                  {(() => {
                    const tc = getTierConfig(result.tier||result.value_tier);
                    return (
                      <div style={{display:"inline-flex",alignItems:"center",gap:10,background:tc.bg,border:`1px solid ${tc.border}`,borderRadius:99,padding:"8px 20px",marginBottom:12}}>
                        <span style={{fontSize:20}}>{tc.icon}</span>
                        <span style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:800,color:tc.color,letterSpacing:"-0.3px"}}>{tc.label}</span>
                      </div>
                    );
                  })()}
                  <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.5px"}}>Estimated Roster Budget</div>
                  <div style={{fontFamily:"var(--font-display)",fontSize:38,fontWeight:700,color:"var(--teal)",lineHeight:1}}>
                    {result.range_fmt || result.point_estimate_fmt}
                  </div>
                  <button className="btn btn-primary" style={{marginTop:16,width:"100%"}} onClick={() => {
                    if (!roster || !setRoster) return;
                    const players = roster?.players || [];
                    const rolePrefix = role === "guard" ? "G" : role === "forward" ? "F" : role === "center" ? "C" : "P";
                    const n = players.filter(p => p.player_name?.startsWith(`Profile ${rolePrefix}`)).length + 1;
                    const profileName = `Profile ${rolePrefix}${n}`;
                    const nilVal = result.point_estimate ? (result.point_estimate/1000000).toFixed(2)
                                 : result.range_low ? ((result.range_low + (result.range_high||result.range_low))/2/1000000).toFixed(2)
                                 : "0.50";
                    const newRoster = {...roster, players:[...players, {
                      player_name: profileName,
                      role, school:"—", nil_value_m: nilVal, elig_years: elig,
                      _stats: {
                        pts_per_g:  stats.pts_per_g,  ast_per_g:  stats.ast_per_g,
                        reb_per_g:  stats.reb_per_g,  stl_per_g:  stats.stl_per_g,
                        blk_per_g:  stats.blk_per_g,  fg_pct:     stats.fg_pct,
                        fg3_pct:    stats.fg3_pct,    ft_pct:     stats.ft_pct,
                        mp_per_g:   stats.mp_per_g,   conf_tier:  confTier,
                        elig_years: elig,              trend:      trend,
                      },
                      _result: {
                        tier: result.tier||result.value_tier,
                        range_fmt: result.range_fmt,
                        point_estimate: result.point_estimate,
                        perf_score: result.perf_score,
                        adjusted_score: result.adjusted_score,
                      }
                    }]};
                    setRoster(newRoster);
                    setLastAdded(profileName);
                    if (onAddedToRoster) onAddedToRoster(profileName, role||"player");
                    api("/user/squads",{method:"POST",body:JSON.stringify({squad_name:"2026-27 Roster",squad_data:newRoster,is_default:true})}).catch(()=>{});
                  }}>
                    + Add to Roster
                  </button>
                  {lastAdded && (
                    <div style={{marginTop:8,display:"flex",alignItems:"center",gap:6,color:"var(--teal)",fontSize:12,fontWeight:500}}>
                      <span>✓</span>
                      <span>Added to Roster as <strong>{lastAdded}</strong></span>
                    </div>
                  )}
                </div>
                {(() => {
                  const tc = getTierConfig(result.tier||result.value_tier);
                  return (
                    <div style={{padding:"16px 20px"}}>
                      <div style={{background:tc.bg,border:`1px solid ${tc.border}`,borderRadius:10,padding:"14px 16px",marginBottom:14}}>
                        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                          <span style={{fontSize:20}}>{tc.icon}</span>
                          <div>
                            <div style={{fontFamily:"var(--font-display)",fontSize:15,fontWeight:700,color:tc.color}}>{tc.label} profile</div>
                            <div style={{fontSize:12,color:"var(--text-secondary)",lineHeight:1.4,marginTop:2}}>{tc.tagline}</div>
                          </div>
                        </div>
                        <div style={{borderTop:`1px solid ${tc.border}`,paddingTop:10,display:"flex",flexDirection:"column",gap:5}}>
                          {tc.bullets.map((b,i) => (
                            <div key={i} style={{fontSize:12,color:"var(--text-secondary)",lineHeight:1.5,display:"flex",gap:8}}>
                              <span style={{color:tc.color,flexShrink:0}}>·</span><span>{b}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{display:"flex",gap:12}}>
                        <div style={{flex:1,background:"var(--surface2)",borderRadius:8,padding:"10px 14px",textAlign:"center"}}>
                          <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:2,textTransform:"uppercase",letterSpacing:"0.3px",display:"flex",alignItems:"center",justifyContent:"center"}}>
                            Perf score<Tooltip label="Performance Score" body="Weighted composite of position-specific stats (pts, reb, ast, fg%, etc.). Scored 0–100 within role group. 50 = median D-I player at this position." pill="0 – 100 scale"/>
                          </div>
                          <div style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:700}}>{result.perf_score?.toFixed(1)||"—"}</div>
                        </div>
                        <div style={{flex:1,background:"var(--surface2)",borderRadius:8,padding:"10px 14px",textAlign:"center"}}>
                          <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:2,textTransform:"uppercase",letterSpacing:"0.3px",display:"flex",alignItems:"center",justifyContent:"center"}}>
                            Adj score<Tooltip label="Adjusted Score" body="Perf score after conference tier and eligibility multipliers. Higher conf tier + more elig years = larger upward adjustment." pill="Perf × conf × elig"/>
                          </div>
                          <div style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:700}}>{result.adjusted_score?.toFixed(1)||"—"}</div>
                        </div>
                        <div style={{flex:1,background:"var(--surface2)",borderRadius:8,padding:"10px 14px",textAlign:"center"}}>
                          <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:2,textTransform:"uppercase",letterSpacing:"0.3px",display:"flex",alignItems:"center",justifyContent:"center"}}>
                            Percentile<Tooltip label="Position Percentile" body="Estimated rank among D-I players at this position based on performance score. Top 5% = Elite territory." pill="Higher is better" left={true}/>
                          </div>
                          <div style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:700}}>
                            {result.perf_score != null
                              ? `Top ${Math.max(1, Math.round(100 - result.perf_score))}%`
                              : "—"}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="card">
                <div className="card-header"><div className="card-title">How to use this result</div></div>
                <div style={{padding:"14px 20px",fontSize:13,color:"var(--text-secondary)",lineHeight:1.8}}>
                  {getTierConfig(result.tier||result.value_tier).use.map((line,i) => (
                    <div key={i} style={{marginBottom:4}}>{line}</div>
                  ))}
                </div>
                <div style={{padding:"10px 16px",borderTop:"1px solid var(--border)",background:"#FEF2F2",borderBottomLeftRadius:"var(--radius-lg)",borderBottomRightRadius:"var(--radius-lg)"}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#991B1B",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>⚠ For Internal Planning Use Only</div>
                  <div style={{fontSize:11,color:"#7F1D1D",lineHeight:1.6}}>
                    This output is NOT an FMV certification and must not be submitted to NIL Go, Deloitte, the CSC, or any regulatory body. Not for presentation to student-athletes, agents, or legal representatives. Not legal or compliance advice.
                  </div>
                  <div style={{fontSize:10,color:"var(--text-muted)",marginTop:6}}>
                    {result.disclaimer || "AI-generated statistical profile estimate. Actual market values may differ. Rah-AI: Vantage by Epicentra LLC."}
                  </div>
                </div>
              </div>
            </>
          ):(
            <div className="card" style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",minHeight:300}}>
              <div style={{textAlign:"center",padding:40}}>
                <div style={{fontSize:40,marginBottom:16}}>🎯</div>
                <div style={{fontFamily:"var(--font-display)",fontSize:18,fontWeight:600,marginBottom:8}}>Enter any profile stats</div>
                <div style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.6,maxWidth:260}}>
                  No player name needed. Enter stats from any source and get an instant NIL estimate powered by Rah-AI: Vantage.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div style={{marginTop:32,padding:"10px 0",borderTop:"1px solid var(--border)",
                   fontSize:11,color:"var(--text-muted)",lineHeight:1.6,textAlign:"center"}}>
        NIL valuations are AI-generated estimates based on statistical modeling.
        Actual market values may differ. Not financial or legal advice. · Rah-AI: Vantage by Epicentra LLC
      </div>
    </div>
  );
}

// ── Notes Screen ─────────────────────────────────────────────────────────────

// ── WatchlistBlock ────────────────────────────────────────────────────────────
function WatchlistBlock({ api, onImportProfile }) {
  const [watchlist, setWatchlist]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [collapsed, setCollapsed]   = useState(false);
  const [expanded, setExpanded]     = useState(null);
  const [playerNotes, setPlayerNotes] = useState({});
  const [noteInput, setNoteInput]   = useState({});

  useEffect(() => {
    api("/user/watchlist")
      .then(d => setWatchlist(d.watchlist || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function removeFromWatchlist(key) {
    await api(`/user/watchlist/${encodeURIComponent(key)}`, { method: "DELETE" }).catch(() => {});
    setWatchlist(prev => prev.filter(w => w.player_key !== key));
    if (expanded === key) setExpanded(null);
  }

  async function toggleExpand(key) {
    if (expanded === key) { setExpanded(null); return; }
    setExpanded(key);
    if (!playerNotes[key]) {
      api(`/user/notes/${encodeURIComponent(key)}`)
        .then(d => setPlayerNotes(prev => ({...prev, [key]: d.notes || []})))
        .catch(() => {});
    }
  }

  async function addNote(key) {
    const txt = (noteInput[key]||"").trim();
    if (!txt) return;
    const n = await api(`/user/notes/${encodeURIComponent(key)}`, {
      method: "POST", body: JSON.stringify({ note_text: txt })
    }).catch(() => null);
    if (n) {
      setPlayerNotes(prev => ({...prev, [key]: [n, ...(prev[key]||[])]}));
      setNoteInput(prev => ({...prev, [key]: ""}));
    }
  }

  async function deleteNote(key, id) {
    await api(`/user/notes/${id}`, { method: "DELETE" }).catch(() => {});
    setPlayerNotes(prev => ({...prev, [key]: (prev[key]||[]).filter(n => n.id !== id)}));
  }

  return (
    <div className="card" style={{marginBottom:16}}>
      <div className="card-header" style={{cursor:"pointer"}} onClick={() => setCollapsed(v => !v)}>
        <div>
          <div className="card-title">Watchlisted players</div>
          <div className="card-sub">{watchlist.length} players tracked</div>
        </div>
        <span style={{fontSize:12,color:"var(--text-muted)"}}>{collapsed ? "▸ Show" : "▾ Hide"}</span>
      </div>
      {!collapsed && (
        loading
          ? <div className="loading" style={{padding:20}}><div className="spinner"/>Loading…</div>
          : watchlist.length === 0
            ? <div style={{padding:"20px",textAlign:"center",fontSize:13,color:"var(--text-muted)"}}>No players watchlisted — add from Scout Board</div>
            : <>
              {watchlist.map(w => {
                const _m = (() => { try { return JSON.parse(w.status||"{}"); } catch(e) { return {}; } })();
                const isOpen = expanded === w.player_key;
                const notes = playerNotes[w.player_key] || [];
                return (
                  <div key={w.player_key} style={{borderBottom:"1px solid var(--border)"}}>
                    {/* Main row */}
                    <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 16px",cursor:"pointer"}}
                      onClick={() => toggleExpand(w.player_key)}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.player_key}</div>
                        <div style={{fontSize:11,color:"var(--text-muted)",marginTop:1,textTransform:"capitalize"}}>{_m.role||"—"} · {_m.school||"—"}</div>
                      </div>
                      <span style={{fontSize:11,color:"var(--text-muted)",flexShrink:0,cursor:"pointer"}} onClick={e=>{e.stopPropagation();toggleExpand(w.player_key);}}>
                      {isOpen ? "▾ stats" : "▸ stats"}
                    </span>
                      <button
                        className="btn btn-primary btn-sm"
                        style={{background:"linear-gradient(135deg,#7c3aed,#4f46e5)",border:"none",fontSize:10,padding:"4px 10px",flexShrink:0}}
                        onClick={e => { e.stopPropagation(); if (onImportProfile) onImportProfile({ player_name: w.player_key, role: _m.role||"guard", school: _m.school||"—", predicted_nil_value: parseFloat(_m.nil||"1.00")*1000000, elig_years_remaining: _m.elig||2, conf_tier_label: _m.conf||"Rest D-I", pts_per_g: _m.pts, reb_per_g: _m.reb, ast_per_g: _m.ast, fg_pct: _m.fg, fg3_pct: _m.fg3, ft_pct: _m.ft, stl_per_g: _m.stl, blk_per_g: _m.blk, mp_per_g: _m.mp, tov_per_g: _m.tov, orb_per_g: _m.orb, g: _m.g, _ts: Date.now() }); }}>
                        ↗ Evaluate
                      </button>
                      <button onClick={e => { e.stopPropagation(); removeFromWatchlist(w.player_key); }}
                        style={{background:"none",border:"none",color:"var(--red)",fontSize:18,cursor:"pointer",padding:"2px 6px",lineHeight:1,flexShrink:0}}>×</button>
                      <span style={{fontSize:11,color:"var(--text-muted)",flexShrink:0}}>{isOpen ? "▾" : "▸"}</span>
                    </div>
                    {/* Expanded: stats + notes */}
                    {isOpen && (
                      <div style={{padding:"12px 16px 16px",background:"var(--surface2)",borderTop:"1px solid var(--border)"}}>
                        {/* Stats grid */}
                        {(_m.pts || _m.reb || _m.ast) && (
                          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:12}}>
                            {[["PTS/G",_m.pts],["REB/G",_m.reb],["AST/G",_m.ast],["STL/G",_m.stl],["BLK/G",_m.blk],["FG%",_m.fg ? (parseFloat(_m.fg)*100).toFixed(1)+"%" : null],["3P%",_m.fg3 ? (parseFloat(_m.fg3)*100).toFixed(1)+"%" : null],["FT%",_m.ft ? (parseFloat(_m.ft)*100).toFixed(1)+"%" : null]].filter(([,v]) => v != null).map(([label,val]) => (
                              <div key={label} style={{background:"var(--surface)",borderRadius:6,padding:"6px 8px",textAlign:"center"}}>
                                <div style={{fontSize:10,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.3px"}}>{label}</div>
                                <div style={{fontSize:13,fontWeight:600,marginTop:2}}>{val}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Notes */}
                        <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px",color:"var(--text-muted)",marginBottom:8}}>Scouting notes</div>
                        <div style={{display:"flex",gap:8,marginBottom:10}}>
                          <input
                            className="form-input"
                            style={{flex:1,fontSize:12,padding:"6px 10px"}}
                            placeholder="Add a note…"
                            value={noteInput[w.player_key]||""}
                            onChange={e => setNoteInput(prev => ({...prev, [w.player_key]: e.target.value}))}
                            onKeyDown={e => e.key==="Enter" && addNote(w.player_key)}/>
                          <button className="btn btn-primary btn-sm" onClick={() => addNote(w.player_key)}
                            style={{fontSize:12,padding:"6px 14px",flexShrink:0}}>Add</button>
                        </div>
                        {notes.length === 0
                          ? <div style={{fontSize:12,color:"var(--text-muted)"}}>No notes yet</div>
                          : notes.map(n => (
                            <div key={n.id} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,padding:"6px 0",borderBottom:"1px solid var(--border)"}}>
                              <div style={{fontSize:12,lineHeight:1.5,flex:1}}>{n.note_text}</div>
                              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                                <span style={{fontSize:10,color:"var(--text-muted)"}}>{n.created_at ? new Date(n.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : ""}</span>
                                <button onClick={() => deleteNote(w.player_key, n.id)}
                                  style={{background:"none",border:"none",color:"var(--red)",fontSize:14,cursor:"pointer",padding:"0 2px",lineHeight:1}}>×</button>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{padding:"10px 16px",background:"var(--surface2)",display:"flex",gap:16,flexWrap:"wrap",borderBottomLeftRadius:"var(--radius-lg)",borderBottomRightRadius:"var(--radius-lg)"}}>
                {["guard","forward","center"].map(r => {
                  const cnt = watchlist.filter(w => { try { return JSON.parse(w.status||"{}").role===r; } catch(e) { return false; } }).length;
                  if (!cnt) return null;
                  return <span key={r} style={{fontSize:12,color:"var(--text-secondary)",textTransform:"capitalize"}}>{r}s: <strong style={{color:"var(--text-primary)"}}>{cnt}</strong></span>;
                })}
                <span style={{fontSize:12,color:"var(--text-secondary)",marginLeft:"auto"}}>{watchlist.length} tracked</span>
              </div>
            </>
      )}
    </div>
  );
}

function NotesScreen({ api, onImportProfile }) {
  const [notes, setNotes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [error, setError]     = useState(null);

  useEffect(() => {
    setLoading(true);
    api("/user/watchlist")
      .then(async w => {
        const watchlist = w.watchlist || [];
        if (watchlist.length === 0) { setLoading(false); return; }
        const all = [];
        await Promise.all(watchlist.map(p =>
          api(`/user/notes/${encodeURIComponent(p.player_key)}`)
            .then(d => (d.notes || []).forEach(n =>
              all.push({...n, player_name: p.player_key})
            ))
            .catch(() => {})
        ));
        all.sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
        setNotes(all);
      })
      .catch(() => setError("Could not load notes."))
      .finally(() => setLoading(false));
  }, []);

  const filtered = notes.filter(n =>
    !search.trim() ||
    (n.note_text||"").toLowerCase().includes(search.toLowerCase()) ||
    (n.player_name||"").toLowerCase().includes(search.toLowerCase())
  );

  const grouped = filtered.reduce((acc, n) => {
    const k = n.player_name || "Unknown";
    if (!acc[k]) acc[k] = [];
    acc[k].push(n);
    return acc;
  }, {});

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-title">Notes + Watchlist</div>
        <div className="page-sub">Your intel. Your program. Private to your account.</div>
      </div>
      {!loading && !error && (
        <div className="kpi-row" style={{marginBottom:16}}>
          <div className="kpi-card">
            <div className="kpi-label">Total notes</div>
            <div className="kpi-value">{notes.length}</div>
            <div className="kpi-delta teal">across all players</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Players noted</div>
            <div className="kpi-value">{Object.keys(grouped).length}</div>
            <div className="kpi-delta teal">unique profiles</div>
          </div>
        </div>
      )}
      {/* Watchlist block */}
      <WatchlistBlock api={api} onImportProfile={onImportProfile} />

      <div className="card" style={{marginBottom:16,padding:"10px 16px"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <Icon name="search" size={14}/>
          <input
            className="form-input"
            style={{border:"none",background:"none",flex:1,fontSize:13,padding:"4px 0"}}
            placeholder="Search notes or player name…"
            value={search}
            onChange={e => setSearch(e.target.value)}/>
        </div>
      </div>
      <div style={{marginTop:16,padding:"10px 0",borderTop:"1px solid var(--border)",fontSize:11,color:"var(--text-muted)",textAlign:"center"}}>
        Scouting notes are private to your account. · Rah-AI: Vantage by Epicentra LLC
      </div>
    </div>
  );
}


// ── Roster Profile Notes ──────────────────────────────────────────────────────
function RosterProfileNotes({ api, playerKey }) {
  const [notes, setNotes]     = useState([]);
  const [input, setInput]     = useState("");
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    if (!playerKey) return;
    api(`/user/notes/${encodeURIComponent(playerKey)}`)
      .then(d => setNotes(d.notes || []))
      .catch(() => {});
  }, [playerKey]);

  async function addNote() {
    if (!input.trim()) return;
    setSaving(true);
    try {
      const n = await api(`/user/notes/${encodeURIComponent(playerKey)}`, {
        method: "POST", body: JSON.stringify({ note_text: input.trim() })
      });
      setNotes(prev => [n, ...prev]);
      setInput("");
    } catch(e) {} finally { setSaving(false); }
  }

  async function deleteNote(id) {
    await api(`/user/notes/${id}`, { method: "DELETE" }).catch(() => {});
    setNotes(prev => prev.filter(n => n.id !== id));
  }

  return (
    <div>
      <div style={{display:"flex",gap:8,marginBottom:10}}>
        <input
          className="form-input"
          style={{flex:1,fontSize:12,padding:"6px 10px"}}
          placeholder="Add a note (offer amount, visit outcome, contact date…)"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key==="Enter" && addNote()}
        />
        <button
          className="btn btn-primary btn-sm"
          style={{fontSize:11,padding:"6px 12px",flexShrink:0}}
          onClick={addNote}
          disabled={saving}>
          {saving ? "…" : "Add"}
        </button>
      </div>
      {notes.length === 0
        ? <div style={{fontSize:12,color:"var(--text-muted)",fontStyle:"italic"}}>No notes yet</div>
        : notes.map(n => (
          <div key={n.id} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,padding:"6px 0",borderBottom:"1px solid var(--border)"}}>
            <div style={{fontSize:12,lineHeight:1.5,flex:1}}>{n.note_text}</div>
            <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
              <span style={{fontSize:10,color:"var(--text-muted)"}}>{fmtDate(n.created_at)}</span>
              <button onClick={() => deleteNote(n.id)} style={{background:"none",border:"none",color:"var(--red)",fontSize:14,cursor:"pointer",padding:"0 2px",lineHeight:1}}>×</button>
            </div>
          </div>
        ))
      }
    </div>
  );
}

// ── App shell ─────────────────────────────────────────────────────────────────
const SCREENS = [
  { id:"portal",   label:"Scout Board",      icon:"portal", title:"Scout Board" },
  { id:"predict",  label:"Profile Evaluator",icon:"search", title:"Profile Evaluator" },
  { id:"roster",   label:"Roster Builder",   icon:"grid",   title:"Roster Builder" },
  { id:"notes",    label:"Notes + Watchlist", icon:"check",  title:"Notes + Watchlist" },
];

export default function App() {
  const [auth, setAuth] = useState(() => {
    try { const a = localStorage.getItem("scout_auth"); return a ? JSON.parse(a) : null; }
    catch { return null; }
  });
  const [active, setActive] = useState("portal");
  const [importedProfile, setImportedProfile] = useState(null);
  const [apiOk, setApiOk] = useState(null);
  const [budgetM, setBudgetM] = useState("18");
  const [budgetInput, setBudgetInput] = useState("18");

  function saveBudget(val) {
    setBudgetM(val);
    setBudgetInput(val);
    if (api) {
      // Persist budget inside the squad record — no extra endpoint needed
      api("/user/squads").then(d => {
        const found = (d.squads || []).find(s => s.squad_name === "2026-27 Roster");
        const squadData = found ? { ...found.squad_data, nil_budget_m: val } : { players: [], nil_budget_m: val };
        api("/user/squads", {
          method: "POST",
          body: JSON.stringify({ squad_name: "2026-27 Roster", squad_data: squadData, is_default: true }),
        }).catch(() => {});
      }).catch(() => {});
    }
  }
  const [roster, setRoster] = useState({ players: [], nil_budget_m: "18" });
  const [showIntake, setShowIntake] = useState(false);
  const [initialTopMatch, setInitialTopMatch] = useState(false);
  const [showToast, setShowToast] = useState(null);
  const [autoRunCompat, setAutoRunCompat] = useState(false);
  const [programCtx, setProgramCtx] = useState(null);

  const api = auth ? makeApi(auth.token, () => { localStorage.removeItem("scout_auth"); setAuth(null); }) : null;

  useEffect(() => {
    if (!auth || !api) return;
    // Check if program context exists — show intake modal if not
    api("/user/program_context").then(d => {
      if (d.exists) {
        setProgramCtx(d);
        if (d.total_budget_m) { setBudgetM(String(d.total_budget_m)); setBudgetInput(String(d.total_budget_m)); }
      } else {
        setShowIntake(true);
      }
    }).catch(() => {});
    api("/user/squads").then(d => {
      const found = (d.squads || []).find(s => s.squad_name === "2026-27 Roster");
      if (found) {
        setRoster(found.squad_data);
        if (found.squad_data?.nil_budget_m) {
          setBudgetM(found.squad_data.nil_budget_m);
          setBudgetInput(found.squad_data.nil_budget_m);
        }
      }
    }).catch(() => {});
    api("/user/prefs").then(d => {
      if (d.prefs?.budgetM) {
        setBudgetM(d.prefs.budgetM);
        setBudgetInput(d.prefs.budgetM);
      }
    }).catch(() => {});
  }, [auth]);

  useEffect(() => {
    if (!auth) return;
    fetch(`${API_BASE}/health`)
      .then(r => r.json())
      .then(d => setApiOk(d.db_connected))
      .catch(() => setApiOk(false));
  }, [auth]);

  function handleLogin(authData) {
    localStorage.setItem("scout_auth", JSON.stringify(authData));
    setAuth(authData);
  }

  function handleLogout() {
    localStorage.removeItem("scout_auth");
    setAuth(null);
    setActive("portal");
    setRoster({ players: [], nil_budget_m: "18" });
  }

  if (!auth) return <LoginScreen onLogin={handleLogin}/>;

  // ── Program Intake Modal ───────────────────────────────────────────────────
  function IntakeModal() {
    const [step, setStep] = useState(1);
    const [players, setPlayers] = useState("");
    const [totalBudget, setTotalBudget] = useState("");
    const [committed, setCommitted] = useState("");
    const [grades, setGrades] = useState({ guard: null, forward: null, center: null });
    const [depth, setDepth] = useState({ guard: 2, forward: 2, center: 1 });
    const [upgrades, setUpgrades] = useState([]);
    const [needs, setNeeds] = useState([]);
    const remaining = totalBudget && committed ? (parseFloat(totalBudget) - parseFloat(committed)).toFixed(1) : null;

    const GRADE_MAP = { A: 85, B: 60, C: 40, D: 20 };
    const POSITIONS = ["guard", "forward", "center"];
    const UPGRADE_OPTS = ["Guard", "Forward", "Center"];
    const NEED_OPTS = ["Scoring", "Playmaking", "Perimeter Defense", "Interior Defense", "Rebounding", "Depth / Insurance"];

    function toggleArr(arr, setArr, val) {
      setArr(arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);
    }

    async function handleSave() {
      const body = {
        players_committed: parseInt(players) || 0,
        total_budget_m: parseFloat(totalBudget) || 0,
        committed_spend_m: parseFloat(committed) || 0,
        guard_grade: grades.guard,
        forward_grade: grades.forward,
        center_grade: grades.center,
        guard_depth: depth.guard,
        forward_depth: depth.forward,
        center_depth: depth.center,
        upgrade_positions: upgrades,
        burning_needs: needs,
      };
      await api("/user/program_context", { method: "POST", body: JSON.stringify(body) });
      setProgramCtx({ ...body, exists: true });
      if (body.total_budget_m) { setBudgetM(String(body.total_budget_m)); setBudgetInput(String(body.total_budget_m)); }
      // Stat archetype table — position × grade
      const STAT_ARCHETYPES = {
        guard: {
          A: { pts_per_g:19, ast_per_g:5.5, reb_per_g:3.5, stl_per_g:1.8, blk_per_g:0.3, fg_pct:0.47, fg3_pct:0.37, ft_pct:0.78, mp_per_g:32, tov_per_g:2.8, g:30 },
          B: { pts_per_g:15, ast_per_g:4.5, reb_per_g:3.2, stl_per_g:1.4, blk_per_g:0.2, fg_pct:0.44, fg3_pct:0.34, ft_pct:0.75, mp_per_g:29, tov_per_g:2.3, g:30 },
          C: { pts_per_g:11, ast_per_g:3.5, reb_per_g:2.8, stl_per_g:1.0, blk_per_g:0.1, fg_pct:0.41, fg3_pct:0.31, ft_pct:0.71, mp_per_g:25, tov_per_g:1.8, g:28 },
          D: { pts_per_g: 7, ast_per_g:2.5, reb_per_g:2.2, stl_per_g:0.7, blk_per_g:0.1, fg_pct:0.38, fg3_pct:0.28, ft_pct:0.67, mp_per_g:20, tov_per_g:1.4, g:25 },
        },
        forward: {
          A: { pts_per_g:17, ast_per_g:2.5, reb_per_g:7.5, stl_per_g:1.4, blk_per_g:0.9, fg_pct:0.50, fg3_pct:0.35, ft_pct:0.74, mp_per_g:32, tov_per_g:2.2, g:30 },
          B: { pts_per_g:14, ast_per_g:2.0, reb_per_g:6.5, stl_per_g:1.1, blk_per_g:0.7, fg_pct:0.47, fg3_pct:0.32, ft_pct:0.71, mp_per_g:29, tov_per_g:1.8, g:30 },
          C: { pts_per_g:10, ast_per_g:1.5, reb_per_g:5.5, stl_per_g:0.8, blk_per_g:0.5, fg_pct:0.44, fg3_pct:0.29, ft_pct:0.67, mp_per_g:25, tov_per_g:1.5, g:28 },
          D: { pts_per_g: 7, ast_per_g:1.2, reb_per_g:4.5, stl_per_g:0.6, blk_per_g:0.3, fg_pct:0.41, fg3_pct:0.26, ft_pct:0.63, mp_per_g:20, tov_per_g:1.2, g:25 },
        },
        center: {
          A: { pts_per_g:15, ast_per_g:1.5, reb_per_g:9.5, stl_per_g:0.8, blk_per_g:2.2, fg_pct:0.55, fg3_pct:0.25, ft_pct:0.68, mp_per_g:30, tov_per_g:2.0, g:30 },
          B: { pts_per_g:12, ast_per_g:1.2, reb_per_g:8.0, stl_per_g:0.6, blk_per_g:1.6, fg_pct:0.52, fg3_pct:0.22, ft_pct:0.64, mp_per_g:27, tov_per_g:1.7, g:30 },
          C: { pts_per_g: 9, ast_per_g:1.0, reb_per_g:6.5, stl_per_g:0.5, blk_per_g:1.1, fg_pct:0.49, fg3_pct:0.18, ft_pct:0.60, mp_per_g:23, tov_per_g:1.4, g:28 },
          D: { pts_per_g: 6, ast_per_g:0.8, reb_per_g:5.0, stl_per_g:0.4, blk_per_g:0.7, fg_pct:0.46, fg3_pct:0.15, ft_pct:0.56, mp_per_g:18, tov_per_g:1.1, g:25 },
        },
      };
      // Slot weight slope — steady decline for depth players
      const SLOT_WEIGHTS = [1.00, 0.75, 0.50, 0.40, 0.35];
      const slotWeight = (i) => SLOT_WEIGHTS[Math.min(i, SLOT_WEIGHTS.length-1)] || 0.30;
      // Grade order for sub-tier stat archetype on depth slots
      const GRADE_ORDER = ["A","B","C","D"];
      function subTierGrade(grade, slots) {
        const idx = GRADE_ORDER.indexOf(grade);
        return GRADE_ORDER[Math.min(idx + Math.floor(slots/2), GRADE_ORDER.length-1)];
      }
      // Build player list first to compute total weight for committed spend distribution
      const committedM = parseFloat(body.committed_spend_m) || 0;
      const playerSlots = [];
      POSITIONS.forEach(pos => {
        const grade = grades[pos === "guard" ? "guard" : pos === "forward" ? "forward" : "center"];
        if (!grade) return;
        const pct = GRADE_MAP[grade] || 40;
        const prefix = pos === "guard" ? "G" : pos === "forward" ? "F" : "C";
        const posLabel = pos === "guard" ? "guard" : pos === "forward" ? "forward" : "center";
        const count = pos === "guard" ? (body.guard_depth || 2)
                    : pos === "forward" ? (body.forward_depth || 2)
                    : (body.center_depth || 1);
        for (let i = 0; i < count; i++) {
          const sw = slotWeight(i);
          playerSlots.push({ pos, posLabel, prefix, grade, pct, slotIdx: i, slotNum: i+1, weight: pct * sw });
        }
      });
      // Total weight for proportional distribution
      const totalWeight = playerSlots.reduce((s, p) => s + p.weight, 0) || 1;
      // Pre-fill roster with Current profiles
      const newPlayers = [];
      playerSlots.forEach(({ pos, posLabel, prefix, grade, pct, slotIdx, slotNum, weight }) => {
        // Committed spend distributed by grade × slot weight
        const distVal = committedM > 0
          ? parseFloat(((weight / totalWeight) * committedM).toFixed(2))
          : pos === "guard"
            ? (pct >= 80 ? 0.85 : pct >= 60 ? 0.48 : pct >= 40 ? 0.28 : 0.12)
            : pos === "forward"
            ? (pct >= 80 ? 0.72 : pct >= 60 ? 0.42 : pct >= 40 ? 0.24 : 0.10)
            : (pct >= 80 ? 0.65 : pct >= 60 ? 0.38 : pct >= 40 ? 0.20 : 0.09);
        const slotGrade = subTierGrade(grade, slotIdx);
        const archetype = STAT_ARCHETYPES[posLabel]?.[slotGrade] || STAT_ARCHETYPES[posLabel]?.["D"];
        const tierLabel = pct >= 80 ? "Elite" : pct >= 60 ? "Premium" : pct >= 40 ? "First Team" : "Role Player";
        newPlayers.push({
          player_name: `Current ${prefix}${slotNum}`,
          role: posLabel, position: posLabel,
          nil_value_m: String(distVal),
          predicted_nil_value: distVal * 1000000,
          tier: tierLabel,
          elig: "—", class_year: "—", elig_years: "—", is_current: true,
          _stats: { ...archetype, conf_tier: "Power 4", trend: "Stable", elig_years: "—" },
          _result: {
            tier: tierLabel,
            perf_score: pct >= 80 ? 82 : pct >= 60 ? 65 : pct >= 40 ? 48 : 30,
            adjusted_score: pct >= 80 ? 80 : pct >= 60 ? 63 : pct >= 40 ? 46 : 28,
            range_fmt: distVal.toFixed(2) + "M",
          },
        });
      });
      if (newPlayers.length > 0) {
        const newRoster = { players: newPlayers, nil_budget_m: String(body.total_budget_m) };
        setRoster(newRoster);
        api("/user/squads", { method: "POST", body: JSON.stringify({ squad_name: "2026-27 Roster", squad_data: newRoster, is_default: true }) }).catch(() => {});
      }
      setInitialTopMatch(true);
      setShowIntake(false);
    }

    const pill = (label, active, onClick) => (
      <button key={label} onClick={onClick} style={{
        padding:"6px 14px", borderRadius:99, fontSize:13, fontWeight:500, cursor:"pointer",
        border: active ? "1px solid var(--accent)" : "1px solid var(--border)",
        background: active ? "var(--accent-dim)" : "var(--surface2)",
        color: active ? "var(--teal)" : "var(--text-secondary)",
        transition:"all 0.15s",
      }}>{label}</button>
    );

    const gradePill = (pos, g) => pill(g, grades[pos] === g, () => setGrades({...grades, [pos]: grades[pos]===g ? null : g}));

    return (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
        <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:16,width:"100%",maxWidth:520,padding:32,boxShadow:"0 24px 64px rgba(0,0,0,0.4)"}}>
          {/* Header */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}>
            <div>
              <div style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:700,letterSpacing:"-0.4px"}}>Set up your program</div>
              <div style={{fontSize:13,color:"var(--text-muted)",marginTop:4}}>Step {step} of 3 — takes 60 seconds</div>
            </div>
            <button onClick={() => setShowIntake(false)} style={{background:"none",border:"none",color:"var(--text-muted)",fontSize:20,cursor:"pointer",padding:4}}>×</button>
          </div>

          {/* Step indicators */}
          <div style={{display:"flex",gap:6,marginBottom:28}}>
            {[1,2,3].map(s => (
              <div key={s} style={{height:3,flex:1,borderRadius:99,background: s <= step ? "var(--accent)" : "var(--border)",transition:"background 0.3s"}}/>
            ))}
          </div>

          {/* Step 1 — Roster & Budget */}
          {step === 1 && (
            <div>
              <div style={{fontSize:14,fontWeight:600,marginBottom:16}}>Roster &amp; Budget</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                <div>
                  <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.4px"}}>Players committed</div>
                  <input type="number" value={players} onChange={e=>setPlayers(e.target.value)} placeholder="e.g. 10"
                    style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface2)",color:"var(--text-primary)",fontSize:14,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.4px"}}>Total Roster Budget ($M)</div>
                  <input type="number" step="0.5" value={totalBudget} onChange={e=>setTotalBudget(e.target.value)} placeholder="e.g. 5.0"
                    style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface2)",color:"var(--text-primary)",fontSize:14,outline:"none",boxSizing:"border-box"}}/>
                </div>
              </div>
              <div style={{marginBottom:16}}>
                <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.4px"}}>Already committed ($M)</div>
                <input type="number" step="0.5" value={committed} onChange={e=>setCommitted(e.target.value)} placeholder="e.g. 2.5"
                  style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface2)",color:"var(--text-primary)",fontSize:14,outline:"none",boxSizing:"border-box"}}/>
              </div>
              {remaining !== null && (
                <div style={{padding:"10px 14px",borderRadius:8,background:"var(--accent-dim)",border:"1px solid rgba(13,148,136,0.2)",fontSize:13,color:"var(--teal)",marginBottom:16}}>
                  💰 You have <strong>${remaining}M</strong> remaining in your Roster Budget
                </div>
              )}
            </div>
          )}

          {/* Step 2 — Position grades */}
          {step === 2 && (
            <div>
              <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>How would you grade your current roster?</div>
              <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:20}}>Skip positions not on your roster</div>
              {POSITIONS.map(pos => (
                <div key={pos} style={{marginBottom:16}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                    <div style={{fontSize:12,fontWeight:600,textTransform:"capitalize",color:"var(--text-secondary)"}}>{pos}</div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:11,color:"var(--text-muted)"}}>On roster:</span>
                      <select value={depth[pos]} onChange={e => setDepth({...depth, [pos]: parseInt(e.target.value)})}
                        style={{padding:"3px 8px",borderRadius:6,border:"1px solid var(--border)",
                          background:"var(--surface2)",color:"var(--text-primary)",fontSize:12,
                          cursor:"pointer",outline:"none",fontFamily:"var(--font-body)"}}>
                        {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    {["A","B","C","D"].map(g => gradePill(pos, g))}
                    {pill("Skip", grades[pos] === "skip", () => setGrades({...grades, [pos]: grades[pos]==="skip" ? null : "skip"}))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 3 — Upgrade targets + needs */}
          {step === 3 && (
            <div>
              <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>What are you looking to add?</div>
              <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:16}}>Select all that apply</div>
              <div style={{fontSize:12,fontWeight:600,color:"var(--text-secondary)",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.4px"}}>Positions to upgrade</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:20}}>
                {UPGRADE_OPTS.map(o => pill(o, upgrades.includes(o), () => toggleArr(upgrades, setUpgrades, o)))}
              </div>
              <div style={{fontSize:12,fontWeight:600,color:"var(--text-secondary)",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.4px"}}>Burning needs</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {NEED_OPTS.map(o => pill(o, needs.includes(o), () => toggleArr(needs, setNeeds, o)))}
              </div>
            </div>
          )}

          {/* Footer buttons */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:28}}>
            <button onClick={() => setShowIntake(false)} style={{background:"none",border:"none",color:"var(--text-muted)",fontSize:13,cursor:"pointer"}}>
              Skip — start with blank slate
            </button>
            <div style={{display:"flex",gap:10}}>
              {step > 1 && (
                <button onClick={() => setStep(step-1)} style={{padding:"9px 20px",borderRadius:8,border:"1px solid var(--border)",background:"var(--surface2)",color:"var(--text-secondary)",fontSize:13,fontWeight:500,cursor:"pointer"}}>
                  Back
                </button>
              )}
              {step < 3
                ? <button onClick={() => setStep(step+1)} style={{padding:"9px 24px",borderRadius:8,border:"none",background:"var(--accent)",color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                    Next →
                  </button>
                : <button onClick={handleSave} style={{padding:"9px 24px",borderRadius:8,border:"none",background:"var(--accent)",color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                    Set up my program →
                  </button>
              }
            </div>
          </div>
        </div>
      </div>
    );
  }

  const current = SCREENS.find(s => s.id === active);
  const program = auth.program || "Program";

  return (
    <>
      <style>{styles}</style>
      {showIntake && <IntakeModal />}
      <div className="layout">
        <aside className="sidebar">
          <div className="sidebar-logo">
            <div className="logo-mark">
              <div className="logo-icon" style={{background:"none",padding:0}}>
                <svg width="32" height="32" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="72" height="72" rx="14" fill="#141B24"/>
                  <path d="M36 11 L13 36 L36 61 L36 11 Z" fill="#3B9FD4"/>
                  <path d="M36 11 L59 36 L36 61 L36 11 Z" fill="#4DD4A8"/>
                </svg>
              </div>
              <div>
                <div className="logo-text">Rah-AI: Vantage</div>
                <div className="logo-sub">We evaluate profiles, not players.</div>
              </div>
            </div>
          </div>

          <div className="sidebar-section" style={{marginTop:8}}>Workspace</div>
          {SCREENS.map(s => (
            <button key={s.id} className={`nav-item ${active===s.id?"active":""}`} onClick={() => setActive(s.id)}>
              <Icon name={s.icon} size={15}/>{s.label}
            </button>
          ))}

          <div className="sidebar-section" style={{marginTop:16}}>Program</div>
          <div style={{padding:"4px 20px"}}>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",fontWeight:500}}>{program}</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:2}}>2026–27 season</div>
          </div>


          <div className="sidebar-bottom">
            <div className="status-pill">
              <div className={`status-dot ${apiOk===false?"offline":""}`}/>
              <span>{apiOk===null?"Connecting…":apiOk?"API connected":"API offline"}</span>
            </div>
            <div className="powered-tagline">Every dollar. Every decision.</div>
            <div className="powered-by">Powered by Epicentra LLC</div>
          </div>
        </aside>

        <main className="main">
          {showToast && (
            <div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",
              zIndex:1000,background:"var(--surface)",border:"1px solid rgba(13,148,136,0.4)",
              borderRadius:10,padding:"12px 18px",boxShadow:"0 4px 24px rgba(0,0,0,0.15)",
              display:"flex",alignItems:"center",gap:12,minWidth:320}}>
              <span style={{fontSize:16}}>✓</span>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--text-primary)"}}>Added to Roster Builder</div>
                <div style={{fontSize:11,color:"var(--text-muted)",marginTop:1}}>{showToast.name} · {showToast.role}</div>
              </div>
              <button onClick={() => { setActive("roster"); setAutoRunCompat(true); setShowToast(null); }}
                style={{padding:"5px 12px",borderRadius:7,border:"1px solid var(--teal)",
                  background:"rgba(13,148,136,0.08)",color:"var(--teal)",fontSize:11,
                  fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>
                Check Compatibility →
              </button>
              <button onClick={() => setShowToast(null)}
                style={{background:"none",border:"none",color:"var(--text-muted)",fontSize:16,cursor:"pointer",padding:"0 2px"}}>×</button>
            </div>
          )}
          <div className="topbar">
            <div className="topbar-title">{current?.title}</div>
            <div className="topbar-right">
              <span className="program-badge">{program}</span>
              {program && (
                <button onClick={async () => {
                  await api("/user/program_context", {method:"DELETE"});
                  setProgramCtx(null);
                  setRoster(null);
                  setShowIntake(true);
                }} style={{padding:"5px 12px",borderRadius:7,border:"1px solid rgba(214,59,59,0.3)",
                  background:"rgba(214,59,59,0.06)",color:"var(--red)",fontSize:12,fontWeight:500,
                  cursor:"pointer",transition:"all 0.15s",display:"flex",alignItems:"center",gap:5}}
                  onMouseOver={e=>{e.currentTarget.style.background="rgba(214,59,59,0.12)";e.currentTarget.style.borderColor="var(--red)";}}
                  onMouseOut={e=>{e.currentTarget.style.background="rgba(214,59,59,0.06)";e.currentTarget.style.borderColor="rgba(214,59,59,0.3)";}}>
                  ↺ Reset
                </button>
              )}
              <button className="logout-btn" onClick={handleLogout}>
                <Icon name="logout" size={12}/> Sign out
              </button>
            </div>
          </div>
          <div className="page">
            {active === "portal"    && <PortalBoard api={api} program={program} roster={roster} setRoster={setRoster} budgetM={budgetM} setBudgetM={saveBudget} programCtx={programCtx} initialTopMatch={initialTopMatch} onImportProfile={p=>{setImportedProfile(p); setActive("predict");}} onGoRoster={()=>setActive("roster")} onGoNotes={()=>setActive("notes")}/>}
            {active === "roster"    && <RosterBuilder api={api} budgetM={budgetM} onBudgetChange={saveBudget} roster={roster} setRoster={setRoster} programCtx={programCtx} autoRunCompat={autoRunCompat} onCompatRan={()=>setAutoRunCompat(false)} onImportProfile={p=>{setImportedProfile(p); setActive("predict");}}/>}
            {active === "predict"   && <CustomPredictScreen api={api} importedProfile={importedProfile} onImportConsumed={()=>setImportedProfile(null)} roster={roster} setRoster={setRoster} onAddedToRoster={(name,role)=>{setShowToast({name,role});setTimeout(()=>setShowToast(null),5000);}}/>}
            {active === "notes"     && <NotesScreen api={api} onImportProfile={p=>{setImportedProfile(p); setActive("predict");}}/>}
            {/* analytics hidden until stable */}
          </div>
        </main>
      </div>
    </>
  );
}
